import { z } from "zod";
import { TENANT_ROLES, TenantHomeScreenSchema } from "../auth-service/contract";
import { PRESET_SUBJECT_TYPES } from "./presetSubject";
import { REVIEW_PERSPECTIVE_ID_RE } from "./reviewPerspectives";
import { RecordVisibilitySchema, SharedWithSchema } from "./recordVisibility";
// PRODUCT_DEV-1225: 外部カレンダーの予定のキーの形（正本は calendarLink の契約）
import { EXTERNAL_EVENT_KEY_RE } from "../calendarLink/contract";
import type { RecordVisibility } from "./recordVisibility";
import type { LiveValueHistory } from "./liveValueMerge";
import {
  INQUIRY_FAQ_ANSWER_MAX_LENGTH,
  INQUIRY_FAQ_QUESTION_MAX_LENGTH,
  INQUIRY_LINKS_MAX,
  INQUIRY_MESSAGE_MAX_LENGTH,
  InquiryAnswerReferenceSchema,
  InquiryCategorySchema,
  InquiryMessageRoleEnum,
} from "./inquiry-answer";
import type {
  InquiryAnswerReference,
  InquiryMessageRole,
} from "./inquiry-answer";
// PRODUCT_DEV-673: 活動サマリの集計そのものの型は ./activitySummary.ts が正本。
// API のレスポンス型で再定義せず、そこから借りる（集計とレスポンスの形がズレないように）。
import type {
  ActivityMetrics,
  ActivitySourceKind,
  ActivityTrend,
  MetricsComparison,
  PeriodRange,
} from "./activitySummary";

/*
 * DynamoDBの型
 */
// Meetings
// PRODUCT_DEV-875: 担当者メールアドレスの決まり。日程調整のサーバ側の判定
// （scheduling/contract.ts の CreateInviteRequestSchema: trim して z.string().email()・上限 320）と
// 同じにし、「顧客には保存できた（送り先の候補にも出た）のに日程調整では送れない」ずれを作らない。
// 空欄は「未入力」として通す。
export const CONTACT_EMAIL_MAX_LENGTH = 320;
const ContactEmailFormatSchema = z.string().max(CONTACT_EMAIL_MAX_LENGTH).email();

/** 前後の空白を除き、空なら null にする（保存前の正規化）。 */
export const normalizeContactEmail = (
  v: string | null | undefined,
): string | null => {
  const t = v?.trim() ?? "";
  return t === "" ? null : t;
};

/** 担当者メールアドレスとして保存してよいか（空欄は可）。 */
export const isContactEmail = (v: string | null | undefined): boolean => {
  const t = normalizeContactEmail(v);
  return t === null || ContactEmailFormatSchema.safeParse(t).success;
};

// PRODUCT_DEV-212: nameKana は読み仮名（カタカナ）。音声入力直後はnameKanaにのみ値が入り、
// 編集時にユーザがname（漢字氏名）を任意で追加する。既存DDBレコード後方互換のためoptional。
export const ContactSchema = z.object({
  name: z.string().nullable(), // 氏名（漢字含む）
  nameKana: z.string().nullable().optional(), // 読み仮名（カタカナ）。音声入力で初期値が入る。
  department: z.string().nullable(),
  position: z.string().nullable(),
  // PRODUCT_DEV-655: この方について話された内容（フリーテキスト・行単位で蓄積）。
  // GateKeeper のヒアリングメモ抽出が「明示的にこの人物に帰属された発言のうち
  // preset/マスタ項目に当てはまらないもの」を自動格納し、マスタ同期では既存行に
  // append-dedup マージする（上書きしない）。後方互換のため optional。
  notes: z.string().nullable().optional(),
  // PRODUCT_DEV-875: メールアドレス（日程調整の送り先候補に使う）。後方互換のため optional。
  // 商談・録音・抽出データの読み込みでも共用するので、ここでは書式も長さも見ない
  // （保存済みデータを読めなくしないため）。書式は保存の入口で isContactEmail を使って見る。
  email: z.string().nullable().optional(),
});
export type DDBContact = z.infer<typeof ContactSchema>;

// PRODUCT_DEV-512: 担当者類似判定(contacts/match-suggest)の「非空 contact」共通述語。
// suggestions の editedIndex はこの述語でフィルタした配列基準のため、client/server で
// 述語が乖離すると index がズレて別人の contact に統合結果が適用される（実害発生済み）。
// 必ず両側でこの関数を使うこと。nameKana のみの contact（音声入力直後）も判定対象。
export const isNonEmptyEditedContact = (c: DDBContact): boolean =>
  (c.name?.trim() ?? "") !== "" ||
  (c.nameKana?.trim() ?? "") !== "" ||
  (c.department?.trim() ?? "") !== "" ||
  (c.position?.trim() ?? "") !== "";

// PRODUCT_DEV-875: 人が担当者を書き換える入口（顧客の編集・管理者の顧客画面）で使う形。
// ContactSchema と同じ項目で、メールアドレスの書式と長さ（320 字まで）だけを足して見る。
export const EditableContactSchema = ContactSchema.extend({
  email: ContactSchema.shape.email.refine(isContactEmail, {
    message: "メールアドレスの形式が正しくありません",
  }),
});

/**
 * 外部の LLM へ渡す前に担当者からメールアドレスを外す（PRODUCT_DEV-875）。
 * 同一人物の判定にも聞き取りにも使わず、個人情報を社外へ新しく出すだけになるため。
 * 担当者を丸ごと JSON にしてプロンプトへ入れる経路（録音の引き継ぎ・類似判定）は必ずこれを通す。
 */
export const withoutContactEmail = <T extends object>(c: T): Omit<T, "email"> => {
  const { email: _email, ...rest } = c as T & { email?: unknown };
  return rest as Omit<T, "email">;
};

/** 比較用のメールアドレスのキー（前後の空白を除き小文字化。空は ""）。 */
export const contactEmailKey = (v: string | null | undefined): string =>
  (normalizeContactEmail(v) ?? "").toLowerCase();

/** 書式の正しくないメールアドレスを持つ担当者が 1 人でもいるか（商談の保存の検査用）。 */
export const hasInvalidContactEmail = (
  contacts: readonly Pick<DDBContact, "email">[],
): boolean => contacts.some((c) => !isContactEmail(c.email));

// PRODUCT_DEV-512: 顧客マスタ担当者の「置換更新」指示。
// 担当者には安定IDが無く氏名正規化キーで照合するため、改姓・カナ確定のように
// キーが変わる更新は通常のマージでは既存を見つけられず重複登録になる。
// そこで「旧担当者(oldContact の氏名キー)で既存エントリを特定し、newContact で全置換する」
// 指示を meeting PATCH に optional で添えることで、改姓を含む更新を重複なく反映する。
// この指示は商談レコードには保存しない（patch.ts で剥がす）。
export const ContactMasterUpdateSchema = z.object({
  oldContact: ContactSchema, // 置換対象の既存担当者（識別は氏名→無ければカナの正規化キー）
  newContact: ContactSchema, // 置換後の全項目（改姓・カナ確定を含む）
});
export type ContactMasterUpdate = z.infer<typeof ContactMasterUpdateSchema>;

// PRODUCT_DEV-402: extractedData は preset.fields 駆動の動的構造を許容する。
// レガシーな既定キーは型付きで残しつつ、`.passthrough()` でカスタムプリセットの
// 任意キーを保存できるようにする。company は必須から外す（custom preset で不在のため）。
// 厳密な値検証は API 層 (apps/unizo/api/utils/extractedDataSchema.ts) が
// preset を引いた上で動的に行う。
const ExtractedDataSchema = z
  .object({
    meeting_date: z.string().optional(), // "2025-12-19" などの日付文字列
    company: z.string().nullable().optional(),
    title: z.string().min(1).max(50).nullable().optional(), // 商談記録タイトル
    contacts: z.array(ContactSchema).nullable().optional(),
    needs: z.string().nullable().optional(),
    client_reaction: z.string().nullable().optional(),
    next_action: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
  })
  .passthrough();

// PRODUCT_DEV-527: CRM コピー文の LLM 要約（preset.clipboardCopyMaxLength 超過時に生成）。
// オンデマンド生成ではなく商談レコードに保存し、同一内容に対して常に同じ文を
// コピーできる決定性を担保する。sourceHash は要約元テキスト（sanitize 後）の
// SHA-256 hex。コピー時に現在のコピー文のハッシュと照合し、不一致なら再生成する。
export const ClipboardCopySummarySchema = z.object({
  text: z.string(), // 要約済みコピー文（API 層が maxLength 以内を保証）
  sourceHash: z.string(), // 要約元テキストの SHA-256 hex
  generatedAt: z.string(), // ISO文字列
});
export type ClipboardCopySummary = z.infer<typeof ClipboardCopySummarySchema>;

/**
 * AI 抽出 ToDo 候補（Memo に embed、ユーザ採択時のみ UnizoTodos に作成）
 * PRODUCT_DEV-998: 商談記録（DDBMeetingSchema.suggestedTodos）でも使うので、その前に置く
 */
export const SuggestedTodoSchema = z.object({
  /**
   * PRODUCT_DEV-998: 候補の id（商談記録の候補だけが持つ。メモの候補は持たない）。
   * 候補から作ったToDoの `sourceActionId` に入れ、「追加済み」の判定に使う
   * （件名で判定すると、追加したあとに件名を直したとき未追加に戻って見えるため）。
   */
  id: z.string().max(64).optional(),
  /**
   * PRODUCT_DEV-998: この候補と同じものが、紐づく議事録の画面ですでに ToDo になっているときの、その ToDo の id
   * （商談記録を作った人の ToDo のうち `sourceMinutesId` を持つもの）。画面は「議事録でToDo化済み」と出し、追加させない。
   */
  minutesTodoId: z.string().max(64).optional(),
  title: z.string().min(1).max(200),
  dueAt: z.string().nullable().optional(), // ISO8601、null=期限なし
  confidence: z.number().min(0).max(1).optional(), // AI 信頼度
});
export type SuggestedTodo = z.infer<typeof SuggestedTodoSchema>;

/**
 * PRODUCT_DEV-1179: 対話モードの録音で、音声対話（OpenAI Realtime）が使ったトークンの1本ぶんの合計。
 * 録音画面が返答ごとの `response.done` の usage を足し上げて、作成時（POST /meetings）に送る。
 * 費用の実測用で画面には出さない。**クライアントが数えて送る申告値**なので、請求の正本は OpenAI の
 * 利用明細のまま（ここは「どのプリセット・どの対話でどれだけ読んだか」を見るためのもの）。
 * cachedInputTokens は inputTokens の内数（OpenAI の定義どおり）。
 */
const USAGE_COUNT = z.number().int().nonnegative().max(1e9);
export const RealtimeUsageSchema = z.object({
  /** 返答の回数（response.done の数。取り消された返答も含む） */
  responses: USAGE_COUNT,
  inputTokens: USAGE_COUNT,
  /** inputTokens のうち、キャッシュで読めた量（単価 1/10） */
  cachedInputTokens: USAGE_COUNT,
  outputTokens: USAGE_COUNT,
  inputTextTokens: USAGE_COUNT.optional(),
  inputAudioTokens: USAGE_COUNT.optional(),
  cachedTextTokens: USAGE_COUNT.optional(),
  cachedAudioTokens: USAGE_COUNT.optional(),
  outputTextTokens: USAGE_COUNT.optional(),
  outputAudioTokens: USAGE_COUNT.optional(),
  model: z.string().max(64).optional(),
});
export type RealtimeUsage = z.infer<typeof RealtimeUsageSchema>;

/**
 * PRODUCT_DEV-1046: 記録者以外の人の振り返り1人分（`DDBMeeting.memberReviews[userId]`）。
 * 記録者の分は今までどおり `extractedData.minutes_review`。形（観点 id → 文）はそれと同じ。
 */
export const MemberReviewSchema = z.object({
  slots: z.record(z.string(), z.string()),
  /** processing = 録音のあとの整理中（dialogue-completion worker が completed / error にする） */
  status: z.enum(["processing", "completed", "error"]),
  updatedAt: z.string(),
});
export type MemberReview = z.infer<typeof MemberReviewSchema>;

/**
 * PRODUCT_DEV-1046: 1人分の振り返りに持てる観点の数と、1つの観点の長さ。
 * 商談記録は1件 400KB まで（DynamoDB）なので、1人あたり最大 20×2000字（UTF-8 で約120KB）に抑え、
 * 人数は話せる人（記録者・参加者・管理者）に限ることで記録が上限に当たらないようにする。
 */
export const MEMBER_REVIEW_MAX_SLOTS = 20;
export const MEMBER_REVIEW_SLOT_MAX_LENGTH = 2000;

export const DDBMeetingSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  // MEETING#<date>#<time>#<id> — date/time は下記のとおり**区切り無し**。
  // 実例: "MEETING#20260531#215740#y8kdev"
  SK: z.string(),
  GSI1PK: z.string(), // TENANT#<tenantId>
  GSI1SK: z.string(), // MEETING#<id>
  id: z.string(),
  title: z.string(),
  /**
   * 商談を採番した日。**区切り無しの "YYYYMMDD"**（例: "20260531"）。
   *
   * ⚠ PRODUCT_DEV-760 まで、ここのコメントは "YYYY-MM-DD" と書かれていたが**誤り**
   * だった（dev/stg/prod の UnizoMeetings を実測して確認。3環境とも全件 "YYYYMMDD"）。
   * この書式は SK にもそのまま入るため、区切り付きの日付で SK のレンジ条件を組むと
   * '-'(0x2D) < '0'(0x30) の順序により**一致0件**になる。範囲 Query を書くときは
   * 必ず区切りを外すこと。
   *
   * なお**表示・集計で使うべきは `meetingDate`（ユーザが直せる実際の商談日）優先**で、
   * こちらはそのフォールバック（`liteRowOccurredOn` / `effectiveMeetingDate` を参照）。
   */
  date: z.string(),
  /** 商談を採番した時刻。**区切り無しの "HHMMSS"**（例: "215740"）。 */
  time: z.string(),
  duration: z.string(), // "1:35" など、フォーマットは文字列として扱う
  // PRODUCT_DEV-402: preset 駆動でフィールド構造が変化するため optional 化。
  // 一覧表示の代表値は API 層で preset.primaryFieldId から導出して埋めるが、
  // フロント (Recording 等) が一時的に `company: null` を送る経路があるため
  // input 互換のため nullable も残す。consumer は `?? undefined` で吸収する。
  company: z.string().nullable().optional(),
  // PRODUCT_DEV-583: 相手顧客(顧客マスタ)の companyId。reviewed 保存時に解決できたら付与する
  // (会社名の文字列突合の曖昧さを解消する非正規化)。旧レコード/未解決は未設定で、
  // マイ顧客の集計は companyId 優先・未設定は company 名突合にフォールバックする。
  companyId: z.string().optional(),
  contacts: z.array(ContactSchema).nullable().optional(), // 任意
  status: z.string(), // 必要になったら enum 化
  extractedData: ExtractedDataSchema,
  // PRODUCT_DEV-589: GateKeeper が実際に受け取った抽出データ（対話モード=ライブ抽出値 /
  // 自由記録=seed）のスナップショット。extractedData は GK 出力で上書きされ入力が失われるため、
  // Playground の「本番同等の再現」用に別途保持する。派生データにつき PatchMeetingSchema には
  // 含めない（PATCH API の書き込み面を広げない）。本項目追加以前の商談には存在しない。
  liveExtractedData: ExtractedDataSchema.optional(),
  // PRODUCT_DEV-1095: 録音中に同じ項目へ来た値の移り変わり（liveValueMerge.ts）。liveExtractedData と同じく
  // Playground の再現用の派生データで、GET では返さない・PATCH では書かせない（PATCH の応答には liveExtractedData と同じく載る）。
  liveValueHistory: z
    .record(
      z.string(),
      z.array(z.object({ at: z.string(), value: z.string(), action: z.enum(["set", "replace", "append", "carried"]) })),
    )
    .optional(),
  createdAt: z.string(), // ISO文字列
  /** 入っている案件フォルダの1件目（PRODUCT_DEV-1265 からは `groupIds[0]`。古い画面・軽い一覧が読む） */
  groupId: z.string().nullable(),
  /**
   * PRODUCT_DEV-1265: 入っている全部の案件フォルダ（1件目は `groupId` と同じ）。
   * 無い古い記録は `groupId` だけで読む。読むときは `caseFolderMembership.ts` の `meetingGroupIds` を通す。
   */
  groupIds: z.array(z.string()).optional(),
  presetId: z.string(),
  sourceMeetingId: z.string().optional(),
  /**
   * PRODUCT_DEV-796: この商談記録を作った（または後からつないだ）日程調整の scheduleId。
   *
   * マイページ「今日の予定」の突き合わせと、日程調整〜議事録〜商談記録のつながりの表示に使う。
   * **日程調整側には持たせない**（日程調整の本体は dev-lab と共有する部品のため、記録側だけで持つ）。
   * `null` は「つないでいたが外した」。未設定と同じく「つながりなし」として読む
   * （**会社名や時刻から推測して自動でつなぐ処理は無い**ので、「外した」と「未設定」を
   * 区別しなくても、外したものが勝手に戻ることは無い）。
   * ⚠ 議事録から作った商談記録にはコピーしない。議事録経由のつながりは `minutes.scheduleId` が正本。
   * ⚠ 商談記録はテナント全員に見えるが、日程調整は持ち主しか見られない。**この ID から
   * 日程調整の中身を引いて見せるときは、必ず持ち主に限る API（`GET /scheduling`）を通す。**
   * ⚠ **サーバはこの ID が本当に記録の持ち主の日程調整かを確かめていない**（記録の Lambda は
   * 日程調整の表を読めない）。守っているのは「つなぐのは記録の持ち主だけ」と「読み手が本人の分に
   * 閉じている」ことだけ。**日程調整の側から記録を逆引きする処理を足すときは、そこで持ち主
   * （記録の作成者 === 日程調整の持ち主）を照合すること。**
   */
  scheduleId: z.string().nullable().optional(),
  /**
   * PRODUCT_DEV-1017: 手帳の自分の予定（会議・商談）の行から作った記録の、その予定（`DDBSelfPlan.planId`）。
   * 作成時にだけ付く（後から付け替える API は無い）。
   * ⚠ 議事録から作った商談記録・続き記録・コピー記録にはコピーしない（議事録経由は `minutes.selfPlanId` が正本）。
   * ⚠ **サーバはこの ID が記録の持ち主の予定かを確かめていない**（記録の Lambda は自分の予定の表を読めない）。
   * 自分の予定の側から記録を逆引きするときは、**記録の作成者 === 予定の持ち主（見ている本人）**を照合すること。
   */
  selfPlanId: z.string().optional(),
  /**
   * PRODUCT_DEV-1225: 外部カレンダー（Google / Microsoft 365）の予定の行から作った記録の、その予定のキー
   * （`<provider>:primary:<eventId>`。カレンダーのメールアドレスは入れない）。作成時にだけ付く。
   * ⚠ `selfPlanId` と同じく持ち主を照合していない。予定の側から引くときは記録の作成者 === 見ている本人を確かめる。
   */
  externalEventKey: z.string().optional(),
  /**
   * PRODUCT_DEV-721 第3期 U5: 記録した時点の部署（記録した人がそのとき画面で選んでいた部署）。
   * 未設定は属性ごと無い（U5 より前の記録・部署に所属していない人の記録）。
   * - 編集の判定に使う（作成者かつその部署に所属。部署が無い・削除済みなら作成者。admin は従来どおり）
   * - 作成時はサーバが所属を確かめ、所属していなければ捨てる（記録そのものは保存する）
   * - 付け替えは PATCH で**単独で**送る（`saveMeetingDeptId`）
   */
  deptId: z.string().optional(),
  /**
   * PRODUCT_DEV-1024: 参加者（社内の userId）。記録者と管理者のほかに、**内容を直せる**人。
   * - 足す・外すのは記録者と管理者だけ（参加者は自分だけ外れられる）。PATCH で**単独で**送る
   * - 参加者が通るのは内容の編集だけ。削除・復元・ステータス変更・部署や日程調整の付け替え・
   *   振り返りの追記は今までどおり記録者と管理者（判定は shared の `canEditMeetingContent`）
   * - 記録者自身は入れない（入れても判定は変わらないが、一覧が二重になる）。未設定は属性ごと無い
   */
  participantUserIds: z.array(z.string().min(1).max(128)).max(50).optional(),
  /**
   * PRODUCT_DEV-1046: 記録者以外の人の振り返り（userId → 1人分）。記録者の分は `extractedData.minutes_review`。
   * - 話せるのは参加者と管理者（記録者は今までどおり minutes_review）。話しても参加者には足さない
   * - 商談記録の項目（共通の項目・タイトル・担当者）には触らない。保存しても version・更新日時は動かさない
   * - 直すのは本人だけ（管理者は誰の分でも）。PATCH の `memberReview` を**単独で**送る
   * - 未設定は属性ごと無い
   */
  memberReviews: z.record(z.string(), MemberReviewSchema).optional(),
  recordingMode: z.enum(["dialogue", "speed"]).optional(),
  /**
   * PRODUCT_DEV-1045: 議事録の作成中に先に作った商談記録の、その議事録（`DDBMeetingMinutes.pendingMeeting`）。
   * 作成時にだけ付く。付いている商談記録は作成時に仕上げへ回さず（meetings/post.ts）、
   * 議事録ができたときに minutes-finalize-worker が回す。meeting-sweeper は待つ時間を長く取る。
   */
  awaitingMinutesId: z.string().optional(),
  reviewed: z.boolean(),
  ownerId: z.string(),
  createdBy: z.string(),
  version: z.number().int(),
  updatedAt: z.string(), // ISO文字列
  updatedBy: z.string(),
  deletedAt: z.string().optional(), // ゴミ箱移動日時（ISO）
  deletedBy: z.string().optional(), // 削除実行者userId
  trashedGroupId: z.string().nullable().optional(), // ゴミ箱移動時の元groupId保存
  trashedGroupIds: z.array(z.string()).optional(), // PRODUCT_DEV-1265: ゴミ箱移動時に入っていた全部のフォルダ
  ttl: z.number().optional(), // DynamoDB TTL（epoch秒、deletedAt + 30日 / PRODUCT_DEV-576: テスト商談は作成時+1日）
  // PRODUCT_DEV-576(項目1): プリセットの「お試し対話（テスト）」で作成された商談。
  // 通常の記録フローで保存するが、一覧では作成者本人にのみ表示し、ttl(作成+1日)で自動失効する。
  isTest: z.boolean().optional(),
  /**
   * PRODUCT_DEV-1030: 公開範囲。未設定は公開（会社内の全員）。判定は `recordVisibility.ts` の
   * `canViewRecord` だけを通す。変えられるのは作成者本人だけで、PATCH で**単独で**送る。
   */
  visibility: RecordVisibilitySchema.optional(),
  /** PRODUCT_DEV-1030: 限定公開で共有する人の userId（限定公開のときだけ持つ）。 */
  sharedWith: SharedWithSchema.optional(),
  rawTranscript: z.string().optional(), // 生データの文字起こし文章（ユーザ発話のみ）
  cleanedTranscript: z.string().optional(), // 整形済み文字起こし文章（フィラー除去+辞書補完）
  dialogueLog: z.string().optional(), // 対話ログ全文（AI+User発話、dialogueモードのみ）
  meetingDate: z.string().optional(), // "YYYYMMDD" - ユーザー編集可能な商談日時（未設定時はdateにフォールバック）
  meetingTime: z.string().optional(), // "HHMMSS" - ユーザー編集可能な商談時刻（未設定時はtimeにフォールバック）
  audioContentType: z.string().optional(), // 自由記録モード音声のMIME (audio/webm | audio/mp4)
  // PRODUCT_DEV-527: CRM コピー文の保存済み LLM 要約。API 層（copy-summary）のみが書き込む
  // （PatchMeetingSchema には含めず、フロントから直接更新させない）。
  clipboardCopySummary: ClipboardCopySummarySchema.optional(),
  // PRODUCT_DEV-531: Sansanコンタクト連携で登録済みになった日時(ISO)。API層(sansan-register)
  // のみが書き込む（PatchMeetingSchema には含めない）。UIの「登録済」表示・再送警告に使う。
  sansanRegisteredAt: z.string().optional(),
  // PRODUCT_DEV-655: GateKeeper ヒアリングメモ抽出（第3コール）が書き込む
  // 「顧客本人に明示的に帰属された発言のうち preset/マスタ項目に当てはまらない事項」。
  // 個人商談のみ。商談編集画面で編集可能（PatchMeetingSchema に含める）。
  // 名寄せ確定保存時に individualProfile.otherNotes へ append-dedup で追記される。
  customerHearingNotes: z.string().optional(),
  // PRODUCT_DEV-998: ToDo候補（AI が商談記録と紐づく議事録から作る）。
  // 初めて編集画面を開いたとき・「再生成」を押したときに API 層（suggest-todos）だけが書き込む
  // （PatchMeetingSchema には含めない）。編集できる人（記録者・管理者・参加者）に見せる。
  suggestedTodos: z.array(SuggestedTodoSchema).optional(),
  // PRODUCT_DEV-998: ToDo候補を作った日時(ISO)。あれば「作成済み」（2回目以降の編集では作り直さない）。
  suggestedTodosGeneratedAt: z.string().optional(),
  // PRODUCT_DEV-1179: 音声対話で使ったトークンの合計（作成時にだけ付く。PATCH では書かせない・GET では返さない）
  realtimeUsage: RealtimeUsageSchema.optional(),
  /**
   * PRODUCT_DEV-1174: AI が作り直した日時（ISO）。再生成が終わったときに完了ワーカーだけが書く
   * （PatchMeetingSchema には含めない）。無ければ作成日時（createdAt）を生成日時として見せる
   * （作成時の生成は作成から数十秒で終わるので、作成時には持たせない）。
   */
  aiGeneratedAt: z.string().optional(),
  /**
   * PRODUCT_DEV-1174: 再生成を頼んだ日時（ISO）。PATCH の `regenerate` だけが書く。
   * `status:"processing"` で、これが `aiGeneratedAt`（無ければ作成日時）より新しければ「再生成中」
   */
  regenerateRequestedAt: z.string().optional(),
});

export type DDBMeetingContact = z.infer<typeof ContactSchema>;
export type DDBMeeting = z.infer<typeof DDBMeetingSchema>;

// PRODUCT_DEV-1143: Sansan の名刺の中身。登録 API の社外出席者にこの4項目をそのまま入れる
// （会社名・姓・名・メールアドレスを既存の名刺とそろえると、Sansan が同じ人・会社にまとめる。上限は Sansan の 250 字）。
export const SansanCardSchema = z.object({
  companyName: z.string().trim().min(1).max(250),
  lastName: z.string().trim().min(1).max(250),
  firstName: z.string().trim().max(250).optional(),
  email: z.string().trim().max(250).optional(),
});
export type SansanCard = z.infer<typeof SansanCardSchema>;

/**
 * PRODUCT_DEV-1143: Sansan に送る担当者の名前（氏名、無ければヨミガナ）。空なら null（Sansan に送らない担当者）。
 * preview の候補（`contactName`）と登録の突き合わせで**同じこの関数**を使う（片方だけ trim すると正しい登録が 409 になる）。
 */
export const sansanContactName = (contact: {
  name?: string | null;
  nameKana?: string | null;
}): string | null => contact.name?.trim() || contact.nameKana?.trim() || null;

// PRODUCT_DEV-531: Sansanコンタクト登録APIのリクエストボディ。
// memo 本文はフロントが renderCopyTemplate で構築済みのものを渡す（copy-summary と同方式）。
//
// PRODUCT_DEV-1143: sansanCards = 確認モーダルで選んだ名刺。
// - contactIndex は meeting.contacts の位置。contactName は `sansanContactName` で作った名前で、今の商談と合わなければ 409
// - 指定のある担当者は card の4項目で置き換え、指定の無い担当者は今までどおり商談の担当者から作る。contactIndex の重複は 400
// - **互換の要**: z.object は知らない項目を黙って捨てるので、古い API に sansanCards が届くと選んだ名刺は使われない。
//   web は preview が contactCandidates を返したとき（＝同じ Lambda が新しい版のとき）だけ sansanCards を送り、
//   登録の応答の usedSansanCards と送った数を照らし合わせる。この分岐を外さないこと
export const SansanRegisterRequestSchema = z.object({
  memo: z.string(),
  sansanCards: z
    .array(
      z.object({
        contactIndex: z.number().int().min(0),
        contactName: z.string(),
        card: SansanCardSchema,
      }),
    )
    .max(50)
    .superRefine((cards, ctx) => {
      const seen = new Set<number>();
      for (const c of cards) {
        if (seen.has(c.contactIndex)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate contactIndex ${c.contactIndex}` });
        }
        seen.add(c.contactIndex);
      }
    })
    .optional(),
});
export type SansanRegisterRequest = z.infer<typeof SansanRegisterRequestSchema>;

/** PRODUCT_DEV-1143: 確認モーダルの名刺の候補。送るのは SansanCard の4項目だけで、部署・名刺交換日は見分けるための表示用。 */
export interface SansanCardCandidate extends SansanCard {
  departmentName?: string;
  exchangeDate?: string; // "YYYY-MM-DD"
}

/** PRODUCT_DEV-1143: 担当者ごとの名刺の候補（同じ人の名刺は1件にまとめてある）。 */
export interface SansanContactCandidates {
  contactIndex: number; // meeting.contacts の位置
  contactName: string; // `sansanContactName` で作った名前（登録の突き合わせに使う）
  candidates: SansanCardCandidate[];
  /** 最初から選んでおく候補の位置（氏名と会社名がそろう候補が1件だけのとき） */
  suggestedIndex?: number;
}

// PRODUCT_DEV-531: POST /meetings/{id}/sansan-register/preview（確認モーダル）の応答。登録できないときも 200 で理由を返す（モーダルで止めるため）。
// 文言はサーバが作る（登録 API が 422 で返す文言と同じ）。画面は message をそのまま出す
export type SansanRegisterPreviewResponse =
  | {
      ok: true;
      ownerName: string;
      via: "recorder" | "fallback";
      recorderName: string;
      /** 社内出席者の表示名（名義の人が先頭。Sansan にいる商談の参加者が続く） */
      internalAttendeeNames: string[];
      /** Sansan にいなくて社内出席者に入れなかった参加者の人数 */
      skippedParticipants: number;
      /** PRODUCT_DEV-1143: 担当者ごとの名刺の候補。古い API は返さない（そのとき web は候補を出さず sansanCards も送らない） */
      contactCandidates?: SansanContactCandidates[];
      /** PRODUCT_DEV-1143: 名刺の検索に失敗した（候補は出せないが、今までどおり登録はできる） */
      candidateSearchFailed?: boolean;
    }
  | {
      ok: false;
      reason:
        | "not_configured"
        | "recorder_not_found"
        | "fallback_not_found"
        | "no_attendees" // 会社名か担当者が無い（両方が要る）
        | "invalid_datetime";
      message: string;
    };

export const SansanRegisterResponseSchema = z.object({
  success: z.boolean(),
  reportId: z.string().optional(),
  registeredAt: z.string(),
  /** PRODUCT_DEV-1143: 選んだ名刺で置き換えた担当者の数（古い API は返さない。web は送った数と照らし合わせる） */
  usedSansanCards: z.number().optional(),
});
export type SansanRegisterResponse = z.infer<
  typeof SansanRegisterResponseSchema
>;

// PRODUCT_DEV-493: 編集ログ計測（プリセット単位の修正率・項目別編集回数）
// hot store: 前後値を含む詳細イベントを保持し、DynamoDB TTL で 7日経過後に消失する。
// 永続的な集計（項目別編集回数・修正率）は admin 側の日次 aggregator が S3 に書き出す。
export const EditEventChangeSchema = z.object({
  fieldId: z.string(),
  label: z.string().optional(), // preset.fields 由来の表示ラベル（解決できた場合）
  before: z.string().nullable().optional(), // 文字列化した変更前値（複雑な値は JSON 文字列）
  after: z.string().nullable().optional(), // 文字列化した変更後値
});
export type DDBEditEventChange = z.infer<typeof EditEventChangeSchema>;

export const DDBEditEventSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#<YYYY-MM-DD(JST)>
  SK: z.string(), // EDIT#<ISO timestamp>#<ulid>
  tenantId: z.string(),
  presetId: z.string(),
  meetingId: z.string(),
  actorUserId: z.string(),
  eventKind: z.enum(["field_edit", "register_transition"]),
  // eventKind === "field_edit" のみ
  changedFieldIds: z.array(z.string()).optional(),
  changes: z.array(EditEventChangeSchema).optional(),
  // eventKind === "register_transition" のみ（仮登録→本登録時、同リクエストで項目編集を伴ったか）
  reviewedTransition: z.enum(["with_edit", "without_edit"]).optional(),
  createdAt: z.string(), // ISO8601
  ttl: z.number().int(), // epoch秒（createdAt + 7日）
});
export type DDBEditEvent = z.infer<typeof DDBEditEventSchema>;

// PRODUCT_DEV-434: 自然言語商談検索（AIエージェント検索）
export const MeetingFilterInterpretationSchema = z.object({
  company: z.string().optional(),
  contactName: z.string().optional(),
  /**
   * 記録者（自社ユーザ）の名前。「本蔵が記録した商談」の "本蔵"。
   *
   * `contactName`（取引先側の担当者）とは**別軸**。前者は商談の相手、こちらは
   * 商談を入力した社内の人で、突き合わせ先も searchText ではなくテナントの
   * ユーザ一覧 → `MeetingLiteRow.createdBy` になる。
   *
   * ここには**名前をそのまま**入れる（LLM は userId を知らない）。userId への
   * 解決はサーバ側で行う。
   */
  recordedBy: z.string().optional(),
  /**
   * **役割が言い回しから決まらない人名。**「クスマ素子さんが実施した商談」の "クスマ素子"。
   *
   * 日本語では「〜さんが実施した」「〜さんの商談」のように、その人が**記録した社内の人**
   * なのか**会った相手**なのかが文だけでは決まらない言い方がある。無理にどちらかへ倒すと
   * 外したときに気づけないので、**決まらないことをそのまま**この枠で受ける。
   *
   * 役割の判定はサーバ側が**データで**行う（テナントのユーザ名簿に載っていれば記録者、
   * 載っていなければ取引先の担当者）。言い回しの列挙に頼らないので、新しい動詞が
   * 出てきても壊れない。
   */
  personName: z.string().optional(),
  dateFrom: z.string().optional(), // "YYYY-MM-DD" (JST)
  dateTo: z.string().optional(),
  reviewed: z.boolean().optional(),
  status: z.enum(["active", "trashed", "disabled"]).optional(),
  groupId: z.string().optional(),
  semanticQuery: z.string().optional(), // Stage 3 でベクトル検索に使用
  keywords: z.array(z.string()).optional(),
});
export type MeetingFilterInterpretation = z.infer<
  typeof MeetingFilterInterpretationSchema
>;

export const MeetingSearchRequestSchema = z.object({
  query: z.string().min(1).max(500),
  limit: z.number().int().min(1).max(100).optional(),
  groupId: z.string().optional(),
  rerank: z.boolean().optional(), // Stage 3 で LLM rerank の有効化
});
export type MeetingSearchRequest = z.infer<typeof MeetingSearchRequestSchema>;

/**
 * PRODUCT_DEV-620: AI 回答が引用した記録の種別。
 * 商談記録以外（議事録/メモ/ToDo）も引用できるようにするため導入した。
 */
export const SearchRecordKindSchema = z.enum([
  "meeting",
  "minutes",
  "memo",
  "todo",
]);
export type SearchRecordKind = z.infer<typeof SearchRecordKindSchema>;

/** PRODUCT_DEV-620: 引用元の記録（種別 + id）。UI はこれを引いてカセットを描く */
export const CitedRecordSchema = z.object({
  kind: SearchRecordKindSchema,
  id: z.string(),
});
export type CitedRecord = z.infer<typeof CitedRecordSchema>;

/**
 * PRODUCT_DEV-434: 引用元の「箇所」。
 *
 * S3 Vectors への移行で近傍検索が**チャンク単位**で返るようになり、どのチャンクが
 * 最も近かったかとその本文が追加コストなしで手に入るようになった（本文は非フィルタ
 * メタデータとして格納してあり、クエリの戻りに含まれる）。これにより回答の根拠を
 * 「この商談」ではなく「この商談のこの箇所」まで示せる。
 *
 * 追加専用フィールドなので、既存 consumer は無視すれば従来どおり動く。
 */
export const CitedChunkSchema = z.object({
  kind: SearchRecordKindSchema,
  id: z.string(),
  /** レコード内でのチャンク位置。0 は構造化サマリ、1 以降が文字起こし。 */
  chunkIdx: z.number().int(),
  /** 該当チャンクの本文（表示用に呼び出し側で切り詰める）。 */
  text: z.string(),
});
export type CitedChunk = z.infer<typeof CitedChunkSchema>;

export const MeetingSearchResponseSchema = z.object({
  /**
   * 該当した商談を関連度順に `limit` 件まで。**引用された商談だけではない**。
   *
   * PRODUCT_DEV-434: 一時期ここを `citedRecords` の商談分だけに絞っていたが、
   * 回答プロンプトは引用を 3 件までに制限しているため、回答文が「18 件です」と
   * 言うのに画面には 3 件しか出ない、という食い違いになっていた。
   * **引用は「根拠として挙げたもの」で、該当一覧とは別の概念**。
   * どれが引用かは `citedMeetingIds` / `citedRecords` で判別する。
   */
  meetings: z.array(DDBMeetingSchema.partial()),
  interpretation: MeetingFilterInterpretationSchema,
  /** 該当した商談の総数（`limit` を掛ける前）。`meetings.length` とは一致しない。 */
  total: z.number().int(),
  truncated: z.boolean(),
  /** PRODUCT_DEV-434: LLM が上位商談を根拠に生成する自然言語回答 */
  answer: z.string().optional(),
  /**
   * PRODUCT_DEV-434: answer の根拠となった商談 id（重要度順）。
   * PRODUCT_DEV-620 以降は商談以外も引用しうるため `citedRecords` が正本だが、
   * 既存 consumer（/chat）の後方互換のため商談分だけをここにも引き続き入れる。
   */
  citedMeetingIds: z.array(z.string()).optional(),
  /** PRODUCT_DEV-620: 種別つきの引用元（商談/議事録/メモ/ToDo を横断） */
  citedRecords: z.array(CitedRecordSchema).optional(),
  /**
   * PRODUCT_DEV-434: 引用元の「箇所」。意味検索で最も近かったチャンクを
   * レコードごとに 1 件返す。ベクトル検索が無効／失敗した場合は空になる。
   */
  citedChunks: z.array(CitedChunkSchema).optional(),
});

/**
 * PRODUCT_DEV-620: 引用された議事録 / メモ / ToDo は、各一覧 API が返すのと
 * **同じ型**（`MinutesListItem` / `MemoItem` / `TodoItem`）をそのまま返す。
 *
 * ここで別形を再定義すると、同じ形が contract・API・web の 3 箇所に分散し、
 * 片方だけ変えたときに静かにズレる（web 側が欠けたフィールドを偽の必須値で
 * 埋め直す羽目になっていた）。これらは zod スキーマを持たない既存 interface
 * なので、型だけを合成する（このスキーマはレスポンス生成の型付け用であり
 * `parse()` はされない）。
 */
export type MeetingSearchResponse = z.infer<
  typeof MeetingSearchResponseSchema
> & {
  citedMinutes?: MinutesListItem[];
  citedMemos?: MemoItem[];
  citedTodos?: TodoItem[];
};

// ── PRODUCT_DEV-613: 商談軽量索引 (lite index) ─────────────────────────────
// 一覧/検索/顧客件数を数万件規模へスケールさせるための「共有・軽量索引」。
// テナント単位で S3 に材料化し DDB Streams で増分維持、クライアントが読んで
// 即時フィルタ/件数/集計/ページングを行う。本文(transcript)は載せない。
// isTest(作成者限定・一時)は共有索引から除外する。表示忠実度のため、識別情報
// (isIndividual/displayName) と表示タイトルは索引生成時に precompute する。
export const MeetingLiteRowSchema = z.object({
  id: z.string(),
  date: z.string(), // 採番日 "YYYYMMDD"
  time: z.string(), // 採番時刻 "HHMMSS"
  meetingDate: z.string().optional(), // ユーザ編集の商談日 "YYYYMMDD"（effective date 算出用）
  meetingTime: z.string().optional(), // ユーザ編集の商談時刻 "HHMMSS"
  displayTitle: z.string(), // extractedData.title || title || "" （表示用・生）
  company: z.string().nullable().optional(), // 生: 仮情報判定/法人名完全一致フィルタ/お気に入りメタ
  companyId: z.string().optional(), // 顧客件数(B)集計キー
  isIndividual: z.boolean(), // precompute: 個人商談か（カード名行のアイコン分岐）
  displayName: z.string().optional(), // precompute: カード名行の表示名（個人氏名 or 法人名）
  groupId: z.string().nullable().optional(), // グループフィルタ/フォルダラベル
  groupIds: z.array(z.string()).optional(), // PRODUCT_DEV-1265: 入っている全部のフォルダ
  reviewed: z.boolean(), // 仮情報/状態フィルタ
  status: z.string(), // 整形中スピナー等（索引は基本 active のみ）
  ttl: z.number().optional(), // 自動削除予定ラベル
  createdBy: z.string(), // 作成者フィルタ/お気に入りメタ・isTest の per-viewer 絞り込み
  // PRODUCT_DEV-613: お試し対話のテスト商談。索引には含めるが、配信 API が
  // 「isTest かつ作成者≠閲覧者」を落とす（作成者本人のみ一覧に出る）。顧客件数集計は
  // isTest を数えない（テスト商談は実商談ではない）。
  isTest: z.boolean().optional(),
  // PRODUCT_DEV-1030: 公開範囲。配信 API が `canViewRecord` で見せてよい行だけに絞る
  // （索引は会社で1本なので、限定・非公開の行も載せたうえで閲覧者ごとに落とす。isTest と同じ）
  visibility: RecordVisibilitySchema.optional(),
  sharedWith: SharedWithSchema.optional(),
  updatedAt: z.string().optional(), // 一覧の更新日時ソート（お気に入り/履歴）用
  createdAt: z.string().optional(), // updatedAt 欠落時のソートフォールバック用
  // PRODUCT_DEV-613: 顧客件数(B)のサーバ側集計で「最終商談の preset/モード」を出すため。
  presetId: z.string().optional(),
  recordingMode: z.enum(["dialogue", "speed"]).optional(),
  searchText: z.string(), // 正規化連結（キーワード一致用・表示には使わない）
  // PRODUCT_DEV-698: 管理者ビュー（scope=team）は索引しか読まない。次アクションが無いと
  // 「予定はあったのに記録が無い」と会社別の「状況」が出せないため、索引に載せる。
  // 本文を毎回引くと月表示で数百件の GetItem になる。既存行は日次 reconcile で埋まる。
  nextAction: z.string().optional(), // extractedData.next_action の先頭（表示用・生）
  nextActionOn: z.string().optional(), // 次アクションから読んだ日付 "YYYY-MM-DD"（全文から読む）
  // PRODUCT_DEV-796: 日程調整の詳細に「この予定の記録」を出すため（商談本体を引かずに逆引きする）。
  // つながっていない（未設定・null）行には載せない。⚠ 持ち主を照合していない値なので、読む側で
  // 「記録の作成者 === 日程調整の持ち主」を確かめる（DDBMeetingSchema.scheduleId の注意書き）。
  scheduleId: z.string().optional(),
  // PRODUCT_DEV-782: 引き継いで作った記録の元商談（次回アポの行・グループの続き・コピー記録で付く）。
  // 手帳で「過ぎた次回アポを後日記録した」を予定の日に結ぶため（記録の商談日は作った日になるので、
  // 日付では結べない）。未設定の行には載せない。⚠ 持ち主を照合していない値なので、読む側で
  // 記録と元商談の作成者が同じかを確かめる（scheduleId と同じ）。⚠ この項目より前の行は夜間の
  // 全再構築まで載らない。未設定は「つながりなし」ではなく「分からない」と読む。
  sourceMeetingId: z.string().optional(),
  // PRODUCT_DEV-1017: 手帳の自分の予定の行から作った記録の、その予定。手帳で自分の予定を「記録済」に
  // するため。⚠ 持ち主を照合していない値なので、読む側で記録の作成者 === 見ている本人を確かめる。
  // この項目より前の行は夜間の全再構築まで載らない（元々この項目を持つ記録は無いので実害は無い）。
  selfPlanId: z.string().optional(),
  // PRODUCT_DEV-1225: 外部カレンダーの予定の行から作った記録の、その予定のキー。手帳でその予定を「記録済」にする。
  // ⚠ 持ち主を照合していない値なので、読む側で記録の作成者 === 見ている本人を確かめる（selfPlanId と同じ）。
  externalEventKey: z.string().optional(),
  // PRODUCT_DEV-721: 記録した時点の部署（一覧の「部署」の絞り込みと、編集できるかの事前判定）。
  // 未設定の行には載せない（U5 より前の記録は日次の作り直しでも未設定のまま）。
  deptId: z.string().optional(),
  // PRODUCT_DEV-796: 振り返り（extractedData.minutes_review）が書かれているか。
  // ⚠ **未設定は「分からない」**（この項目より前に作られた行は日次 reconcile まで載らない）。false と読まない。
  hasReview: z.boolean().optional(),
});
export type MeetingLiteRow = z.infer<typeof MeetingLiteRowSchema>;

// GET /meetings/lite-index レスポンス。テナント配下の active・isTest除外 の全行を返す。
export const MeetingLiteIndexResponseSchema = z.object({
  success: z.boolean(),
  rows: z.array(MeetingLiteRowSchema),
  generatedAt: z.string().optional(), // 索引の材料化(最終更新)時刻 ISO
});
export type MeetingLiteIndexResponse = z.infer<
  typeof MeetingLiteIndexResponseSchema
>;

/**
 * 自分の予定の ID の形（ulid。PRODUCT_DEV-1017）。長すぎる値でキーの上限に当たって 500 にしないため、
 * 入口で形だけ確かめる（api の `isSelfPlanId` もこれを使う）。⚠ 自分の予定の節（下の方）ではなくここに
 * 置くのは、すぐ下の `CreateMeetingSchema` がモジュールの読み込み時に使うため（const は宣言より前に使えない）。
 */
export const SELF_PLAN_ID = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * PRODUCT_DEV-1041: 予定の行から作る記録の商談日時（予定の開始時刻を日本時間で。"YYYYMMDD" / "HHMMSS"）。
 * 送らなければ従来どおり作成時刻（`createMeeting`）。片方だけでは使わない（日付と時刻がちぐはぐになる）。
 * **形が不正でも 400 にしない**（録り終えた記録を失わせない。捨てて作成時刻に倒す）。
 * ⚠ 置き場所が上の `SELF_PLAN_ID` と同じなのも同じ理由（すぐ下の `CreateMeetingSchema` が使う）。
 */
export const PlannedMeetingDateSchema = z
  .string()
  .regex(/^\d{8}$/)
  .optional()
  .catch(undefined);
export const PlannedMeetingTimeSchema = z
  .string()
  .regex(/^\d{6}$/)
  .optional()
  .catch(undefined);

// 登録可能なカラムを定義
// PRODUCT_DEV-402: company 必須 → presetId 必須に切替。
// company は API 層で preset.primaryFieldId 由来で埋める。
export const CreateMeetingSchema = DDBMeetingSchema.pick({
  title: true,
  duration: true,
  company: true,
  contacts: true,
  status: true,
  extractedData: true,
  groupId: true,
  presetId: true,
  sourceMeetingId: true,
  recordingMode: true,
  audioContentType: true,
  isTest: true,
})
  .partial()
  .required({ presetId: true })
  // PRODUCT_DEV-796: 今日の予定（日程調整の行）から作った記録。作成時に「外した」（null）は
  // 意味を持たないので、保存形（nullable）ではなく文字列だけを受ける
  .extend({
    scheduleId: z.string().min(1).max(64).optional(),
    // PRODUCT_DEV-1017: 手帳の自分の予定の行から作った記録。**形が不正でも 400 にしない**
    // （録り終えた記録を失わせない）。捨てた値はキーが undefined のまま残るので、
    // 受け取る側（meetings/post.ts）が値のあるときだけ保存へ渡す
    selfPlanId: z.string().regex(SELF_PLAN_ID).optional().catch(undefined),
    // PRODUCT_DEV-1225: 外部カレンダーの予定の行から作った記録。形が不正でも 400 にしない（selfPlanId と同じ）
    externalEventKey: z.string().regex(EXTERNAL_EVENT_KEY_RE).optional().catch(undefined),
    // PRODUCT_DEV-1041: 予定・議事録から作った記録の商談日時（予定の開始時刻 / 議事録を録った時刻）
    meetingDate: PlannedMeetingDateSchema,
    meetingTime: PlannedMeetingTimeSchema,
    // PRODUCT_DEV-721: 記録先の部署（画面のいまの部署）。所属していなければサーバが捨てる
    deptId: z.string().min(1).max(64).optional(),
    // PRODUCT_DEV-1030: 非公開の予定・議事録から作った記録は最初から同じ公開範囲にする。
    // **形が不正でも 400 にしない**（録り終えた記録を失わせない）。ただし不正な値は**非公開**に倒す
    // （狭めたつもりの記録を黙って公開にしない。送らなければ従来どおり公開）
    visibility: RecordVisibilitySchema.optional().catch("private"),
    sharedWith: SharedWithSchema.optional().catch(undefined),
    // PRODUCT_DEV-1045: 作成中の議事録ができたら仕上げる商談記録（`status:"processing"` で作る）
    awaitingMinutesId: z.string().min(1).max(64).optional(),
    // PRODUCT_DEV-1179: 音声対話で使ったトークンの合計。**形が不正でも 400 にしない**（計測のための項目で
    // 録り終えた記録を失わせない）。捨てた値はキーが undefined のまま残るので、meetings/post.ts が値のあるときだけ保存へ渡す
    realtimeUsage: RealtimeUsageSchema.optional().catch(undefined),
  })
  .strict();

export type DDBCreateMeeting = z.infer<typeof CreateMeetingSchema>;

// POST /meetings リクエストボディ（conversationLog付き）
export const CreateMeetingWithLogSchema = CreateMeetingSchema.extend({
  conversationLog: z.string().optional(), // 文字起こしログ（非同期処理用）
});
export type CreateMeetingWithLog = z.infer<typeof CreateMeetingWithLogSchema>;

// 更新可能なカラムを定義
export const PatchMeetingSchema = DDBMeetingSchema.pick({
  title: true,
  duration: true,
  company: true,
  contacts: true,
  status: true,
  extractedData: true,
  groupId: true,
  presetId: true,
  reviewed: true,
  rawTranscript: true,
  cleanedTranscript: true,
  dialogueLog: true,
  meetingDate: true,
  meetingTime: true,
  // PRODUCT_DEV-655: ヒアリングメモ（個人）。編集画面でレビュー・修正して保存できる。
  customerHearingNotes: true,
})
  .partial()
  // PRODUCT_DEV-512: 顧客マスタ担当者の置換更新指示（改姓等のキー変更を既存エントリへ反映）。
  // DDBMeetingSchema には含めない（商談レコードには保存しない）。patch.ts で剥がして使う。
  .extend({
    // 1保存=1商談の担当者数に対応する更新指示のため上限を設ける（一括全置換の濫用防止）。
    contactMasterUpdates: z
      .array(ContactMasterUpdateSchema)
      .max(50)
      .optional(),
    // PRODUCT_DEV-542 ③: 顧客マスタへ「同期マージする担当者」を明示する任意リスト。
    // 「今回は変更しない（マスタはそのまま）」を選んだ担当者を商談レコードには残しつつ、
    // マスタ同期からは除外するために使う。未指定なら従来どおり contacts 全件を同期する
    // （後方互換）。DDBMeetingSchema には含めない（patch.ts で剥がして同期にのみ使う）。
    contactsToMerge: z.array(ContactSchema).max(100).optional(),
    // PRODUCT_DEV-543(改): 個人(顧客マスタ)への保存時反映指示。商談中は自動登録せず（仮登録）、
    // 編集画面でユーザが「新規追加 / 既存へ経歴追加 / 今回は変更しない」を確定して送る。
    // 氏名・各項目値は本 patch の extractedData.customer_info（編集後最新）から再取得する
    // ため、ここには PII を載せない。DDBMeetingSchema には含めない（patch.ts で剥がして使う）。
    individualRegistration: z
      .object({
        // "skip" は PRODUCT_DEV-543改で UI から発行されない旧互換値（「登録済データを使用する」は
        // 反映指示を送らない＝個人マスタ不変）。旧データ/旧クライアント互換のため enum から削除しない。
        decision: z.enum(["new", "existing", "skip"]),
        // existing のとき更新対象の個人レコード companyId。
        targetCompanyId: z.string().optional(),
        // PRODUCT_DEV-543改: 経歴追加対象はサーバが customer_info とマスタの差分で自動判定するため
        // fields は実質未使用（常に []）。契約は後方互換のため維持（即時削除しない）。
        fields: z
          .array(z.enum(["occupation", "traits", "age", "income", "family"]))
          .default([]),
      })
      .optional(),
    // PRODUCT_DEV-796: 日程調整へ後からつなぐ（文字列）・外す（null）。
    // ⚠ **単独で送る。** ほかの項目と一緒に送ると 400（通常の保存の副作用を巻き込まないため）。
    // つなぐのは記録の持ち主だけ（外すのは管理者も可）。判定は api の utils/scheduleLink.ts。
    scheduleId: z.string().min(1).max(64).nullable().optional(),
    // PRODUCT_DEV-721: 部署の付け替え。⚠ **単独で送る**（ほかの項目と一緒なら 400）。
    // 作成者は自分の所属の中、admin はテナントの削除されていない部署。外す（null）は作らない
    deptId: z.string().min(1).max(64).optional(),
    // PRODUCT_DEV-1024: 参加者の置き換え（全員分を送る。空配列で全員外す）。⚠ **単独で送る**
    // （ほかの項目と一緒なら 400）。記録者と管理者だけ。参加者は自分を外す置き換えだけ通る
    participantUserIds: z.array(z.string().min(1).max(128)).max(50).optional(),
    // PRODUCT_DEV-1046: 振り返りの手直し。⚠ **単独で送る**（ほかの項目と一緒なら 400）。
    // userId を省くと自分の分。他人の分は管理者だけ。送った観点でその人の分を丸ごと置き換える。
    // PRODUCT_DEV-1204: 記録者の分（userId が記録者）も、記録者本人か記録を直せる人（canModifyMeeting）なら送れる。
    // そのときは extractedData.minutes_review だけを置き換え、確定（reviewed）は変えない（振り返りだけの保存）。
    // text は記録者の古い形式（見出しに分かれていない1本の文。PRODUCT_DEV-945 より前）を直すときだけ使い、その文で置き換える。
    // text を送るときは slots を空にする。空の text・中身のある slots との同時送信・記録者以外の分での text は 400
    memberReview: z
      .object({
        userId: z.string().min(1).max(128).optional(),
        text: z.string().max(MEMBER_REVIEW_SLOT_MAX_LENGTH * MEMBER_REVIEW_MAX_SLOTS).optional(),
        slots: z
          .record(z.string().min(1).max(64), z.string().max(MEMBER_REVIEW_SLOT_MAX_LENGTH))
          .refine((o) => Object.keys(o).length <= MEMBER_REVIEW_MAX_SLOTS, {
            message: `slots must have at most ${MEMBER_REVIEW_MAX_SLOTS} entries`,
          }),
      })
      .strict()
      .optional(),
    // PRODUCT_DEV-1030: 公開範囲の変更。⚠ **単独で送る**（ほかの項目と一緒なら 400）。作成者本人だけ
    visibility: RecordVisibilitySchema.optional(),
    // PRODUCT_DEV-1174: 選んだプリセットで商談記録を作り直す。⚠ **単独で送る**（ほかの項目と一緒なら 400）。
    // 記録を直せる人（canModifyMeeting）だけ・完了済みの記録だけ。材料は記録に残っている対話ログ（議事録から
    // 作った記録も同じ欄）か文字起こしで、サーバが選ぶ。企業名・担当者・お客様の氏名とフリガナ・振り返りは残し、
    // 作り直した記録は仮登録（reviewed:false）になる。中身は完了ワーカーが書く（応答は status:"processing"）
    regenerate: z.object({ presetId: z.string().min(1).max(128) }).strict().optional(),
    sharedWith: SharedWithSchema.optional(),
    // PRODUCT_DEV-557: 【非推奨・後方互換のため残置】商談編集画面の「全店顧客番号」更新指示。
    // 当初は本フィールド経由で reviewed 保存の副作用として企業マスタへ反映していたが、
    // 「企業マスタを正本にして直接更新する」方針（Backlog #763426008）に変更したため、
    // web は本フィールドを送らず PATCH /companies/{companyId} で企業マスタを直接更新する。
    // api 側でも本フィールドは剥がして無視する（patch.ts）。ロールアウト中に旧 web が送っても
    // .strict() で 400 にしないため schema には残す。将来クリーンアップで削除予定。
    companySystemIntegrationId: z.string().optional(),
  })
  .strict();

export type DDBPatchMeeting = z.infer<typeof PatchMeetingSchema>;

// PRODUCT_DEV-527: POST /meetings/{meetingId}/copy-summary
// web が構築済みのコピー文（穴埋め後の最終全文）を送り、API が上限以内に
// LLM 要約して商談レコードへ保存 + 返却する。
// 上限はクライアントから受けず、API が meeting.presetId → preset.clipboardCopyMaxLength
// を参照して決める（任意の maxLength を指定した過剰切り詰め要約の保存を防ぐ）。
export const MeetingCopySummaryRequestSchema = z.object({
  text: z.string().min(1).max(20000), // 構築済みコピー文（sanitize は API 側でも冪等に適用）
  // テンプレート先頭の固定見出しブロック（例:「【定型文種類】\n＜...＞」）。
  // text の先頭と一致する場合のみ、要約後も一字一句保全される（LLM 任せにしない）。
  fixedHeader: z.string().max(2000).optional(),
});
export type MeetingCopySummaryRequest = z.infer<
  typeof MeetingCopySummaryRequestSchema
>;

export const MeetingCopySummaryResponseSchema = z.object({
  summary: ClipboardCopySummarySchema,
  // LLM が 2 回試行しても maxLength に収まらず機械的に切り詰めた場合 true
  truncated: z.boolean(),
  attempts: z.number().int(),
});
export type MeetingCopySummaryResponse = z.infer<
  typeof MeetingCopySummaryResponseSchema
>;

// Presets
/**
 * PRODUCT_DEV-1074（対話の版 3）: 聞かない条件。前提の項目（`fieldId`、サブ項目なら `subId`）の値が
 * `values` のどれかなら、この欄・サブ項目は聞かず、アプリが「対象外（reason）」を書き込む。
 * `values` は前提のサブ項目の選択肢（`options`）か、「なし」「わからない」「話に出なかった」のどれか。
 * 判定は presetConditions.ts の evaluateSkips だけで行う（文字の一致・AI の解釈に頼らない）。
 *
 * PRODUCT_DEV-1144（対話の版 4）: 1行の書き方を3つに広げた。どれか1つがあればよい（検査は validatePresetConditions）。
 * - `values`: 今までどおり。「回答なし」は「わからない」「話に出なかった」「未記録」の3語で持つ
 * - `answered: true`: 前提に答えがある（空・「なし」以外の3語・未記録・対象外でない）なら当たる（アプリが判定）
 * - `text`: 文章の条件。録音中の AI が会話（前提があればその答え）から判断し、当たれば聞かずに「対象外（reason）」と書く。
 *   前提の無い文章だけの条件は `fieldId` を空文字にする
 * 版 3 以下のプリセットは `values` だけを使う（`values` が空の行は v4 の書き方）。
 */
/** 聞かない条件の1行（前提の項目と、聞かない値） */
export const SkipWhenAtomSchema = z.object({
  fieldId: z.string(),
  subId: z.string().optional(),
  values: z.array(z.string()),
  /** PRODUCT_DEV-1144（版 4）: 前提に答えがあれば当たる */
  answered: z.boolean().optional(),
  /** PRODUCT_DEV-1144（版 4）: 文章の条件（AI が判断する） */
  text: z.string().optional(),
});
export const SkipWhenSchema = SkipWhenAtomSchema.extend({
  reason: z.string(),
  /**
   * 「かつ」で足す条件（任意）。この条件と `and` の全部に当たったときだけ聞かない。
   * 配列の要素どうし（skipWhen の各要素）は「または」（どれか1つに当たれば聞かない）。
   */
  and: z.array(SkipWhenAtomSchema).optional(),
});
export type DDBSkipWhen = z.infer<typeof SkipWhenSchema>;

/**
 * PRODUCT_DEV-1144（対話の版 4）: 条件の向き。"skip"（既定・未設定も同じ）= 以下を満たす場合は聞かない、
 * "only" = 以下を満たす場合のみ聞く（どれにも当たらなければ聞かない。前提に答えが無いときも聞かない）。
 * "only" で書く理由は skipWhen の理由が1つに決まるときだけ（決まらなければ何も書かない・空でもよい）。判定は presetConditions.ts
 */
export const ConditionModeSchema = z.enum(["skip", "only"]);

/** PRODUCT_DEV-1185（対話の版 5）: 区分値の入れ方（チェックボックス・ラジオボタン・プルダウン） */
export const OptionInputStyleSchema = z.enum(["checkbox", "radio", "select"]);
export type OptionInputStyle = z.infer<typeof OptionInputStyleSchema>;

const SubFieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  /**
   * @deprecated PRODUCT_DEV-1056: 管理画面からは触らない（使わないサブ項目は削除する）。
   * 全環境の移行（enabled:false の削除）が済むまで、読む側の `enabled !== false` は残す。
   */
  enabled: z.boolean().optional(),
  /**
   * PRODUCT_DEV-1056: 必須。空なら対話モードで次の欄へ進む前に聞き直させる
   * （担当者が「わからない」等と答えたら、その言葉を記録して止まる）。
   * true のものだけ必須（PRODUCT_DEV-1072 でお客様情報も特別扱いをやめた）。
   */
  required: z.boolean().optional(),
  /** PRODUCT_DEV-1074（対話の版 3）: 選択肢。AI はこの中（か「なし」「わからない」「話に出なかった」）から記録する */
  options: z.array(z.string()).optional(),
  /**
   * PRODUCT_DEV-1144（対話の版 4）: 記録のみ。聞かず、話に出たら選択肢（options・必須）から1つ記録する。出なければ空。
   * `required` とは同時に付けない。版 3 以下では使わない
   */
  recordOnly: z.boolean().optional(),
  /** PRODUCT_DEV-1074（対話の版 3）: 聞かない条件（どれか1つに当たれば聞かない） */
  skipWhen: z.array(SkipWhenSchema).optional(),
  /** PRODUCT_DEV-1144（版 4）: 条件の向き（ConditionModeSchema） */
  conditionMode: ConditionModeSchema.optional(),
  /**
   * PRODUCT_DEV-1185（対話の版 5）: 区分値を記録の画面で人が入れる形。checkbox は選択肢が2つのときだけ
   * （チェック＝1番目・外す＝2番目）。未設定は選択肢2つならチェック、それ以外はラジオ（presetSubject の optionInputStyleOf）。
   * 版 4 以下では使わない（記録の画面は文字入力のまま）
   */
  inputStyle: OptionInputStyleSchema.optional(),
});

export type DDBSubField = z.infer<typeof SubFieldSchema>;

// PRODUCT_DEV-543(改): プリセットの「情報種別」。company=企業情報 / individual=個人情報 / none=いずれでもない。
// 種別ごとのフィールド相互排他ルールは presetSubject.ts の validatePresetSubjectFields に集約。
export const PresetSubjectTypeSchema = z.enum(PRESET_SUBJECT_TYPES);
export type PresetSubjectTypeValue = z.infer<typeof PresetSubjectTypeSchema>;

export const FieldReferenceSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  status: z.string().optional(),
  value: z.string().optional().nullable(),
  question: z.string().optional(),
  /**
   * PRODUCT_DEV-1144: 録音画面で、この欄を聞いているあいだ読み上げ文の下に出す例示（「売上悪化、人材不足 など」）。
   * 画面だけのもので、録音中の対話 AI（プロンプト・ツール定義）には渡さない。ツール定義（web の
   * lib/prompts/tools.ts）が項目名を指定して組み立てているので入らない。どの対話の版でも使える
   */
  hint: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  /**
   * @deprecated PRODUCT_DEV-430: voiceOrder/displayOrder へ移行。
   * 後方互換: voiceOrder/displayOrder が未設定の場合のフォールバックとして使用。
   */
  order: z.number().optional(),
  /** PRODUCT_DEV-430: AIが音声で質問する順序（未設定時は order にフォールバック） */
  voiceOrder: z.number().optional(),
  /** PRODUCT_DEV-430: 画面で表示する順序（未設定時は order にフォールバック） */
  displayOrder: z.number().optional(),
  enabled: z.boolean().optional(),
  locked: z.boolean().optional(),
  $ref: z.string().optional(),
  subFields: z.array(SubFieldSchema).optional(),
  /** PRODUCT_DEV-1074（対話の版 3）: 欄ごと聞かない条件（どれか1つに当たれば聞かない） */
  skipWhen: z.array(SkipWhenSchema).optional(),
  /** PRODUCT_DEV-1144（版 4）: 条件の向き（ConditionModeSchema） */
  conditionMode: ConditionModeSchema.optional(),
});

export type DDBFieldReference = z.infer<typeof FieldReferenceSchema>;

/**
 * PRODUCT_DEV-1144（対話の版 4）: 欄をまたぐくり返し（問題点 → 経営課題 を「ない」まで聞く）。
 * 判定・検査・件ごとの値の読み書きは presetLoops.ts。商談記録の値は1件なら今までの形、2件以上なら件ごとの配列。
 */
export const LoopGroupSchema = z.object({
  id: z.string(),
  /** 音声順で並んだ欄の id（2つ以上。検査は validateLoopGroups） */
  fieldIds: z.array(z.string()),
  /** 2件目から読む「続けるかの確認」。最初の欄の質問を兼ねる */
  continueQuestion: z.string(),
  /** 多くても何件まで聞くか。未設定は上限なし */
  maxRounds: z.number().int().min(2).optional(),
});
export type DDBLoopGroup = z.infer<typeof LoopGroupSchema>;

/**
 * PRODUCT_DEV-944: プリセットごとに足す「振り返りの観点」。共通の観点（コード固定）の後に聞く。
 * 別プリセットではなく商談プリセットの中の欄として持つ（ユーザーが選ぶプリセット一覧に出さない）。
 * `id` は Function Calling のプロパティ名と `minutes_review` の保存キーになるので英数と `_` だけ。
 * `linkedFieldId` を付けると、その商談項目が埋まっていれば聞かず、空なら聞いてその項目へ書く。
 * id の重複・共通の観点との衝突・紐づけ先の実在は `validatePresetReviewPerspectives`
 * （reviewPerspectives.ts）が検査する。
 */
export const ReviewPerspectiveSchema = z.object({
  id: z.string().regex(REVIEW_PERSPECTIVE_ID_RE),
  label: z.string(),
  question: z.string(),
  linkedFieldId: z.string().optional(),
});
export type DDBReviewPerspective = z.infer<typeof ReviewPerspectiveSchema>;

// SpeedModeGuide（自由記録モード用ガイドブロック）
export const SpeedModeGuideItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  subItems: z.array(z.string()),
});

export type SpeedModeGuideItem = z.infer<typeof SpeedModeGuideItemSchema>;

/**
 * PRODUCT_DEV-684: プリセットのライフサイクル。
 *
 * - 未設定（undefined）= 従来どおり利用可能。**既存の全レコードがこれに当たる**ため後方互換。
 * - `draft` = AI 作成途中の下書き。録音・部署設定・データ出力のどこにも出さない。
 * - `active` = 明示的に有効化済み（未設定と同義。UI で「有効化」した結果を残すために持つ）。
 */
export const PRESET_STATUSES = ["draft", "active"] as const;
export const PresetStatusSchema = z.enum(PRESET_STATUSES);
export type PresetStatus = z.infer<typeof PresetStatusSchema>;

/**
 * 下書き（未完成）プリセットか。
 *
 * **下書きを隠す一次防御は `GET /presets` の既定除外**（`?includeDrafts=true` で opt-in）。
 * 論理削除の除外がクライアント 8 箇所の `!p.isDelete` に分散している構造をそのまま踏襲すると、
 * 1 箇所書き忘れただけで半完成のプリセットが録音画面に出るため、サーバ側で既定 deny にしてある。
 * この述語は **API を経由せず repository を直読みする経路**（データ出力の列カタログなど）で使う。
 *
 * 併せて `POST /meetings` は下書き presetId を 400 で拒否し、`PATCH /presets/{id}` の
 * status 変更は sysadmin 限定 + 有効化時に項目構成を検証する（多層防御）。
 */
export const isDraftPreset = (p: { status?: string | null }): boolean =>
  p.status === "draft";

export const StructuredPromptSchema = z.object({
  role: z.string(),
  absoluteRules: z.array(z.string()),
  rules: z.array(z.string()),
  initialAction: z.string().optional(),
  conversationExamples: z
    .array(
      z.object({
        trigger: z.string(),
        response: z.string(),
        forCopyMode: z.boolean().optional(),
      }),
    )
    .optional(),
});

export type StructuredPrompt = z.infer<typeof StructuredPromptSchema>;

export const DDBPresetSchema = z
  .object({
    PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
    SK: z.string().regex(/^PRESET#[^#]+$/, 'SK must be "PRESET#{presetId}"'),
    tenantId: z.string(),
    presetId: z.string(),
    order: z.number(),
    name: z.string(),
    description: z.string(),
    icon: z.string(),
    category: z.string(),
    systemPrompt: z.string(),
    fields: z.array(FieldReferenceSchema),
    readonly: z.boolean(),
    author: z.string().optional(),
    version: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    productTemplateId: z.string().optional(),
    isDelete: z.boolean().optional(),
    // PRODUCT_DEV-355: ピン留め時刻 ISO（テナント共有）。未設定/null = 非ピン。
    // 一覧はピン群を pinnedAt 昇順（先にピンしたものが上）→ 非ピン群を updatedAt 降順で表示。
    pinnedAt: z.string().nullable().optional(),
    realtimeModel: z.string().optional(),
    realtimeConfigId: z.string().optional(),
    dynamicContextSettings: z
      .object({
        enableCompanySearch: z.boolean().default(true),
        // PRODUCT_DEV-561: 個人検索（録音中に個人マスタを氏名で照合）。企業検索とは同時 ON 不可
        // （排他検証は presetSubject.validateSearchModeExclusivity に集約）。情報種別=個人
        // プリセットでのみ有効。既定 false = 従来挙動（回帰ゼロ）。
        enableIndividualSearch: z.boolean().default(false),
        enableDictionary: z.boolean().default(true),
      })
      .optional(),
    structuredPrompt: StructuredPromptSchema.optional(),
    speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
    // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id（fields[].id または $ref 末尾）。
    // 未設定時は API 層で extractedData.company → 空文字 にフォールバック。
    primaryFieldId: z.string().optional(),
    // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。未設定の既存プリセットは
    // 種別検証の対象外（後方互換）。設定すると presetSubject の相互排他ルールが効く。
    // null は「未設定へ戻す」(Patch でクリア) を許容するため nullable。
    subjectType: PresetSubjectTypeSchema.nullable().optional(),
    // PRODUCT_DEV-478: 商談詳細「コピーする」で生成する CRM 貼り付け用テキストの定型文。
    // `{{作成者}}` 等のスカラーと `{{#担当者}}...{{/担当者}}` の繰り返しブロックを持つ
    // プレースホルダ文字列（描画は web の renderCopyTemplate / 共通 renderTemplate）。
    // 未設定時はコピー機能が従来のハードコード形式にフォールバックする（後方互換）。
    // 注意: 録音フローの copyMode（過去商談からの引き継ぎ）とは無関係の別概念。
    clipboardCopyTemplate: z.string().optional(),
    // PRODUCT_DEV-527: CRM コピー文の文字数上限（例: 連携先 CRM の上限が 700 文字）。
    // コピー全文（URL 込み・コードポイント数）がこれを超えると LLM 要約が起動する。
    // 未設定 = 要約なし（既存挙動）。Patch で「解除」を null として SET するため
    // 実データ互換で nullable も許容する（consumer は falsy 判定 / ?? undefined で吸収）。
    clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
    // PRODUCT_DEV-1056: 対話の動きの版（presetSubject.ts の presetDialogueVersion）。未設定は 1（10/6 より前の動き）
    dialogueVersion: z.number().int().min(1).optional(),
  // PRODUCT_DEV-1144: 前の話に戻るときに AI が頭に付ける言葉（対話の版 4 だけ）。空・未設定は何も付けない
  returnPhrase: z.string().nullable().optional(),
  // PRODUCT_DEV-1144: 欄をまたぐくり返し（対話の版 4 だけ）。未設定・空はくり返さない
  loopGroups: z.array(LoopGroupSchema).nullable().optional(),
    // PRODUCT_DEV-684: ライフサイクル（下書き/有効）。未設定 = 従来どおり利用可能（後方互換）。
    status: PresetStatusSchema.nullable().optional(),
    // PRODUCT_DEV-944: 振り返りで足す観点。未設定 = 共通の観点だけ（後方互換）。
    reviewPerspectives: z.array(ReviewPerspectiveSchema).optional(),
  })
  .superRefine((v, ctx) => {
    // tenantId/presetId と PK/SK の整合性チェック（事故防止）
    const expectedPK = `TENANT#${v.tenantId}`;
    const expectedSK = `PRESET#${v.presetId}`;

    if (v.PK !== expectedPK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["PK"],
        message: `PK must equal ${expectedPK}`,
      });
    }
    if (v.SK !== expectedSK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["SK"],
        message: `SK must equal ${expectedSK}`,
      });
    }
  });

export type DDBPreset = z.infer<typeof DDBPresetSchema>;

export const PatchPresetSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  category: z.string().optional(),
  order: z.number().optional(),
  readonly: z.boolean().optional(),
  systemPrompt: z.string().optional(),
  productTemplateId: z.string().optional(),
  realtimeModel: z.string().optional(),
  realtimeConfigId: z.string().optional(),
  dynamicContextSettings: z
    .object({
      enableCompanySearch: z.boolean().default(true),
      // PRODUCT_DEV-561: 個人検索（企業検索と排他・情報種別=個人限定・既定 false）。
      enableIndividualSearch: z.boolean().default(false),
      enableDictionary: z.boolean().default(true),
    })
    .optional(),
  structuredPrompt: StructuredPromptSchema.optional(),
  speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
  fields: z
    .array(FieldReferenceSchema.extend({ id: z.string().optional() }))
    .optional(),
  // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id を更新可能に。
  // null/空文字は「未設定（フォールバック動作）」として扱う。
  primaryFieldId: z.string().nullable().optional(),
  // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。null=未設定へ戻す（クリア）。
  subjectType: PresetSubjectTypeSchema.nullable().optional(),
  // PRODUCT_DEV-478: CRM コピー定型文テンプレート。空文字は「未設定（従来形式にフォールバック）」。
  clipboardCopyTemplate: z.string().optional(),
  // PRODUCT_DEV-527: コピー文の文字数上限。null は「未設定（要約なし）に戻す」。
  clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
  // PRODUCT_DEV-1056: 対話の動きの版（presetSubject.ts の presetDialogueVersion）。未設定は 1（10/6 より前の動き）
  dialogueVersion: z.number().int().min(1).optional(),
  // PRODUCT_DEV-1144: 前の話に戻るときに AI が頭に付ける言葉（対話の版 4 だけ）。空・未設定は何も付けない
  returnPhrase: z.string().nullable().optional(),
  // PRODUCT_DEV-1144: 欄をまたぐくり返し（対話の版 4 だけ）。未設定・空はくり返さない
  loopGroups: z.array(LoopGroupSchema).nullable().optional(),
  // PRODUCT_DEV-355: 論理削除フラグ（復元時に false を送る）。
  isDelete: z.boolean().optional(),
  // PRODUCT_DEV-355: ピン留め時刻 ISO。ピン = now、解除 = null。
  pinnedAt: z.string().nullable().optional(),
  // PRODUCT_DEV-684: 下書き → 有効化（`{status:"active"}`）や、AI 作成中の下書き更新に使う。
  status: PresetStatusSchema.nullable().optional(),
  // PRODUCT_DEV-944: 振り返りで足す観点。`[]` で全部消す。保存できるのはシステム管理者だけ（patch.ts）。
  reviewPerspectives: z.array(ReviewPerspectiveSchema).optional(),
  /**
   * PRODUCT_DEV-679: 楽観ロック用。編集開始時点の `updatedAt` を送ると、
   * その間に他者が更新していた場合 409 で弾く（送らなければ従来どおり last-write-wins）。
   *
   * `version` を使わないのは、Presets の version が string 型で
   * 既存の数値インクリメント（createExpressionsForUpdate）に載らないため（presets.ts の NOTE 参照）。
   * `updatedAt` なら既存データの移行が要らない。
   * 永続化はしない（更新式には含めない）。
   */
  expectedUpdatedAt: z.string().optional(),
});
export type DDBPatchPreset = z.infer<typeof PatchPresetSchema>;

export const CreatePresetSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().default(""),
  icon: z.string().optional().default(""),
  category: z.string().optional().default(""),
  systemPrompt: z.string().optional().default(""),
  readonly: z.boolean().optional().default(false),
  realtimeModel: z.string().optional(),
  realtimeConfigId: z.string().optional(),
  dynamicContextSettings: z
    .object({
      enableCompanySearch: z.boolean().default(true),
      // PRODUCT_DEV-561: 個人検索（企業検索と排他・情報種別=個人限定・既定 false）。
      enableIndividualSearch: z.boolean().default(false),
      enableDictionary: z.boolean().default(true),
    })
    .optional(),
  structuredPrompt: StructuredPromptSchema.optional(),
  speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
  fields: z
    .array(FieldReferenceSchema.extend({ id: z.string().optional() }))
    .optional()
    .default([]),
  // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id を作成時に指定可能に。
  primaryFieldId: z.string().optional(),
  // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。null は未設定扱い（作成時は無視）。
  subjectType: PresetSubjectTypeSchema.nullable().optional(),
  // PRODUCT_DEV-478: CRM コピー定型文テンプレート。
  clipboardCopyTemplate: z.string().optional(),
  // PRODUCT_DEV-527: コピー文の文字数上限。null = 未設定（編集 UI が空欄を null で送る）。
  clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
  // PRODUCT_DEV-1056: 対話の動きの版（presetSubject.ts の presetDialogueVersion）。未設定は 1（10/6 より前の動き）
  dialogueVersion: z.number().int().min(1).optional(),
  // PRODUCT_DEV-1144: 前の話に戻るときに AI が頭に付ける言葉（対話の版 4 だけ）。空・未設定は何も付けない
  returnPhrase: z.string().nullable().optional(),
  // PRODUCT_DEV-1144: 欄をまたぐくり返し（対話の版 4 だけ）。未設定・空はくり返さない
  loopGroups: z.array(LoopGroupSchema).nullable().optional(),
  // PRODUCT_DEV-684: AI 作成フローは `draft` で作成する。未指定 = 従来どおり（利用可能）。
  status: PresetStatusSchema.nullable().optional(),
  // PRODUCT_DEV-944: 振り返りで足す観点。保存できるのはシステム管理者だけ（post.ts）。
  reviewPerspectives: z.array(ReviewPerspectiveSchema).optional(),
});
export type CreatePresetRequest = z.infer<typeof CreatePresetSchema>;

// ==== Departments (PRODUCT_DEV-572) ====
// テナント配下の「部署（グループ）」。作成は任意で、未作成なら従来挙動と完全に一致する
// （機能フラグ不要）。部署ごとに「表示Preset（許可リスト）」と「デフォPreset」を絞れる。
// unizo ローカル（所属を shared auth-service の DDBUserTenant には持たせない）。
// 既存の商談グループ（groupId / meeting-groups）とは無関係の別概念。
export const DDBDepartmentSchema = z
  .object({
    PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
    SK: z.string().regex(/^DEPT#[^#]+$/, 'SK must be "DEPT#{deptId}"'),
    tenantId: z.string(),
    deptId: z.string(),
    name: z.string(),
    order: z.number(),
    // デフォPreset。未設定/null = 従来デフォ（order 先頭）にフォールバック。
    defaultPresetId: z.string().nullable().optional(),
    // 表示Preset許可リスト。空配列/未設定 = 全Preset表示（絞り込みなし）。
    visiblePresetIds: z.array(z.string()).optional(),
    // PRODUCT_DEV-721: 外部コード（基幹システムや一括取込と突き合わせる鍵）。未設定は属性ごと無い
    // （null や空文字では持たない）。重複は作成・更新のときにテナント内の削除されていない部署どうしで
    // 検査する（409）が、同時書き込みや論理削除の取り消しでは重なりうる。取込側は重複を前提に照合すること。
    externalCode: z.string().optional(),
    isDelete: z.boolean().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    const expectedPK = `TENANT#${v.tenantId}`;
    const expectedSK = `DEPT#${v.deptId}`;
    if (v.PK !== expectedPK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["PK"],
        message: `PK must equal ${expectedPK}`,
      });
    }
    if (v.SK !== expectedSK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["SK"],
        message: `SK must equal ${expectedSK}`,
      });
    }
  });
export type DDBDepartment = z.infer<typeof DDBDepartmentSchema>;

/**
 * 業務軸のロール (PRODUCT_DEV-721)。
 *
 * ⚠ テナントロール (`DDBUserTenant.role` の "admin" | "power" | "member" | "viewer") とは**別の軸**。
 * どちらも "member" を持つため、所属側は `role` ではなく `deptRole` と呼ぶ。
 * - システム軸 (テナントロール) … テナントの設定とデータを管理する
 * - 業務軸 (これ)               … 部署のメンバーを見る
 *
 * viewer は初期実装に入れない (日報を書かず集計対象にも入らない「見るだけ」の層。
 * 定義は決まっているので、必要になったらここへ足す)。
 */
export const DeptRoleSchema = z.enum(["member", "manager"]);
export type DeptRole = z.infer<typeof DeptRoleSchema>;

// 所属レコード (ユーザー×テナント → 部署)。
// PRODUCT_DEV-721: **1ユーザーが複数の部署に所属できる (兼務)**。SK に departmentId を含めるのが
// その表現。departmentId は ULID で "#" を含まないため、SK は "#" 区切りで割り戻せる。
// ⚠ 第1期より前に設定された所属は対応前の形式 `MEMBER#{userId}` (1人 0〜1 部署) で、unizo は**読まない**
// (9/15 設計レビュー指摘4。以前はリポジトリが両方読んでいた。判定は `memberKeys.ts` の isLegacyMemberSK)。
// 残った行は `dev/tools/PRODUCT_DEV-721/delete-legacy-assignments.ts` で消す。**消すまでは survey の部署の
// 読み込み (`packages/shared-domain/survey/lambda/departments-source.ts`) が読み続け、unizo と見え方が食い違う。**
export const DDBUserDepartmentSchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z
    .string()
    .regex(
      /^MEMBER#[^#]+#[^#]+$/,
      'SK must be "MEMBER#{userId}#{departmentId}"',
    ),
  tenantId: z.string(),
  userId: z.string(),
  departmentId: z.string(),
  /**
   * 業務軸のロール。**読み出し側は未設定を "member" に倒すこと。**
   * 読み出しは zod parse ではなく型キャストなので、旧レコードが残っていても throw せず
   * `undefined` のまま流れてしまう。判定を通る前に既定へ寄せる。
   */
  deptRole: DeptRoleSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string().optional(),
});
export type DDBUserDepartment = z.infer<typeof DDBUserDepartmentSchema>;

// リクエストボディ（.strict() は使わない = useApiPost/Patch の tenantId 自動注入で 400 になる罠を回避。
// tenantId は認証コンテキスト由来をハンドラで採用し、body の値は信用しない）。
// PRODUCT_DEV-721: 外部コードの上限。前後の空白は落として保存する
export const DEPARTMENT_EXTERNAL_CODE_MAX = 64;

export const CreateDepartmentSchema = z.object({
  name: z.string().min(1),
  defaultPresetId: z.string().nullable().optional(),
  visiblePresetIds: z.array(z.string()).optional(),
  // 空文字は「未設定」として保存しない
  externalCode: z.string().trim().max(DEPARTMENT_EXTERNAL_CODE_MAX).optional(),
});
export type CreateDepartmentRequest = z.infer<typeof CreateDepartmentSchema>;

export const PatchDepartmentSchema = z.object({
  name: z.string().min(1).optional(),
  order: z.number().optional(),
  defaultPresetId: z.string().nullable().optional(),
  visiblePresetIds: z.array(z.string()).optional(),
  // null または空文字で消す（属性ごと REMOVE）
  externalCode: z.string().trim().max(DEPARTMENT_EXTERNAL_CODE_MAX).nullable().optional(),
  isDelete: z.boolean().optional(),
});
export type PatchDepartmentRequest = z.infer<typeof PatchDepartmentSchema>;

// ユーザーの所属設定 (PRODUCT_DEV-721 で兼務対応)。
// ⚠ **置換セマンティクス**: 送った集合がそのままその人の新しい所属になる。
// **空配列で所属解除** (旧 `departmentId: null` に相当)。
// 画面が「人単位のパネル ＋ ［保存］」なので、差分ではなく全体を送る形に揃えている。
export const AssignUserDepartmentSchema = z.object({
  assignments: z
    .array(
      z.object({
        departmentId: z.string().min(1),
        // 省略時は "member" (ハンドラ側で既定を埋める)
        deptRole: DeptRoleSchema.optional(),
      }),
    )
    // 部署は「支店 × 職種」で 40 前後の想定。1人が全部署を兼務することはないが、
    // 際限なく書き込ませないための上限
    .max(50)
    .superRefine((list, ctx) => {
      const seen = new Set<string>();
      for (const a of list) {
        if (seen.has(a.departmentId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `departmentId is duplicated: ${a.departmentId}`,
          });
        }
        seen.add(a.departmentId);
      }
    }),
});
export type AssignUserDepartmentRequest = z.infer<
  typeof AssignUserDepartmentSchema
>;

// ユーザー作成 (POST /admin/users) に unizo だけが足す項目 (PRODUCT_DEV-721)。
// 本体の `CreateUserRequest` はプロダクトをまたいで共有する auth-service の契約なので、そこには足さず
// unizo 側の追加分としてここに置く。departmentId はサーバが必須として検査する
// (全員が部署に所属する。サーバが作成と同時に member として所属させる)。
export const CreateUnizoUserExtensionSchema = z.object({
  departmentId: z.string().min(1).optional(),
});
export type CreateUnizoUserExtension = z.infer<
  typeof CreateUnizoUserExtensionSchema
>;

// Companies

// PRODUCT_DEV-543: 個人(type:"individual")の属性プロファイル。
// 法人(corporate)には決して設定しない（admin patch / repository / upsert の多層でクリア）。
// すべて optional・後方互換（既存レコードに無くても parse 成功）。
// age/income/occupation は「いつ時点か」が意味を持つため as-of 基準日（登録時=商談完了日,
// JST "YYYY-MM-DD"）を併記し、表示側で経過年や時点注記を計算する。
// PRODUCT_DEV-543(改): 個人プロファイル各項目の時系列履歴エントリ。
// value は表示値（年齢も "45" 等 string で統一）、asOf は基準日（JST "YYYY-MM-DD"。
// 商談記録由来＝商談日 / 顧客マスタ直接更新由来＝更新日(当日)）。
// PRODUCT_DEV-543(改2): source = 登録・更新経路。"meeting"=商談記録由来(meetingId付き) /
// "master"=顧客マスタ直接更新。optional・後方互換（source 無しの既存エントリも parse 成功）。
export const ProfileHistoryEntrySchema = z.object({
  value: z.string(),
  asOf: z.string(),
  source: z
    .object({
      kind: z.enum(["meeting", "master"]),
      meetingId: z.string().optional(), // kind==="meeting" のとき由来商談ID
    })
    .optional(),
});
export type ProfileHistoryEntry = z.infer<typeof ProfileHistoryEntrySchema>;

export const IndividualProfileSchema = z.object({
  occupation: z.string().optional(), // 職業（例: 開発エンジニア）— 最新値
  occupationAsOf: z.string().optional(), // 職業の基準日（JST "YYYY-MM-DD"）
  // @deprecated PRODUCT_DEV-655: 特徴カラムは廃止し otherNotes へ一本化。
  // 新規書き込みは行わない（既存データは移行ツール dev/tools/PRODUCT_DEV-655 で
  // otherNotes へ履歴ごと移設・クリアする）。後方互換 parse のためスキーマからは消さない。
  traits: z.string().optional(), // （旧）特徴（趣味/嗜好/性格/人物像など自由記述）
  ageEstimate: z.number().int().min(0).max(150).optional(), // 推定年齢（最新登録時点）
  ageAsOf: z.string().optional(), // 年齢の基準日（表示時に経過年を加算）
  income: z.string().optional(), // 収入（"500万円" 等、レンジ表現を許すため string）— 最新値
  incomeAsOf: z.string().optional(), // 収入の基準日（年注記に使用）
  family: z.string().optional(), // 家族構成（父・母・妻・子供 など）— 最新値
  // PRODUCT_DEV-655: その他ヒアリングできた事項（フリーテキスト・行単位の蓄積メモ）。
  // 既存5項目のような「最新値置換」ではなく、商談ごとの抽出行を append-dedup で蓄積する。
  // 構造化はしない（後で LLM 等により構造化する前提のメモ領域）。基準日は history 側で保持。
  otherNotes: z.string().optional(),
  // PRODUCT_DEV-543(改): 全項目の時系列履歴（「経歴追加」で選択した項目だけ追記）。
  // 上のフラット項目＝最新値、history＝過去含む全件（新しい順は呼び出し側の表示で整える）。
  // すべて optional・後方互換（履歴の無い既存レコードもそのまま parse 成功）。
  history: z
    .object({
      occupation: z.array(ProfileHistoryEntrySchema).optional(),
      // @deprecated PRODUCT_DEV-655: otherNotes へ移行（新規書き込みなし・移行後クリア）。
      traits: z.array(ProfileHistoryEntrySchema).optional(),
      age: z.array(ProfileHistoryEntrySchema).optional(),
      income: z.array(ProfileHistoryEntrySchema).optional(),
      family: z.array(ProfileHistoryEntrySchema).optional(),
      // PRODUCT_DEV-655: otherNotes の追記ログ。商談由来エントリ=その商談で追加した行のみ、
      // マスタ手修正由来=編集後全文を1件。value/asOf/source の形は既存項目と共通。
      otherNotes: z.array(ProfileHistoryEntrySchema).optional(),
    })
    .optional(),
});
export type IndividualProfile = z.infer<typeof IndividualProfileSchema>;

// PRODUCT_DEV-543(改): 「変更なし」標識（旧仕様）。
// PRODUCT_DEV-543(改2): この標識は**今後書き込まない**（履歴＝実値のみ・1日付1件）。
// 定数は残置し、既存データに残る「（変更なし）」を表示・フラット計算から除外するフィルタにのみ使う。
export const PROFILE_NO_CHANGE = "（変更なし）";

// PRODUCT_DEV-543(改): 経歴追加対象になり得る個人プロファイル項目のキー。
// PRODUCT_DEV-655: "traits"（特徴）は廃止し otherNotes（その他ヒアリングできた事項）へ一本化。
// 定数から除去することで、名寄せ反映・完全一致判定・同日競合検出・比較 UI が一斉に
// traits を対象外にする（既存データは移行ツール dev/tools/PRODUCT_DEV-655 で otherNotes へ移設）。
export const INDIVIDUAL_PROFILE_HISTORY_FIELDS = [
  "occupation",
  "age",
  "income",
  "family",
] as const;
export type IndividualProfileHistoryField =
  (typeof INDIVIDUAL_PROFILE_HISTORY_FIELDS)[number];

export const DDBCompanySchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z.string().regex(/^COMPANY#[^#]+$/, 'SK must be "COMPANY#{companyId}"'),
  tenantId: z.string(),
  companyId: z.string(),
  order: z.number(),
  type: z.enum(["corporate", "individual"]).default("corporate"),
  name: z.string(),
  nameKana: z.string(),
  aliases: z.array(z.string().min(1)).default([]),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string(),
  updatedBy: z.string(),
  isTemporary: z.boolean().optional().default(false),
  gbizCorporateNumber: z.string().optional(),
  location: z.string().optional(),
  // PRODUCT_DEV-212: 商談レビュー時に登録される企業担当者リスト（スナップショットではなくマスタ）
  contacts: z.array(ContactSchema).optional().default([]),
  // PRODUCT_DEV-420: 外部システム連携用ID（CSVアップロード時の重複チェックキー）
  systemIntegrationId: z.string().optional(),
  // PRODUCT_DEV-543: 個人(type:"individual")専用の属性プロファイル（法人では未設定）。
  individualProfile: IndividualProfileSchema.optional(),
  // PRODUCT_DEV-583: マイ顧客の社内担当。ownerId=主担当(編集権限を持つ1名)。
  // 初回reviewed商談の記録者を未所有時のみ自動付与(claimOwnershipIfUnowned)。以後は編集/引き継ぎで変更可。
  // 未設定=未所有(旧レコード/未担当)。担当情報の唯一の正本(逆引き非正規化は作らない)。
  ownerId: z.string().optional(),
  // subAssigneeIds=サブ担当(閲覧のみの追加担当・複数)。未設定は [] 相当として扱う。
  subAssigneeIds: z.array(z.string()).optional(),
});

export type DDBCompany = z.infer<typeof DDBCompanySchema>;

// PRODUCT_DEV-583: マイ顧客ブックマーク（per-user・UnizoCompaniesテーブル内の別アイテム）。
// PK=TENANT#{tenantId}#USER#{userId} は既存の tenant-wide Query(PK=TENANT#{tenantId}) が
// 返さない別パーティションなので衝突しない。companyIds はブックマーク済みの companyId 集合。
export const CustomerBookmarksSchema = z.object({
  PK: z
    .string()
    .regex(/^TENANT#[^#]+#USER#[^#]+$/, 'PK must be "TENANT#{tenantId}#USER#{userId}"'),
  SK: z.literal("CUSTOMER_BOOKMARKS"),
  tenantId: z.string(),
  userId: z.string(),
  companyIds: z.array(z.string()).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerBookmarks = z.infer<typeof CustomerBookmarksSchema>;

// PRODUCT_DEV-512: 法人/個人の判定とクロス参照防止の単一規約。
// 旧レコード(type 未設定)は法人扱いにフォールバックする。検索・候補・名寄せ等の
// 全経路でこのヘルパを使い、安全要件(個人情報のクロス参照防止)を1箇所に集約する。
type CompanyTypeLike = { type?: "corporate" | "individual" | null };
export const getCompanyType = (
  company: CompanyTypeLike,
): "corporate" | "individual" => company.type ?? "corporate";
export const isCorporateCompany = (company: CompanyTypeLike): boolean =>
  getCompanyType(company) === "corporate";

// PRODUCT_DEV-512: 企業名(別名含む)で有効な法人マスタを引く。
// 個人(individual)は対象外＝企業担当者⇔個人のクロス参照を作らない。
export const findActiveCorporateByName = <
  T extends CompanyTypeLike & {
    active: boolean;
    name: string;
    aliases?: string[];
  },
>(
  companies: T[],
  name: string,
): T | undefined =>
  companies.find(
    (c) =>
      c.active &&
      isCorporateCompany(c) &&
      (c.name === name || (c.aliases ?? []).includes(name)),
  );

// PRODUCT_DEV-420: 顧客マスタ CSVアップロード（admin only）
// PRODUCT_DEV-457: 既存企業に対しても上書きするため skip_* を update_* に置換
export const CompanyUploadRowStatusSchema = z.enum([
  "insert",
  "update_system_integration_id_match",
  "update_gbiz_corporate_number_match",
  "error_missing_name",
  "error_invalid_format",
  "error_conflicting_keys",
]);
export type CompanyUploadRowStatus = z.infer<typeof CompanyUploadRowStatusSchema>;

export const CompanyUploadRowSchema = z.object({
  rowNumber: z.number(),
  status: CompanyUploadRowStatusSchema,
  reason: z.string().optional(),
  parsedData: z
    .object({
      name: z.string(),
      nameKana: z.string().optional(),
      aliases: z.array(z.string()).optional(),
      systemIntegrationId: z.string().optional(),
      gbizCorporateNumber: z.string().optional(),
      location: z.string().optional(),
    })
    .optional(),
  existingCompanyId: z.string().optional(),
});
export type CompanyUploadRow = z.infer<typeof CompanyUploadRowSchema>;

export const CompanyUploadPreviewRequestSchema = z.object({
  csv: z.string().min(1),
});
export type CompanyUploadPreviewRequest = z.infer<typeof CompanyUploadPreviewRequestSchema>;

export const CompanyUploadPreviewResponseSchema = z.object({
  success: z.boolean(),
  rows: z.array(CompanyUploadRowSchema),
  summary: z.object({
    total: z.number(),
    insert: z.number(),
    update: z.number(),
    error: z.number(),
    updateBreakdown: z.record(z.string(), z.number()),
  }),
  error: z.string().optional(),
});
export type CompanyUploadPreviewResponse = z.infer<typeof CompanyUploadPreviewResponseSchema>;

export const CompanyUploadExecuteRequestSchema = z.object({
  csv: z.string().min(1),
});
export type CompanyUploadExecuteRequest = z.infer<typeof CompanyUploadExecuteRequestSchema>;

export const CompanyUploadExecuteResponseSchema = z.object({
  success: z.boolean(),
  inserted: z.number(),
  updated: z.number(),
  errors: z.number(),
  rows: z.array(CompanyUploadRowSchema),
});
export type CompanyUploadExecuteResponse = z.infer<typeof CompanyUploadExecuteResponseSchema>;

// gBizInfo 公開企業API レスポンス型
export const GBizInfoCompanySchema = z.object({
  corporateNumber: z.string(),
  name: z.string(),
  nameKana: z.string().optional(),
  location: z.string().optional(),
});
export type GBizInfoCompany = z.infer<typeof GBizInfoCompanySchema>;

/**
 * リクエストボディ, レスポンス型
 */
interface AuthApiRequestBody {
  tenantId: string;
}
/**
 * ミーティング一覧取得API
 * GET /meetings
 * in api/lambda/meetings/get.ts
 */
export type MeetingsRequestBody = AuthApiRequestBody & {};
export interface MeetingsResponse {
  success: boolean;
  meetings: DDBMeeting[];
}

/**
 * ミーティング内容補完API
 * POST /meetings/complete/post.ts
 * in api/lambda/meetings/complete/post.ts
 */
// PRODUCT_DEV-402: ExtractedDataSchema は全キー optional 化済みのため
// .partial() は実質 no-op だが、互換のため `.partial()` の振る舞いを維持。
// `.passthrough()` も継続適用される。
export const ExtractedDataPartialSchema = ExtractedDataSchema.partial();
export const MeetingCompleteRequestBodySchema = z
  .object({
    extractedData: ExtractedDataPartialSchema,
    conversationLog: z.string(),
    presetId: z.string().optional(),
  })
  .strict();

export type ExtractedDataPartial = z.infer<typeof ExtractedDataPartialSchema>;
export type MeetingCompleteRequestBody = AuthApiRequestBody &
  z.infer<typeof MeetingCompleteRequestBodySchema>;

// レスポンス型
export interface MeetingCompleteResponseBody {
  supplementedData: Record<string, unknown> & { title?: string };
  supplementedFields: string[];
  confidence: Record<string, number>;
  // PRODUCT_DEV-655 / PRODUCT_DEV-716: ヒアリングメモ抽出（第3コール・全テナント）。
  // customerHearingNotes=顧客本人（個人商談）分、contactHearingNotes=企業担当者（法人商談）分。
  customerHearingNotes?: string;
  contactHearingNotes?: Array<{ name: string; notes: string }>;
  /**
   * PRODUCT_DEV-1095: 録音中に後からつないだ項目のうち、補完が「言い直し・答えでない発言」として外した・訂正した
   * 項目と根拠（キーは「項目id」か「項目id.サブ項目id」）。ここに無い項目で情報が落ちたら保存前に録音中の値へ戻す。
   */
  liveMergeCorrections?: Record<string, string>;
}
export interface MeetingCompleteResponse {
  success: boolean;
  result: MeetingCompleteResponseBody;
}

/**
 * ミーティング作成API
 * POST /meetings/
 * in api/lambda/meetings/post.ts
 */
export type MeetingsPostRequestBody = AuthApiRequestBody &
  DDBCreateMeeting & {
    conversationLog?: string; // 文字起こしログ（非同期処理用）
    /**
     * PRODUCT_DEV-612（連鎖モデル FB）: 対話ログの出どころ。
     *
     * `"minutes"` は議事録本文から作った商談。post.ts が `DialogueCompletionMessage.mode`
     * へ透過し、worker が抽出テンプレートへ箇条書き書式ルールを連結する。
     * 未指定（通常の音声録音）は従来どおり。
     * CreateMeetingSchema は `.strict()` なので、conversationLog と同様に parse 前に剥がすこと。
     */
    mode?: "minutes";
    /**
     * PRODUCT_DEV-1095: 録音中に同じ項目へ来た値の移り変わり（liveValueMerge.ts）。
     * post.ts が `DialogueCompletionMessage.liveValueHistory` へ透過し、記録のあとの補完が統合を確かめる。
     * CreateMeetingSchema は `.strict()` なので、conversationLog と同様に parse 前に剥がすこと。
     */
    liveValueHistory?: LiveValueHistory;
  };
export interface MeetingsPostResponse {
  success: boolean;
  meeting: DDBMeeting;
}

/**
 * ミーティング取得API
 * GET /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/get.ts
 */
export type MeetingsMeetingIdRequestBody = AuthApiRequestBody & {};
export interface MeetingsMeetingIdResponse {
  success: boolean;
  meeting: DDBMeeting;
}

/**
 * ミーティング更新API
 * PATCH /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/patch.ts
 */
export type MeetingsPatchRequestBody = AuthApiRequestBody &
  DDBPatchMeeting & {
    /**
     * PRODUCT_DEV-612（連鎖モデル）: 振り返り録音の対話ログ。
     *
     * これが載っている PATCH は「既存商談に振り返りを追記する」経路を意味し、
     * patch.ts が status:"processing" にして dialogue-completion キューへ流す
     * （POST /meetings と同じ非同期 GateKeeper パターン）。この経路だけ extractedData は
     * **置換ではなくマージ**で適用される（空値で既存を消さない）。
     * PatchMeetingSchema は `.strict()` なので、post.ts と同様 parse 前に剥がすこと。
     */
    conversationLog?: string;
  };
export interface MeetingsPatchResponse {
  success: boolean;
  meeting: DDBMeeting;
  /**
   * PRODUCT_DEV-952: この保存で個人のお客さまとして顧客マスタに登録・解決した companyId。
   * 個人のお客さまは名寄せの確定＝この保存で初めて顧客マスタに作られるため、web は保存前に
   * 書き込み先を知り得ない。全店顧客番号を企業マスタへ書く先として使う。
   *
   * **必ず個人(individual)のレコードを指す。** `meeting.companyId` は、個人の名寄せが
   * 成立しなかったときに取引先名から法人を引き当てた結果が入ることがある（PRODUCT_DEV-583）。
   * それを書き込み先に使うと、個人のお客さまの番号が法人のマスタへ載ってしまうので、
   * この専用フィールドと取り違えないこと。
   */
  registeredIndividualCompanyId?: string;
}
/**
 * ミーティング削除API
 * DELETE /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/delete.ts
 */
export type MeetingsDeleteRequestBody = AuthApiRequestBody & {};
export interface MeetingsDeleteResponse {
  success: boolean;
  meetingId: string;
}

/**
 * プリセット一覧取得API
 * GET /presets
 * in api/lambda/presets/get.ts
 */
export type PresetsRequestBody = AuthApiRequestBody & {};
export interface PresetsResponse {
  success: boolean;
  presets: DDBPreset[];
}

/**
 * プリセット取得API
 * GET /presets/[presetId]
 * in api/lambda/presets/[presetId]/get.ts
 */
export type PresetsPresetIdRequestBody = AuthApiRequestBody & {};
export interface PresetsPresetIdResponse {
  success: boolean;
  preset: DDBPreset;
}

/**
 * 部署一覧取得API（管理者）
 * GET /admin/departments
 */
export interface DepartmentsResponse {
  success: boolean;
  departments: DDBDepartment[];
}

/**
 * 部署 作成/更新API（管理者）
 * POST /admin/departments, PATCH /admin/departments/[deptId]
 */
export interface DepartmentResponse {
  success: boolean;
  department: DDBDepartment;
}

/**
 * ユーザー所属一覧取得API（管理者）
 * GET /admin/user-departments — テナント内の userId → departmentId マッピング
 */
export interface UserDepartmentsResponse {
  success: boolean;
  // PRODUCT_DEV-721: 兼務があるので **同じ userId が複数行**返る
  assignments: { userId: string; departmentId: string; deptRole: DeptRole }[];
}

/**
 * ユーザー所属 設定/解除API（管理者）
 * PATCH /admin/user-departments/[userId]
 */
export interface AssignUserDepartmentResponse {
  success: boolean;
}

/**
 * 自分の所属部署取得API（一般ユーザー）
 * GET /my-department — 未所属/部署論理削除済みは department:null
 */
/** 所属している部署1つぶん（プリセットの絞り込みに使う情報つき）。 */
export interface MyDepartmentItem {
  deptId: string;
  name: string;
  defaultPresetId?: string | null;
  visiblePresetIds?: string[];
  /**
   * PRODUCT_DEV-721 U6: この部署での役割。管理者ビュー（部の数字）を「いまの部署」で出し分けるために使う。
   * 画面表示の出し分け用で、認可はサーバ側が正。**古いサーバは返さない**ので、無ければ `isManager` に倒す。
   */
  deptRole?: "member" | "manager";
  /**
   * PRODUCT_DEV-917: **既定の部署（先頭の所属）がこの部署である人**の userId。
   * 部署の付いていない古い商談を、商談記録の「チーム」に出すかの判定に使う（作成者がここに居れば出す）。
   * 日報（`belongsToReportDept`）と同じ規則で、兼務している人の古い記録は1つの部にだけ出る。
   * **古いサーバは返さない**ので、無ければ「部署の付いた記録だけ」で絞る。
   * ⚠ **表示の絞り込み専用。認可や名前の解決に使わない。** 所属の行はユーザーを無効化しても残るので、
   * もう居ない人の ID が混ざりうる（日報の既定の部署も同じ行を読むので、数え方はそろっている）。
   */
  defaultMemberUserIds?: string[];
}

export interface MyDepartmentResponse {
  success: boolean;
  /**
   * いま「その部署として」振る舞っている所属。
   *
   * ⚠ **この欄は後方互換のために単数のまま残す。** 兼務している人でも1つだけ入る。
   * 画面が切替を持たなかった頃から8画面がこれを読んでおり、形を変えると全部が壊れる。
   * 兼務の全部を見たいときは `departments` を読むこと。
   */
  department: MyDepartmentItem | null;
  /**
   * 所属している部署の全部（PRODUCT_DEV-917）。論理削除された部署は含まない。
   * **2つ以上あるときだけ**、画面に部署の切替を出す。
   */
  departments?: MyDepartmentItem[];
  /**
   * PRODUCT_DEV-721: **どこか1つでも** manager をしているか（業務軸）。
   *
   * `department` が単数なのは切替 UI がまだ無いためで、兼務している人は
   * ここに出ていない部署の manager かもしれない。**枠に紐づけず人単位で返す**ことで、
   * 画面が「返ってきた1部署のロール」で判断してしまうのを避ける。
   * 画面表示の出し分け用で、**認可はサーバ側が正**（改ざんは許容）。
   */
  isManager: boolean;
}

/**
 * 顧客マスタ取得API
 * GET /companies
 * in api/lambda/companies/get.ts
 */
export type CompaniesRequestBody = AuthApiRequestBody & {};
export interface CompaniesResponse {
  success: boolean;
  companies: DDBCompany[];
}

/**
 * 顧客マスタ検索API
 * GET /companies/search
 * in api/lambda/companies/search/get.ts
 */
export const CompaniesSearchRequestBodySchema = z
  .object({
    name: z.string(),
    // topはリクエストで受け取るためNumberへのキャストを行う
    top: z.preprocess((x) => {
      const num = typeof x === "string" ? Number(x) : x;
      return Number.isNaN(num) ? undefined : num;
    }, z.number().min(1).max(20).optional()),
    source: z.enum(["db", "gbizinfo", "all"]).optional().default("all"),
    // PRODUCT_DEV-512: 法人/個人の検索を単一typeにスコープし、結果を混在させない
    // （個人情報保護のための双方向クロス参照防止）。未指定は法人検索（後方互換）。
    // type横断("all")は意図的に用意しない。
    type: z.enum(["corporate", "individual"]).optional().default("corporate"),
    // PRODUCT_DEV-680: 音声対話 AI が聞き取った「読み」（全角カタカナ）。name とは別に受け取り、
    // 両方でスコアリングして良い方を採る（api/utils/stringDiff.ts hybridSearch）。
    // AI が音を漢字に起こすと name にも nameKana にも当たらず、用語辞書へエントリを足して
    // 当てにいく運用になっていた。読みを併送することで辞書なしでも顧客マスタに当たる。
    //
    // **`CompanyMatch.nameKana`（レスポンス側 = マスタ登録の読み仮名）とは別物**なので、
    // 「クエリ側の読み」であることが名前で分かるよう `queryKana` にしている。
    // optional なので未送信時の挙動は従来と完全に同一（後方互換）。
    // 長さ上限は編集距離計算のコスト青天井を避けるためのガード（読み仮名に 200 文字は不要）。
    queryKana: z.string().max(200).optional(),
  })
  .strict();

export type CompaniesSearchRequestBody = AuthApiRequestBody &
  z.infer<typeof CompaniesSearchRequestBodySchema>;

export interface CompanyMatch {
  query: string;
  id: string;
  name: string;
  nameKana?: string;
  speakAs: string;
  aliases?: string[];
  score: number;
  matchedField?: string;
  confidence?: number;
  // PRODUCT_DEV-212: 該当顧客マスタの既存担当者一覧。
  // 録音中に AI が同名担当者を新規登録するのを防ぐためのプロンプト注入に使用。
  contacts?: DDBContact[];
}

export interface CompaniesSearchResponse {
  success: boolean;
  result: {
    matches: CompanyMatch[];
    gbizMatches?: GBizInfoCompany[];
    needsConfirmation: boolean;
    topMatch: string | null;
  };
}

/**
 * 顧客マスタ登録API（ユーザ向け）
 * POST /companies
 * in api/lambda/companies/post.ts
 */
export const CreateCompanyRequestSchema = z.object({
  name: z.string().min(1),
  nameKana: z.string().optional(),
  gbizCorporateNumber: z.string().optional(),
  type: z.enum(["corporate", "individual"]).optional().default("corporate"),
});
export type CreateCompanyRequest = z.infer<typeof CreateCompanyRequestSchema>;
export interface CreateCompanyResponse {
  success: boolean;
  company: DDBCompany;
  isExisting?: boolean;
}

/**
 * PRODUCT_DEV-212: 担当者類似判定 API
 * POST /contacts/match-suggest
 * in api/lambda/contacts/match-suggest/post.ts
 *
 * 商談編集完了時に、編集後の contacts と顧客マスタの既存 contacts を LLM で照合し、
 * 同一人物候補を返却する。フロントは候補ありの contact について確認モーダルを出す。
 */
export const ContactsMatchSuggestRequestSchema = z
  .object({
    companyName: z.string().min(1),
    contacts: z.array(ContactSchema),
  })
  .strict();
export type ContactsMatchSuggestRequest = z.infer<
  typeof ContactsMatchSuggestRequestSchema
>;

export const ContactsMatchSuggestCandidateSchema = z.object({
  existingIndex: z.number().int().nonnegative(),
  existingContact: ContactSchema,
  similarity: z.enum(["high", "medium"]),
  reason: z.string().optional(),
});
export type ContactsMatchSuggestCandidate = z.infer<
  typeof ContactsMatchSuggestCandidateSchema
>;

export const ContactsMatchSuggestionSchema = z.object({
  editedIndex: z.number().int().nonnegative(),
  candidates: z.array(ContactsMatchSuggestCandidateSchema),
});
export type ContactsMatchSuggestion = z.infer<
  typeof ContactsMatchSuggestionSchema
>;

export interface ContactsMatchSuggestResponse {
  success: boolean;
  /** 顧客マスタにマッチしなかった場合などは empty で返す */
  suggestions: ContactsMatchSuggestion[];
  /** 比較対象として使った既存 contacts のスナップショット（フロントで index 参照に使う） */
  existingContacts: DDBContact[];
}

/**
 * PRODUCT_DEV-543: 個人(type:"individual")の類似候補サジェスト API
 * POST /individuals/match-suggest
 * in api/lambda/individuals/match-suggest/post.ts
 *
 * 個人商談（preset に customer_info を含む）の編集時、入力中の氏名/フリガナと
 * 顧客マスタの既存「個人」を決定的部分一致（苗字・フリガナ）で照合し、
 * 同一人物候補を返す。フロントは候補プルダウンを出し、選択で人物を上書きする。
 * 企業担当者(contacts/match-suggest)と異なり LLM は不要（決定的部分一致のみ）。
 */
export const IndividualsMatchSuggestRequestSchema = z
  .object({
    name: z.string().optional(), // 氏名（漢字/カナ、部分入力可）
    nameKana: z.string().optional(), // 読み仮名（カタカナ、部分入力可）
  })
  .strict()
  .refine(
    (d) => (d.name ?? "").trim() !== "" || (d.nameKana ?? "").trim() !== "",
    { message: "name または nameKana のいずれかが必要です" },
  );
export type IndividualsMatchSuggestRequest = z.infer<
  typeof IndividualsMatchSuggestRequestSchema
>;

export const IndividualMatchCandidateSchema = z.object({
  companyId: z.string(),
  name: z.string(),
  nameKana: z.string(),
  individualProfile: IndividualProfileSchema.optional(),
  similarity: z.enum(["high", "medium"]),
  /**
   * PRODUCT_DEV-1234: 全店顧客番号（企業マスタ systemIntegrationId）。同じ名前のお客さまを候補の中で
   * 見分けるため。テナントのフラグ companyExternalSystemId が ON で、番号が登録されているときだけ
   * API が返す（任意の項目なので従来の利用側はそのまま）。
   */
  systemIntegrationId: z.string().optional(),
});
export type IndividualMatchCandidate = z.infer<
  typeof IndividualMatchCandidateSchema
>;

export interface IndividualsMatchSuggestResponse {
  success: boolean;
  /** 部分一致した既存「個人」候補（high→medium 順、最大 N 件） */
  candidates: IndividualMatchCandidate[];
}

/**
 * 顧客マスタ更新API（管理画面向け）
 * PATCH /admin/companies/{companyId}
 * in api/lambda/admin/companies/[companyId]/patch.ts
 */
export const UpdateCompanyRequestSchema = z.object({
  name: z.string().min(1).optional(),
  nameKana: z.string().optional(),
  aliases: z.array(z.string()).optional(),
  active: z.boolean().optional(),
  order: z.number().optional(),
  type: z.enum(["corporate", "individual"]).optional(),
  location: z.string().optional(),
  // PRODUCT_DEV-875: 人が書き換える入口なのでメールアドレスの書式も見る
  contacts: z.array(EditableContactSchema).optional(),
  // PRODUCT_DEV-457: 外部システム連携用ID。空文字列で送るとクリア（undefined と区別）
  systemIntegrationId: z.string().optional(),
  // PRODUCT_DEV-583: 法人番号（マイ顧客の顧客編集画面から更新可能。updateCompany は既に対応済み）
  gbizCorporateNumber: z.string().optional(),
  // PRODUCT_DEV-543: 個人プロファイル（type:"individual" のときのみ反映。corporate では strip）
  // PRODUCT_DEV-543(改2): 反映日の指定は廃止。マスタ直接更新は常にサーバ当日付で経歴追加する。
  individualProfile: IndividualProfileSchema.optional(),
});
export type UpdateCompanyRequest = z.infer<typeof UpdateCompanyRequestSchema>;

/**
 * PRODUCT_DEV-557: 商談編集画面からの「全店顧客番号」(企業マスタ systemIntegrationId) 直接更新。
 * PATCH /companies/{companyId}（非admin・通常利用者）。systemIntegrationId のみ更新可。
 * 機能フラグ companyExternalSystemId 有効テナントのみ許可（api 側でゲート）。
 * PRODUCT_DEV-952: 法人・個人のどちらにも設定できる。顧客番号を法人／個人の区別なく
 * 採番する基幹システムを持つテナントがあるため、type で禁止しない。
 * 空文字=クリア（PRODUCT_DEV-457 と同セマンティクス）。
 * 注: useApiPatch が body に tenantId を注入するため .strict() にしない（未知キーは Zod が strip）。
 */
export const UpdateCompanySystemIntegrationIdRequestSchema = z.object({
  systemIntegrationId: z.string(),
});
export type UpdateCompanySystemIntegrationIdRequest = z.infer<
  typeof UpdateCompanySystemIntegrationIdRequestSchema
>;

/**
 * 企業ヨミガナAI推定API
 * POST /companies/suggest-kana
 * in api/lambda/companies/suggest-kana/post.ts
 *
 * gBizInfoにヨミガナがない場合、企業名からAIでカタカナ読みを推定する。
 */
export const SuggestCompanyKanaRequestSchema = z.object({
  name: z.string().min(1).max(200),
});
export type SuggestCompanyKanaRequest = z.infer<
  typeof SuggestCompanyKanaRequestSchema
>;
export interface SuggestCompanyKanaResponse {
  success: boolean;
  nameKana: string;
}

/**
 * OPENAIのリアルタイム対話APIのセッション取得API
 * GET /openai/realtime/session
 * in api/lambda/openai/realtime/session/get.ts
 */
export const OpenAIRealtimeSessionRequestBodySchema = z
  .object({
    model: z.string().optional(),
    voice: z.string().optional(),
    speed: z.string().optional(),
    instructions: z.string().optional(),
    tools: z.array(z.unknown()).optional(), // any[] 相当
  })
  .strict();

export type OpenAIRealtimeSessionRequestBody = AuthApiRequestBody &
  z.infer<typeof OpenAIRealtimeSessionRequestBodySchema>;

/**
 * OpenAI Realtime API のセッション作成 (ephemeral token) の生レスポンス。
 * GA (`POST /v1/realtime/client_secrets`) は value/expires_at をトップレベルに返し、
 * session.model にモデル名が入る。後方互換のため旧 Beta 形式
 * (`client_secret:{value,expires_at}`, トップレベル `model`) も optional で残す。
 */
export interface OpenAIRealtimeSessionRawResponse {
  // GA shape
  value?: string;
  expires_at?: number;
  session?: {
    model?: string;
    [key: string]: unknown;
  };
  // Beta (deprecated) shape
  client_secret?: {
    value: string;
    expires_at: number;
  };
  model?: string;
}

export interface OpenAIRealtimeSessionResponse {
  success: boolean;
  clientSecret: {
    value: string;
    expiresAt: number;
  };
  model: string;
}

/**
 * Whisper音声テキスト化API
 * POST /openai/whisper/transcribe
 * in api/lambda/openai/whisper/transcribe/post.ts
 */
export const WhisperTranscribeResponseBodySchema = z.object({
  text: z.string(),
  duration: z.number(),
});

export type WhisperTranscribeResponseBody = z.infer<
  typeof WhisperTranscribeResponseBodySchema
>;

// ===== Notification Schemas =====

/** アプリ内通知スキーマ */
export const NotificationSchema = z.object({
  notificationId: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  createdAt: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/** 通知一覧レスポンス */
export const NotificationListResponseSchema = z.object({
  notifications: z.array(NotificationSchema),
  nextToken: z.string().optional(),
});
export type NotificationListResponse = z.infer<
  typeof NotificationListResponseSchema
>;

/** 未読件数レスポンス */
export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number(),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

/** EventBridgeイベント detail スキーマ */
export const NotificationEventDetailSchema = z.object({
  tenantId: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  actorUserId: z.string().optional(),
  metadata: z.record(z.string()).optional(),
});
export type NotificationEventDetail = z.infer<
  typeof NotificationEventDetailSchema
>;

export interface WhisperTranscribeResponse {
  success: boolean;
  result: WhisperTranscribeResponseBody;
}

// ==========================================
// 商談グループ (Meeting Groups)
// ==========================================

// DynamoDB商談グループ型
export interface DDBMeetingGroup {
  PK: string; // TENANT#{tenantId}
  SK: string; // GROUP#{groupId}
  GSI1PK: string; // TENANT#{tenantId}
  GSI1SK: string; // GROUP#{createdAt}
  groupId: string;
  tenantId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  status?: string; // "active" | "trashed"（未設定は"active"扱い）
  // PRODUCT_DEV-1214: 案件フォルダの相手の顧客（顧客マスタの companyId）。任意。
  // 未設定は顧客を持たない自由なフォルダ（今までのグループと同じ）。
  companyId?: string;
  summary?: string; // LLM生成要約テキスト（人間用）
  summaryForAI?: string; // LLM生成要約テキスト（AI記録用構造化）
  summaryUpdatedAt?: string; // 要約の最終更新日時（ISO8601）
  summaryStatus?: "generating" | "completed" | "failed"; // 要約生成ステータス
  summaryErrorMessage?: string; // 要約生成失敗時のエラーメッセージ
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// 商談グループ作成リクエスト
export const CreateMeetingGroupSchema = z.object({
  name: z.string().min(1, "グループ名は必須です").max(100, "グループ名は100文字以内です"),
  // PRODUCT_DEV-1214: 相手の顧客。送らなければ顧客なしのフォルダ（今までどおり）。
  companyId: z.string().min(1).max(100).optional(),
});

export type CreateMeetingGroupRequest = z.infer<typeof CreateMeetingGroupSchema>;

// 商談グループ更新リクエスト
export const UpdateMeetingGroupSchema = z
  .object({
    name: z.string().min(1, "グループ名は必須です").max(100, "グループ名は100文字以内です").optional(),
    // PRODUCT_DEV-1214: 相手の顧客を後から付ける・付け替える。null で外す。送らなければ変えない。
    companyId: z.string().min(1).max(100).nullable().optional(),
  })
  .refine((v) => v.name !== undefined || v.companyId !== undefined, {
    message: "name か companyId のどちらかが必要です",
  });

export type UpdateMeetingGroupRequest = z.infer<typeof UpdateMeetingGroupSchema>;

// 商談グループ一覧レスポンス
export interface MeetingGroupsResponse {
  success: boolean;
  groups: Array<{
    groupId: string;
    name: string;
    meetingCount: number;
    latestMeetingDate: string | null;
    createdAt: string;
    updatedAt: string;
    /** PRODUCT_DEV-1214: 相手の顧客。未設定は顧客なしのフォルダ */
    companyId?: string;
    summary?: string;
    summaryUpdatedAt?: string;
  }>;
}

// 商談グループ単体レスポンス
export interface MeetingGroupResponse {
  success: boolean;
  group: {
    groupId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    /** PRODUCT_DEV-1214: 相手の顧客。未設定は顧客なしのフォルダ */
    companyId?: string;
    summary?: string;
    summaryUpdatedAt?: string;
    summaryStatus?: "generating" | "completed" | "failed";
    summaryErrorMessage?: string;
  };
}

// ==========================================
// 商談バッチ移動 (Meeting Batch Move)
// ==========================================

// バッチ移動リクエスト
export const BatchMoveMeetingsRequestSchema = z.object({
  meetingIds: z.array(z.string()).min(1).max(100),
  groupId: z.string(),
  /**
   * PRODUCT_DEV-1265: 出し入れの種類。未指定は move（以前の動き）。
   * add = そのフォルダにも入れる / remove = そのフォルダからだけ外す / move = fromGroupId から移す
   */
  mode: z.enum(["move", "add", "remove"]).optional(),
  /** PRODUCT_DEV-1265: move の移動元。無いときは入っている全部から移す（以前の動き） */
  fromGroupId: z.string().optional(),
});

export type BatchMoveMeetingsRequest = z.infer<
  typeof BatchMoveMeetingsRequestSchema
>;

// バッチ移動レスポンス
export interface BatchMoveMeetingsResponse {
  success: boolean;
  updatedCount: number;
}

// ==========================================
// 商談バッチ更新 (Batch Patch Meetings)
// ==========================================

// Batch Patch Meetings
export const BatchPatchMeetingsRequestSchema = z.object({
  meetingIds: z.array(z.string()).min(1).max(100),
  status: z.enum(["active", "disabled", "trashed"]).optional(),
  groupId: z.string().nullable().optional(),
});

export type BatchPatchMeetingsRequest = z.infer<
  typeof BatchPatchMeetingsRequestSchema
>;

export interface BatchPatchMeetingsResponse {
  updatedCount: number;
}

// ==========================================
// 商談バッチ削除 (Meeting Batch Delete)
// ==========================================

/**
 * バッチ削除リクエスト
 */
export const BatchDeleteMeetingsRequestBodySchema = z.object({
  meetingIds: z.array(z.string()).min(1, "At least one meeting ID is required"),
  permanent: z.boolean().optional().default(false),
});

export type BatchDeleteMeetingsRequestBody = z.infer<
  typeof BatchDeleteMeetingsRequestBodySchema
>;

/**
 * バッチ削除レスポンス
 */
export const BatchDeleteMeetingsResponseSchema = z.object({
  success: z.boolean(),
  deletedCount: z.number(),
});

export type BatchDeleteMeetingsResponse = z.infer<
  typeof BatchDeleteMeetingsResponseSchema
>;

// ==========================================
// ゴミ箱 (Trash)
// ==========================================

// ゴミ箱一覧レスポンス
export interface TrashListResponse {
  success: boolean;
  meetings: DDBMeeting[];
  groups: DDBMeetingGroup[];
}

// ゴミ箱復元リクエスト
export const TrashRestoreRequestSchema = z.object({
  meetingIds: z.array(z.string()).max(100).optional(),
  groupIds: z.array(z.string()).max(100).optional(),
});

export type TrashRestoreRequest = z.infer<typeof TrashRestoreRequestSchema>;

// ==========================================
// お気に入り (Favorites)
// ==========================================

export const DDBFavoriteSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // MEETING#<meetingId>
  userId: z.string(),
  meetingId: z.string(),
  createdAt: z.string(), // ISO string
  companyName: z.string().optional(),
  title: z.string().optional(),
  reviewed: z.boolean().optional(),
});

export type DDBFavorite = z.infer<typeof DDBFavoriteSchema>;

export const DDBGroupFavoriteSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // GROUP#<groupId>
  userId: z.string(),
  groupId: z.string(),
  createdAt: z.string(), // ISO string
});

export type DDBGroupFavorite = z.infer<typeof DDBGroupFavoriteSchema>;

/**
 * 議事録の保存（PRODUCT_DEV-996）。商談の保存と同じ表・同じパーティションに、SK の接頭辞を
 * `MINUTES#` にして置く（既存の読み手は `MEETING#` / `GROUP#` で絞っているので混ざらない）。
 */
export const DDBMinutesFavoriteSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // MINUTES#<minutesId>
  userId: z.string(),
  minutesId: z.string(),
  createdAt: z.string(), // ISO string
  companyName: z.string().optional(),
  title: z.string().optional(),
});

export type DDBMinutesFavorite = z.infer<typeof DDBMinutesFavoriteSchema>;

// ==========================================
// 閲覧履歴 (View History)
// ==========================================

export const DDBViewHistorySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // MEETING#<meetingId> / MINUTES#<minutesId>（PRODUCT_DEV-996）
  userId: z.string(),
  targetId: z.string(),
  /** PRODUCT_DEV-996: 議事録の閲覧履歴も同じ表に `MINUTES#` で置く。 */
  targetType: z.enum(["meeting", "minutes"]),
  viewedAt: z.string(), // ISO 8601
  companyName: z.string().optional(),
  title: z.string().optional(),
});

export type DDBViewHistory = z.infer<typeof DDBViewHistorySchema>;

// ==========================================
// 顧客レポート (Company Reports)
// ==========================================

/**
 * 8カテゴリ定数
 * 口コミ（エントリー）の分類カテゴリ
 */
export const REPORT_CATEGORIES = [
  { id: "customer_profile", title: "顧客プロファイル", subtitle: "ナラティブトピック", icon: "UserCircle", color: "blue" },
  { id: "pain_points", title: "課題・現状の不満", subtitle: "生声ベースの不満(Pain)", icon: "AlertTriangle", color: "red" },
  { id: "hidden_needs", title: "ニーズ・期待効果", subtitle: "裏テーマ(隠れた動機)", icon: "Eye", color: "purple" },
  { id: "budget_resources", title: "予算・リソース", subtitle: "資金源の実写感", icon: "Wallet", color: "green" },
  { id: "competition", title: "競合・比較状況", subtitle: "現状維持(不作為)の壁", icon: "Shield", color: "orange" },
  { id: "relationships", title: "人物関連・人脈", subtitle: "情緒的属性と力学マップ", icon: "Users", color: "indigo" },
  { id: "decision_process", title: "意思決定・導入基盤", subtitle: "物理的・心理的ハードル", icon: "GitBranch", color: "amber" },
  { id: "risks", title: "取引上の制約・リスク", subtitle: "負け筋のアラート", icon: "AlertOctagon", color: "rose" },
] as const;

export type ReportCategoryId = (typeof REPORT_CATEGORIES)[number]["id"];

export const ReportCategoryIdSchema = z.enum([
  "customer_profile",
  "pain_points",
  "hidden_needs",
  "budget_resources",
  "competition",
  "relationships",
  "decision_process",
  "risks",
]);

/**
 * エントリー（口コミ1件）
 * SK: REPORT_ENTRY#<companyId>#<entryId>
 */
export const DDBReportEntrySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT_ENTRY#<companyId>#<entryId>
  tenantId: z.string(),
  companyId: z.string(),
  entryId: z.string(), // ULID
  categoryId: z.string(), // ReportCategoryId or custom id
  authorId: z.string(), // userId
  authorName: z.string(), // 非正規化表示名
  content: z.string(),
  contentType: z.enum(["fact", "inference"]), // 事実 or 推論
  isPrivate: z.boolean().default(false), // 非公開フラグ
  source: z.enum(["manual", "voice", "ai_extracted"]),
  meetingId: z.string().optional(), // AI抽出の場合の元商談
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DDBReportEntry = z.infer<typeof DDBReportEntrySchema>;

/** エントリー作成リクエスト */
export const CreateReportEntrySchema = z.object({
  categoryId: z.string().min(1),
  content: z.string().min(1),
  contentType: z.enum(["fact", "inference"]),
  isPrivate: z.boolean().default(false),
  source: z.enum(["manual", "voice", "ai_extracted"]).default("manual"),
  meetingId: z.string().optional(),
});
export type CreateReportEntry = z.infer<typeof CreateReportEntrySchema>;

/** エントリー更新リクエスト */
export const UpdateReportEntrySchema = z.object({
  categoryId: z.string().min(1).optional(),
  content: z.string().min(1).optional(),
  contentType: z.enum(["fact", "inference"]).optional(),
  isPrivate: z.boolean().optional(),
});
export type UpdateReportEntry = z.infer<typeof UpdateReportEntrySchema>;

/**
 * カスタムカテゴリ設定
 * SK: REPORT_CONFIG#default or REPORT_CONFIG#<companyId>
 */
export const ReportCustomCategorySchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  icon: z.string(),
  color: z.string(),
});

export const DDBReportConfigSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT_CONFIG#default or REPORT_CONFIG#<companyId>
  tenantId: z.string(),
  scope: z.string(), // "default" or companyId
  customCategories: z.array(ReportCustomCategorySchema),
  categoryOrder: z.array(z.string()).optional(), // 全カテゴリの表示順
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

export type DDBReportConfig = z.infer<typeof DDBReportConfigSchema>;

/** カテゴリ設定更新リクエスト */
export const UpdateReportConfigSchema = z.object({
  customCategories: z.array(ReportCustomCategorySchema),
  categoryOrder: z.array(z.string()).optional(),
});
export type UpdateReportConfig = z.infer<typeof UpdateReportConfigSchema>;

/**
 * 集約ビュー（AIサマリー）
 * SK: REPORT#<companyId> (既存、構造変更)
 */
const ReportSufficiencyAxisSchema = z.object({
  // PRODUCT_DEV-646: 軸の突合キー。従来は name(日本語表示名)で照合していたため、
  // REPORT_CATEGORIES の title を変えると保存済みレポートの軸が全て 0 になった。
  // 既存レコードには存在しないので optional。読み側は categoryId 優先・name フォールバック。
  categoryId: z.string().optional(),
  name: z.string(),
  score: z.number().min(0).max(100),
});

const ReportSalesActivitySchema = z.object({
  date: z.string(),
  title: z.string(),
  actionNeeded: z.boolean(),
  meetingId: z.string().optional(),
  action: z.string().optional(),
  status: z.string().optional(),
});

const ReportActionHistorySchema = z.object({
  month: z.string(),
  content: z.array(z.string()),
  person: z.string(),
});

export const DDBCompanyReportSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT#<companyId>
  tenantId: z.string(),
  companyId: z.string(),
  status: z.enum(["generating", "completed", "failed"]),
  summary: z
    .object({
      text: z.string(),
    })
    .optional(),
  salesInfo: z
    .object({
      text: z.string(),
      activities: z.array(ReportSalesActivitySchema),
    })
    .optional(),
  sufficiency: z
    .object({
      overall: z.number(),
      axes: z.array(ReportSufficiencyAxisSchema),
    })
    .optional(),
  actionAdvice: z
    .object({
      aiAdvice: z.string(),
      history: z.array(ReportActionHistorySchema),
    })
    .optional(),
  generatedAt: z.string().optional(),
  sourceMetadata: z
    .object({
      meetingCount: z.number(),
      latestMeetingDate: z.string().nullable(),
      entryCount: z.number().optional(),
    })
    .optional(),
  categoryInsights: z
    .array(
      z.object({
        categoryId: z.string(),
        insight: z.string(),
        references: z.array(
          z.object({
            type: z.enum(["meeting", "entry"]),
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    )
    .optional(),
  errorMessage: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DDBCompanyReport = z.infer<typeof DDBCompanyReportSchema>;
export type CategoryInsight = NonNullable<DDBCompanyReport["categoryInsights"]>[number];

export interface CompanyReportResponse {
  success: boolean;
  status: "none" | "generating" | "completed" | "failed";
  report?: DDBCompanyReport;
  entries?: DDBReportEntry[];
  config?: DDBReportConfig;
  errorMessage?: string;
}

// ==========================================
// 辞書 (Dictionaries)
// ==========================================

export const DictionaryCategorySchema = z.enum([
  "person_name",
  "product_service",
  "technical_term",
  "department_title",
  "other",
]);
export type DictionaryCategory = z.infer<typeof DictionaryCategorySchema>;

export const DICTIONARY_CATEGORY_LABELS: Record<DictionaryCategory, string> = {
  person_name: "人名",
  product_service: "商品・サービス",
  technical_term: "専門用語",
  department_title: "部署・役職",
  other: "その他",
};

export const DDBDictionarySchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z.string().regex(/^DICT#[^#]+$/, 'SK must be "DICT#{dictionaryId}"'),
  tenantId: z.string(),
  dictionaryId: z.string(),
  word: z.string().min(1).max(100),
  reading: z.string().min(1).max(200),
  readings: z.array(z.string()).optional(),
  formalName: z.string().max(30).optional(),
  meaning: z.string().max(80).optional(),
  category: DictionaryCategorySchema,
  order: z.number(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type DDBDictionary = z.infer<typeof DDBDictionarySchema>;

export const CreateDictionarySchema = z.object({
  word: z.string().min(1).max(100),
  readings: z.array(z.string().min(1).max(200)).min(1).max(10),
  formalName: z.string().max(30).optional(),
  meaning: z.string().max(80).optional(),
  category: DictionaryCategorySchema,
});
export type CreateDictionaryRequest = z.infer<typeof CreateDictionarySchema>;

export const PatchDictionarySchema = z.object({
  word: z.string().min(1).max(100).optional(),
  readings: z.array(z.string().min(1).max(200)).min(1).max(10).optional(),
  formalName: z.string().max(30).nullable().optional(),
  meaning: z.string().max(80).nullable().optional(),
  category: DictionaryCategorySchema.optional(),
  active: z.boolean().optional(),
  order: z.number().optional(),
  /**
   * PRODUCT_DEV-679: 楽観ロック用。編集開始時点の `updatedAt` を送ると、
   * その間に他者が更新していた場合 409 で弾く（送らなければ従来どおり last-write-wins）。
   * 永続化はしない（更新式には含めない）。
   */
  expectedUpdatedAt: z.string().optional(),
});
export type PatchDictionaryRequest = z.infer<typeof PatchDictionarySchema>;

export interface DictionariesResponse {
  success: boolean;
  dictionaries: DDBDictionary[];
}

export interface DictionaryResponse {
  success: boolean;
  dictionary: DDBDictionary;
}

// Realtime API Config
export const DDBRealtimeConfigSchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z
    .string()
    .regex(
      /^REALTIME_CONFIG#[^#]+$/,
      'SK must be "REALTIME_CONFIG#{configId}"',
    ),
  tenantId: z.string(),
  configId: z.string(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  order: z.number(),
  active: z.boolean(),
  // Session creation params
  model: z.string(),
  voice: z.string(),
  speed: z.number().min(0.5).max(2.0),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  // Session update params
  turnDetectionType: z.string(),
  turnDetectionThreshold: z.number().min(0).max(1),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean(),
  turnDetectionCreateResponse: z.boolean(),
  // temperature は GA で廃止（送信されない）。旧レコード read 互換のため optional で残置
  temperature: z.number().min(0).max(2).optional(),
  // モデル別の上限は REALTIME_MODEL_MAX_OUTPUT_TOKENS が正本。UI と送信側の両方でクランプする。
  // ここの max は旧レコード（32768 で保存されたもの）を read できるよう広めに残している
  maxResponseOutputTokens: z.number().min(50).max(32768),
  modalities: z.array(z.string()),
  // reasoning.effort（gpt-realtime-2 系など reasoning 対応モデルのみ送信）。値: minimal/low/medium/high/xhigh
  reasoningEffort: z.string().optional(),
  // Audit
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});
export type DDBRealtimeConfig = z.infer<typeof DDBRealtimeConfigSchema>;

export const CreateRealtimeConfigSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  model: z.string(),
  voice: z.string(),
  speed: z.number().min(0.5).max(2.0),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  turnDetectionType: z.string(),
  turnDetectionThreshold: z.number().min(0).max(1),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean(),
  turnDetectionCreateResponse: z.boolean(),
  temperature: z.number().min(0).max(2).optional(),
  maxResponseOutputTokens: z.number().min(50).max(32768),
  modalities: z.array(z.string()),
  reasoningEffort: z.string().optional(),
});
export type CreateRealtimeConfigRequest = z.infer<
  typeof CreateRealtimeConfigSchema
>;

export const PatchRealtimeConfigSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  order: z.number().optional(),
  active: z.boolean().optional(),
  model: z.string().optional(),
  voice: z.string().optional(),
  speed: z.number().min(0.5).max(2.0).optional(),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  turnDetectionType: z.string().optional(),
  turnDetectionThreshold: z.number().min(0).max(1).optional(),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000).optional(),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500).optional(),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean().optional(),
  turnDetectionCreateResponse: z.boolean().optional(),
  temperature: z.number().min(0).max(2).optional(),
  maxResponseOutputTokens: z.number().min(50).max(32768).optional(),
  modalities: z.array(z.string()).optional(),
  reasoningEffort: z.string().optional(),
});
export type PatchRealtimeConfigRequest = z.infer<
  typeof PatchRealtimeConfigSchema
>;

/**
 * reasoning.effort を送信可能なリアルタイムモデル（OpenAI Realtime GA）。
 * 非対応モデル（gpt-realtime-1.5 等）に reasoning を送ると 400 で弾かれるため、
 * editor の reasoning UI 表示・session.update への付与はこの一覧でゲートする。
 */
export const REASONING_CAPABLE_REALTIME_MODELS: readonly string[] = [
  "gpt-realtime-2",
  "gpt-realtime-2.1",
  "gpt-realtime-2.1-mini",
];

/**
 * リアルタイムモデルごとの出力トークン上限（OpenAI 公式のモデルページ実測値。2026-09-10 確認）。
 *
 * - gpt-realtime-1.5      : context 32,000 / max output 4,096
 * - gpt-realtime-2        : context 128,000 / max output 32,000
 * - gpt-realtime-2.1      : 同上
 * - gpt-realtime-2.1-mini : 同上
 *   https://developers.openai.com/api/docs/models/gpt-realtime-2.1
 *
 * ⚠ **reasoning 対応可否（REASONING_CAPABLE_REALTIME_MODELS）とは別の性質なので、
 * 判定を相乗りさせない。** 以前は「reasoning 対応 = 上限 32768」で切り替えていたが、
 * (a) 公式の上限は 32,768 ではなく 32,000、(b) 将来 reasoning 対応でも上限の小さい
 * モデルが出れば破綻する、の 2 点で成立していなかった。
 *
 * 未知のモデル名は最も安全な 4,096 へフォールバックする（上限超過を送ると
 * session.update 全体が 400 で拒否され、instructions/tools ごと無効化されるため）。
 */
export const REALTIME_MODEL_MAX_OUTPUT_TOKENS: Readonly<
  Record<string, number>
> = {
  "gpt-realtime-1.5": 4096,
  "gpt-realtime-2": 32000,
  "gpt-realtime-2.1": 32000,
  "gpt-realtime-2.1-mini": 32000,
};

/** 上限が判明していないモデルに使うフォールバック値。 */
export const REALTIME_MODEL_MAX_OUTPUT_TOKENS_FALLBACK = 4096;

/** モデル名から出力トークン上限を引く（未知のモデルはフォールバック）。 */
export function realtimeModelMaxOutputTokens(model: string | undefined): number {
  if (!model) return REALTIME_MODEL_MAX_OUTPUT_TOKENS_FALLBACK;
  return (
    REALTIME_MODEL_MAX_OUTPUT_TOKENS[model] ??
    REALTIME_MODEL_MAX_OUTPUT_TOKENS_FALLBACK
  );
}

/**
 * 対話モードの発話の文字起こしに使う後継モデル（PRODUCT_DEV-1085）。
 * whisper-1 は 2027-02-26 に OpenAI の提供が終わる。gpt-live-transcribe は会話のセッションに使えない。
 */
export const REALTIME_TRANSCRIPTION_NEXT_MODEL = "gpt-transcribe";

/**
 * 対話モードの発話の文字起こしモデルを決める（PRODUCT_DEV-1085）。
 * フラグ `realtimeTranscribeGptTranscribe` が ON なら、Realtime 設定に保存された値を見ずに後継モデルにする
 * （保存値は管理画面が決め打ちで書く whisper-1 で、選ばれた値ではないため）。OFF は保存値のまま（未設定は undefined）。
 */
export function resolveRealtimeTranscriptionModel(
  savedModel: string | undefined,
  useNext: boolean,
): string | undefined {
  if (useNext) return REALTIME_TRANSCRIPTION_NEXT_MODEL;
  return savedModel || undefined;
}

export interface RealtimeConfigsResponse {
  success: boolean;
  realtimeConfigs: DDBRealtimeConfig[];
}

export interface RealtimeConfigResponse {
  success: boolean;
  realtimeConfig: DDBRealtimeConfig;
}

// ─── Notifications ────────────────────────────────────

export const NotificationTypeEnum = z.enum([
  "meeting_edited",
  "meeting_shared",
  "meeting_deleted",
  "meeting_created",
  "meeting_group_created",
  "meeting_group_updated",
  "meeting_group_deleted",
  "system_announcement",
  "system_urgent",
  // PRODUCT_DEV-459 Phase B: メモ完了時の自己通知 (記録者本人宛)。
  // emit 側は PRODUCT_DEV-424 (メモ機能本体) で実装予定。
  // PRODUCT_DEV-459: 正常完了は原則通知せず、処理が閾値以上かかった場合のみ memo_completed を発火。
  "memo_completed",
  // PRODUCT_DEV-459: 音声メモの文字起こし/保存失敗時の自己通知 (記録者本人宛)。
  "memo_failed",
  // PRODUCT_DEV-583: マイ顧客の主担当を引き継がれた本人宛の通知。
  "customer_handover",
  // PRODUCT_DEV-635: 問い合わせに運営の回答が届いたときの投稿者本人宛の通知（常時通知・設定対象外）。
  // emit 側は admin API（回答承認時に UnizoNotificationEvents へ直接 Put。worker 経路は通らない）。
  "inquiry_answered",
  // PRODUCT_DEV-808: 日報・週報・月報。下書きができた（未提出の本人）/ 上司のコメント（本人）/
  // 部のまとめができた（まとめの宛先）。emit 側は日報の定期実行とコメント API。
  "report_ready",
  "report_comment",
  "team_report_ready",
  // PRODUCT_DEV-808: 日程調整。回答が出そろった / 相手から「この日程は難しい」（いずれも担当者）。
  // emit 側は日程調整の定期実行（ready-notifier）。
  "schedule_ready_to_confirm",
  "schedule_declined",
  // PRODUCT_DEV-1064: 日程調整の回答の締切を過ぎ、まだそろっていない（「要確認」）
  "schedule_deadline_passed",
]);
export type NotificationType = z.infer<typeof NotificationTypeEnum>;

export const DDBNotificationSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  notificationId: z.string(),
  userId: z.string(),
  type: NotificationTypeEnum,
  title: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  isUrgent: z.boolean().optional(),
  metadata: z.record(z.string()).optional(),
  createdAt: z.string(),
  ttl: z.number().optional(),
});
export type DDBNotification = z.infer<typeof DDBNotificationSchema>;

export interface NotificationsResponse {
  success: boolean;
  notifications: DDBNotification[];
  unreadCount: number;
  /**
   * PRODUCT_DEV-652: 続きページのカーソル（省略可 = 続きなし）。
   * 通知一覧が最新 50 件で打ち切られ、商談作成通知に自分宛の通知が押し出されて
   * 辿れなくなる問題への対応。optional なので、返さない API / 読まない web とも従来どおり動く。
   */
  nextToken?: string;
}

export interface NotificationUnreadCountResponse {
  success: boolean;
  unreadCount: number;
}

export interface NotificationReadAllResponse {
  success: boolean;
}

// ─── Notification Settings (PRODUCT_DEV-293) ──────────

/**
 * 通知設定カテゴリ（アプリ内）
 * Figmaの通知トグルに対応する6カテゴリ（PRODUCT_DEV-293）と、日報・日程調整の5カテゴリ（PRODUCT_DEV-808）。
 * 設定対象外: meeting_group_deleted, system_announcement, system_urgent, memo_*, customer_handover,
 * inquiry_answered（常時通知）
 *
 * ⚠ **API が返す「解決済み」の形は全キー必須にしてある**（既定値・マスタートグル・アイコンの
 * 網羅漏れをコンパイルで捕まえるため）。保存形（`DDBNotificationSettingsSchema`）は partial で、
 * 既定値と読み替えは `notificationSettings.ts` の `resolve*` だけが行う。
 */
export const NotificationCategoriesSchema = z.object({
  meeting_edited: z.boolean(),
  meeting_shared: z.boolean(),
  meeting_deleted: z.boolean(),
  meeting_created: z.boolean(),
  meeting_group_created: z.boolean(),
  meeting_group_updated: z.boolean(),
  // PRODUCT_DEV-808: 日報・週報・月報
  report_ready: z.boolean(),
  report_comment: z.boolean(),
  team_report_ready: z.boolean(),
  // PRODUCT_DEV-808: 日程調整
  schedule_ready_to_confirm: z.boolean(),
  schedule_declined: z.boolean(),
  // PRODUCT_DEV-1064: 回答の締切を過ぎた（要確認）
  schedule_deadline_passed: z.boolean(),
});
export type NotificationCategories = z.infer<typeof NotificationCategoriesSchema>;

/** 商談の6カテゴリ（PRODUCT_DEV-808: 「すべて OFF にしていた人」の判定と画面の群に使う）。 */
export const MEETING_NOTIFICATION_CATEGORY_KEYS = [
  "meeting_edited",
  "meeting_shared",
  "meeting_deleted",
  "meeting_created",
  "meeting_group_created",
  "meeting_group_updated",
] as const satisfies readonly (keyof NotificationCategories)[];

/** 日報・日程調整のカテゴリ（PRODUCT_DEV-808。PRODUCT_DEV-1064 で「回答の締切を過ぎた」を足した）。 */
export const REPORT_SCHEDULE_NOTIFICATION_CATEGORY_KEYS = [
  "report_ready",
  "report_comment",
  "team_report_ready",
  "schedule_ready_to_confirm",
  "schedule_declined",
  "schedule_deadline_passed",
] as const satisfies readonly (keyof NotificationCategories)[];

/**
 * 設定対象のNotificationType一覧（Worker内の設定チェック用）
 *
 * ⚠ ここに無い type は設定を無視して常時通知になる（PRODUCT_DEV-808 の新5種も必ず足す）。
 */
export const SETTINGS_CONTROLLED_TYPES = [
  ...MEETING_NOTIFICATION_CATEGORY_KEYS,
  ...REPORT_SCHEDULE_NOTIFICATION_CATEGORY_KEYS,
] as const satisfies readonly NotificationType[];

/**
 * カテゴリ別 Web Push 通知設定 (PRODUCT_DEV-461)
 * アプリ内通知(categories)と同じカテゴリ。各カテゴリで push を個別ON/OFF。
 * デフォルトは全 false（オプトイン）。push は「アプリ内ONが前提」の部分集合。
 */
export type NotificationPushCategories = NotificationCategories;

/**
 * 種別ごとのメール設定（PRODUCT_DEV-808）
 *
 * メールが届く種類だけを持つ（商談記録と日報コメントはメールが無い）。既定は全 true。
 * 保存されていない `team_report_ready` は、旧個人設定 `teamReportMailOptOut` で読み替える
 * （`resolveMailCategories`）。一度保存した値が以後の正本になる。
 */
export const NotificationMailCategoriesSchema = z.object({
  report_ready: z.boolean(),
  team_report_ready: z.boolean(),
  schedule_ready_to_confirm: z.boolean(),
  schedule_declined: z.boolean(),
  // PRODUCT_DEV-1064: 回答の締切を過ぎた（要確認）
  schedule_deadline_passed: z.boolean(),
});
export type NotificationMailCategories = z.infer<typeof NotificationMailCategoriesSchema>;

/**
 * PRODUCT_DEV-1320: 1部署ぶんの通知設定（保存形・PATCH とも）。形は共通の設定と同じで、
 * メールは**保存したキーだけ**を持つ（共通と同じ理由）。
 */
export const NotificationDepartmentSettingsSchema = z.object({
  categories: NotificationCategoriesSchema.partial().optional(),
  pushCategories: NotificationCategoriesSchema.partial().optional(),
  mailCategories: NotificationMailCategoriesSchema.partial().optional(),
});
export type NotificationDepartmentSettings = z.infer<
  typeof NotificationDepartmentSettingsSchema
>;

/** PRODUCT_DEV-1320: 1人が持てる部署ごとの設定の数の上限（兼務の数より十分大きく、レコードを膨らませない）。 */
export const NOTIFICATION_DEPARTMENT_SETTINGS_MAX = 30;

const NotificationDepartmentSettingsMapSchema = z
  .record(z.string().min(1).max(64), NotificationDepartmentSettingsSchema)
  .refine((m) => Object.keys(m).length <= NOTIFICATION_DEPARTMENT_SETTINGS_MAX, {
    message: `departmentSettings must have at most ${NOTIFICATION_DEPARTMENT_SETTINGS_MAX} entries`,
  });

/**
 * DDB通知設定レコード
 * PK: TENANT#{tenantId}#USER#{userId}
 * SK: "SETTINGS"
 *
 * 保存形は partial（PRODUCT_DEV-808）。カテゴリを足すたびに既存レコードには無いキーが増えるので、
 * 「全キーそろっている」と宣言しない。
 */
export const DDBNotificationSettingsSchema = z.object({
  PK: z.string(),
  SK: z.literal("SETTINGS"),
  userId: z.string(),
  categories: NotificationCategoriesSchema.partial(),
  // PRODUCT_DEV-461: カテゴリ別 push 設定（既存レコードには無いため optional、未定義は全false扱い）
  pushCategories: NotificationCategoriesSchema.partial().optional(),
  // PRODUCT_DEV-808: メール設定。**保存したキーだけ**を持つ。既定値を焼き込むと、
  // 旧 `teamReportMailOptOut` の読み替えが一度の保存で消える
  mailCategories: NotificationMailCategoriesSchema.partial().optional(),
  // PRODUCT_DEV-1320: 兼務の人が「部署ごとに分けて設定する」を ON にしているか。
  // OFF に戻しても departmentSettings は消さない（もう一度 ON にしたとき元に戻す）
  splitByDepartment: z.boolean().optional(),
  // PRODUCT_DEV-1320: 部署ごとの設定（キーは departmentId）。無い部署は共通の設定で読む
  departmentSettings: NotificationDepartmentSettingsMapSchema.optional(),
  updatedAt: z.string(),
});
export type DDBNotificationSettings = z.infer<typeof DDBNotificationSettingsSchema>;

/**
 * PATCH /notification/settings リクエストボディ
 * categories / pushCategories / mailCategories とも任意・部分更新可。
 */
export const PatchNotificationSettingsSchema = z.object({
  categories: NotificationCategoriesSchema.partial().optional(),
  pushCategories: NotificationCategoriesSchema.partial().optional(),
  // PRODUCT_DEV-808
  mailCategories: NotificationMailCategoriesSchema.partial().optional(),
  // PRODUCT_DEV-1320: 部署ごとに分けるか / 部署ごとの設定（送った部署だけ上書き。送らない部署はそのまま）
  splitByDepartment: z.boolean().optional(),
  departmentSettings: NotificationDepartmentSettingsMapSchema.optional(),
});
export type PatchNotificationSettingsRequest = z.infer<typeof PatchNotificationSettingsSchema>;

export interface NotificationSettingsResponse {
  success: boolean;
  categories: NotificationCategories;
  pushCategories: NotificationPushCategories;
  /**
   * PRODUCT_DEV-808: **保存済みのキーだけ**。表示と送信の判定は `resolveMailCategories` で解決する
   * （未保存の `team_report_ready` を旧個人設定で読み替えるため、解決済みの値を返さない）。
   * 古い API は返さないので optional。
   */
  mailCategories?: Partial<NotificationMailCategories>;
  /** PRODUCT_DEV-1320: 部署ごとに分けて設定しているか。古い API は返さないので optional（無ければ false）。 */
  splitByDepartment?: boolean;
  /**
   * PRODUCT_DEV-1320: 部署ごとの設定（キーは departmentId）。アプリ内・push は解決済み、
   * メールは共通と同じく保存済みのキーだけ。**保存していない部署は入らない**（共通の設定で読む）。
   */
  departmentSettings?: Record<
    string,
    {
      categories: NotificationCategories;
      pushCategories: NotificationPushCategories;
      mailCategories: Partial<NotificationMailCategories>;
    }
  >;
}

// ─── Dialogue Character (PRODUCT_DEV-638 対話キャラクター 個人設定) ───────────
// テーブル: UnizoNotificationEvents を流用（唯一の per-user PK を持つテーブル）
//   PK: TENANT#{tenantId}#USER#{userId} / SK: "SETTINGS#DIALOGUE_CHARACTER"
// キャラ＝見た目+声のセット。voice の解決は web 側レジストリ
// (apps/unizo/web/src/lib/character/dialogueCharacters.ts) が行い、
// セッション発行時に ?voice= で上書きする。未設定ユーザは
// 見た目=female・声=リアルタイムAPI設定のフォールバック。

export const DialogueCharacterIdSchema = z.enum([
  "female",
  "male",
  "elephant",
  "robot",
]);
export type DialogueCharacterId = z.infer<typeof DialogueCharacterIdSchema>;

export const DDBDialogueCharacterSettingSchema = z.object({
  PK: z.string(),
  SK: z.literal("SETTINGS#DIALOGUE_CHARACTER"),
  userId: z.string(),
  characterId: DialogueCharacterIdSchema,
  updatedAt: z.string(),
});
export type DDBDialogueCharacterSetting = z.infer<
  typeof DDBDialogueCharacterSettingSchema
>;

/** PATCH /user-settings/dialogue-character リクエストボディ。null=デフォルトに戻す（設定削除） */
export const PatchDialogueCharacterSettingSchema = z.object({
  characterId: DialogueCharacterIdSchema.nullable(),
});
export type PatchDialogueCharacterSettingRequest = z.infer<
  typeof PatchDialogueCharacterSettingSchema
>;

export interface DialogueCharacterSettingResponse {
  success: boolean;
  /** null = 未設定（フォールバック挙動） */
  characterId: DialogueCharacterId | null;
}

// ─── Inquiries (PRODUCT_DEV-635 問い合わせ) ───────────
// テーブル: UnizoInquiries
//   PK: TENANT#{tenantId}#USER#{userId} / SK: INQUIRY#{createdAt}#{inquiryId}
// AI 回答ドラフトは agent-jobs(jobKind=inquiry-answer) が生成し、admin API が
// lazy read-back で draftAnswer に永続化。運営が承認した answer だけがユーザに見える。

// PRODUCT_DEV-908: closed と ai_answered を追加。状態の移り方は
//   AI が答えた問い合わせ → ai_answered（運営には届いていない。利用者の判断待ち）/
//   ai_answered で「運営に問い合わせる」か追記 → open（ここで初めて運営に届く）/
//   ai_answered で「解決しました」→ closed（resolvedByAi = true）/
//   運営が回答・返信 → answered / 利用者が追記 → open（closed からの追記も自動で再オープン）/
//   どちらかが閉じる → closed / どちらかが再オープン → open
export const InquiryStatusEnum = z.enum(["open", "answered", "closed", "ai_answered"]);
export type InquiryStatus = z.infer<typeof InquiryStatusEnum>;

export const InquiryDraftStatusEnum = z.enum(["pending", "ready", "failed"]);
export type InquiryDraftStatus = z.infer<typeof InquiryDraftStatusEnum>;

export const InquiryDraftAnswerSchema = z.object({
  text: z.string(),
  references: z.array(InquiryAnswerReferenceSchema).default([]),
  model: z.string().optional(),
  costUsd: z.number().optional(),
  generatedAt: z.string(),
});
export type InquiryDraftAnswer = z.infer<typeof InquiryDraftAnswerSchema>;

/**
 * 回答に添えるイラスト（PRODUCT_DEV-660）。運営が回答時に選ぶ。
 * done = 星を掲げるゾウ（既定）/ apology = 正面のお辞儀（クレーム対応）/
 * thanks = 花を持ったゾウ（要望・ご意見へのお礼。「はじめての方へ」と同じ絵・PRODUCT_DEV-1162）。
 * 値は足すだけにする（古い画面は知らない値を done として出す）。
 * 「クレームに笑顔で返す」を避けるための切り替えで、UI の variant 名（inquiryDone 等）は
 * 画面都合なのでここには載せない（表示側でマップする）。
 */
export const InquiryAnswerIllustrationEnum = z.enum(["done", "apology", "thanks"]);
export type InquiryAnswerIllustration = z.infer<
  typeof InquiryAnswerIllustrationEnum
>;

export const InquiryAnswerViewSchema = z.object({
  text: z.string(),
  references: z.array(InquiryAnswerReferenceSchema).default([]),
  answeredBy: z.string(), // 回答した運営ユーザの userId
  answeredByName: z.string().optional(),
  answeredAt: z.string(),
  // 未設定 = PD-660 以前に回答済みのデータ。表示側は done にフォールバックする
  illustration: InquiryAnswerIllustrationEnum.optional(),
});
export type InquiryAnswerView = z.infer<typeof InquiryAnswerViewSchema>;

export const InquiryAssigneeSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  // PRODUCT_DEV-635 fix2: アサイン時点の顔写真キー（Users.faceImageKey のスナップショット。
  // キーはイミュータブル（PD-615）なので snapshot で安全。表示は admin の /api/avatar/view-urls で解決）
  faceImageKey: z.string().optional(),
});
export type InquiryAssignee = z.infer<typeof InquiryAssigneeSchema>;

/**
 * やり取りの2通目以降（PRODUCT_DEV-908）。1通目は DDBInquiry.body、運営の最初の回答は
 * DDBInquiry.answer のまま残し、それより後をここへ積む。画面は body → answer → messages の順に
 * 1本のスレッドへ並べる（inquiryThread() を使う）。同じ項目の中の配列にするのは、admin の一覧が
 * 表をまるごと Scan しているため（SK を分けると一覧にやり取りの項目が紛れ込む）。
 */
export const InquiryMessageSchema = z.object({
  // crypto.randomUUID() で採番する（inquiryThread が 1通目・最初の回答に使う "body" / "answer" と衝突させない）
  messageId: z.string().min(1).max(64),
  role: InquiryMessageRoleEnum,
  text: z.string().max(INQUIRY_MESSAGE_MAX_LENGTH),
  // 運営の返信だけが持つ（回答と同じ）
  references: z.array(InquiryAnswerReferenceSchema).optional(),
  illustration: InquiryAnswerIllustrationEnum.optional(),
  authorId: z.string(),
  authorName: z.string().optional(),
  createdAt: z.string(),
});
export type InquiryMessage = z.infer<typeof InquiryMessageSchema>;

// "auto" = 回答のあと返信が無いまま日がたち、定期実行が閉じた（PRODUCT_DEV-908。条件は
// apps/unizo/api/utils/inquiryAutoClose.ts）
export const InquiryClosedByEnum = z.enum(["user", "operator", "auto"]);
export type InquiryClosedBy = z.infer<typeof InquiryClosedByEnum>;

/** 運営へ回す前に AI が出した回答（PRODUCT_DEV-908）。運営にだけ見せる。 */
export const InquiryQaAnswerSchema = z.object({
  text: z.string().max(4000),
  references: z.array(InquiryAnswerReferenceSchema).max(5).default([]),
});
export type InquiryQaAnswer = z.infer<typeof InquiryQaAnswerSchema>;

export const DDBInquirySchema = z.object({
  PK: z.string(),
  SK: z.string(),
  inquiryId: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  // admin 一覧表示用の denormalize（投稿時点の名称スナップショット）
  userName: z.string(),
  tenantName: z.string(),
  category: InquiryCategorySchema,
  title: z.string(),
  body: z.string(),
  // 「解決しない場合は新しく問い合わせる」経由時の元問い合わせ
  relatedInquiryId: z.string().optional(),
  status: InquiryStatusEnum,
  // 運営の担当者（誰でも付け替え可。引き継ぎ確認は web ダイアログ）
  assignee: InquiryAssigneeSchema.optional(),
  draftStatus: InquiryDraftStatusEnum,
  // AI ドラフトの世代番号。再生成のたびに +1（jobId = `{inquiryId}-r{draftRev}`）
  draftRev: z.number(),
  latestJobId: z.string().optional(),
  draftAnswer: InquiryDraftAnswerSchema.optional(),
  answer: InquiryAnswerViewSchema.optional(),
  // fix3(PD-632取り込み): 回答をユーザが確認した時刻。HOME の回答バナーは
  // status=answered && answerAckedAt なし の最新1件に表示し、✕ or 回答閲覧で ack する
  // PD-908: 運営が返信するたびに消す（新しい返信にもバナーを出すため）
  answerAckedAt: z.string().optional(),
  // PRODUCT_DEV-908: 2通目以降のやり取り（未設定 = やり取りなし。移行はしない）
  messages: z.array(InquiryMessageSchema).optional(),
  // PRODUCT_DEV-908: 閉じた時刻と閉じた側。再オープンしたら消す
  closedAt: z.string().optional(),
  closedBy: InquiryClosedByEnum.optional(),
  // PRODUCT_DEV-908: 送った時点で AI が出した回答。やり取りの最初の返事として利用者にも見せ、
  // 運営に回ったあとは AI 下書きにも渡して繰り返させない（priorAiAnswer）。
  // 本文は利用者から受け取らず、サーバが QA の記録（qaLogId）から本人のものだけを引いて入れる
  qaAnswer: InquiryQaAnswerSchema.optional(),
  // PRODUCT_DEV-908: AI の回答だけで利用者が「解決しました」を押して閉じた印（closed のときだけ意味を持つ）
  resolvedByAi: z.boolean().optional(),
  // PRODUCT_DEV-908: 最新の AI 下書きを頼んだ時点のやり取りの通数（inquiryThreadLength）。
  // 運営画面は、今の通数と一致するときだけ下書きを返信欄へ流し込む（答え済みの下書きを再送させない）
  draftBaseLength: z.number().int().min(1).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBInquiry = z.infer<typeof DDBInquirySchema>;

/**
 * inquiryThread / inquiryThreadLength が受ける形。DDBInquiry と、利用者向けの InquiryView
 * （messages の authorId が無い）のどちらも渡せるよう、使う項目だけに絞る。
 */
export interface InquiryThreadSource {
  body: string;
  userName: string;
  createdAt: string;
  qaAnswer?: DDBInquiry["qaAnswer"];
  answer?: DDBInquiry["answer"];
  messages?: ReadonlyArray<Omit<InquiryMessage, "authorId"> & { authorId?: string }>;
}

/** スレッドに並べる1通（画面用）。1通目の本文・最初の回答・messages を同じ形にそろえたもの。 */
export interface InquiryThreadItem {
  /** 1通目は "body"、AI の回答は "ai"、運営の最初の回答は "answer"、それ以外は messageId */
  id: string;
  /** AI の回答（qaAnswer）のとき true。画面は「AI による回答」と出す。role は operator 側に置く */
  fromAi?: boolean;
  role: InquiryMessageRole;
  text: string;
  references: InquiryAnswerReference[];
  illustration?: InquiryAnswerIllustration;
  authorName?: string;
  createdAt: string;
}

/**
 * body → answer → messages を1本のスレッドへ並べる（PRODUCT_DEV-908）。
 * 運営の最初の回答より前に利用者が追記できるので、1通目のあとは createdAt の順に並べ替える
 * （同時刻は body → answer → messages の順のまま。ISO8601 の文字列比較で足りる）。
 */
export function inquiryThread(inquiry: InquiryThreadSource): InquiryThreadItem[] {
  const first: InquiryThreadItem = {
    id: "body",
    role: "user",
    text: inquiry.body,
    references: [],
    authorName: inquiry.userName,
    createdAt: inquiry.createdAt,
  };
  const rest: InquiryThreadItem[] = [];
  if (inquiry.qaAnswer) {
    // AI の回答は送った直後に出るので、1通目と同じ時刻で 1通目のすぐ後に置く
    rest.push({
      id: "ai",
      fromAi: true,
      role: "operator",
      text: inquiry.qaAnswer.text,
      references: inquiry.qaAnswer.references,
      createdAt: inquiry.createdAt,
    });
  }
  if (inquiry.answer) {
    rest.push({
      id: "answer",
      role: "operator",
      text: inquiry.answer.text,
      references: inquiry.answer.references,
      illustration: inquiry.answer.illustration,
      authorName: inquiry.answer.answeredByName,
      createdAt: inquiry.answer.answeredAt,
    });
  }
  for (const m of inquiry.messages ?? []) {
    rest.push({
      id: m.messageId,
      role: m.role,
      text: m.text,
      references: m.references ?? [],
      illustration: m.illustration,
      authorName: m.authorName,
      createdAt: m.createdAt,
    });
  }
  return [
    first,
    ...rest
      .map((item, index) => ({ item, index }))
      .sort((a, b) =>
        a.item.createdAt === b.item.createdAt
          ? a.index - b.index
          : a.item.createdAt < b.item.createdAt
            ? -1
            : 1,
      )
      .map(({ item }) => item),
  ];
}

/** スレッドの通数（1通目・最初の回答を含む）。INQUIRY_MAX_THREAD_LENGTH と比べる。 */
export function inquiryThreadLength(
  inquiry: Pick<InquiryThreadSource, "answer" | "messages">,
): number {
  return 1 + (inquiry.answer ? 1 : 0) + (inquiry.messages?.length ?? 0);
}

/** POST /inquiries/{inquiryId}/messages — 利用者の追記（PRODUCT_DEV-908）。 */
export const PostInquiryMessageRequestSchema = z.object({
  text: z.string().trim().min(1).max(INQUIRY_MESSAGE_MAX_LENGTH),
});
export type PostInquiryMessageRequest = z.infer<
  typeof PostInquiryMessageRequestSchema
>;

// ─── Inquiry FAQ (PRODUCT_DEV-908 よくある質問) ───────────
// テーブル: UnizoInquiryFaqs（全テナント共通の1セット。運営 Umeee が admin で登録する）
//   PK: "FAQ" / SK: "FAQ#{faqId}"
// 件数は少ない見込みなので 1 回の Query で全件取る。書くのは admin、読むのは unizo
// （FAQ 一覧・AI Q&A・AI 下書きのイベントへの同梱）。
export const INQUIRY_FAQ_PK = "FAQ";
export const inquiryFaqSK = (faqId: string): string => `FAQ#${faqId}`;

export const DDBInquiryFaqSchema = z.object({
  PK: z.literal(INQUIRY_FAQ_PK),
  SK: z.string(),
  // crypto.randomUUID()。AI 下書きの入力（InquiryAnswerFaqSchema）と同じ 64 字までにそろえる
  faqId: z.string().min(1).max(64),
  category: InquiryCategorySchema,
  question: z.string().max(INQUIRY_FAQ_QUESTION_MAX_LENGTH),
  answer: z.string().max(INQUIRY_FAQ_ANSWER_MAX_LENGTH),
  // false = 下書き（利用者の一覧にも AI の根拠にも出さない）
  published: z.boolean(),
  // 「この回答を FAQ に登録」から作ったときの元の問い合わせ
  sourceInquiryId: z.string().optional(),
  // PRODUCT_DEV-908: 関連リンク（ガイドの記事・ほかの FAQ・Unizo の常設ページ）。3つまで。未設定 = なし
  links: z.array(InquiryAnswerReferenceSchema).max(INQUIRY_LINKS_MAX).optional(),
  sortOrder: z.number(),
  createdBy: z.string(),
  // PRODUCT_DEV-908: 最後に更新した運営（全テナントの AI の根拠を変える操作なので残す）
  updatedBy: z.string().optional(),
  // PRODUCT_DEV-908: 問い合わせから作った FAQ を公開してよいと確かめた運営
  sanitizedConfirmedBy: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBInquiryFaq = z.infer<typeof DDBInquiryFaqSchema>;

/** 利用者へ返す FAQ（公開中のものだけ。運営の内部情報は落とす）。 */
export const InquiryFaqViewSchema = DDBInquiryFaqSchema.pick({
  faqId: true,
  category: true,
  question: true,
  answer: true,
  links: true,
  sortOrder: true,
  updatedAt: true,
});
export type InquiryFaqView = z.infer<typeof InquiryFaqViewSchema>;

export interface InquiryFaqsResponse {
  success: true;
  faqs: InquiryFaqView[];
}

// API view（内部キー PK/SK と運営向け内部情報 draftAnswer/latestJobId を落とす）
export const InquiryViewSchema = DDBInquirySchema.omit({
  PK: true,
  SK: true,
  draftAnswer: true,
  latestJobId: true,
}).extend({
  // PRODUCT_DEV-908: 利用者へは運営の内部 ID（authorId）を返さない。名前（authorName）だけ残す
  messages: z
    .array(InquiryMessageSchema.extend({ authorId: z.string().optional() }))
    .optional(),
});
export type InquiryView = z.infer<typeof InquiryViewSchema>;

export const CreateInquiryRequestSchema = z.object({
  category: InquiryCategorySchema,
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(4000),
  relatedInquiryId: z.string().max(64).optional(),
  // PRODUCT_DEV-731: 問い合わせ前 AI Q&A から引き継いで投稿されたか（週次レポートの
  // 「AI で解決したか」を測る唯一の手掛かり）。web が Q&A 画面から遷移したときだけ true を
  // 送る自己申告で、サーバは検証しない。**optional 必須**: 古いクライアント・Q&A を通らない
  // 通常投稿は送ってこない（後方互換）。
  fromQa: z.boolean().optional(),
  // 何問 AI に聞いてから運営へ切り替えたか。fromQa が true のときだけ意味を持つ。
  qaTurnCount: z.number().int().min(0).max(100).optional(),
  // PRODUCT_DEV-908: AI が答えたが解決せず運営へ回したときの、その AI の回答の記録
  // （POST /inquiries/qa の応答の qaLogId）。**回答の本文は受け取らない。** 利用者が書き換えた
  // 文を「AI が出した回答」として運営と AI 下書きに見せないよう、サーバが QA の記録から
  // 本人のものだけを引いて DDBInquiry.qaAnswer に保存する。
  qaLogId: z.string().min(1).max(200).optional(),
  // PRODUCT_DEV-908: AI が答えた時点で作る問い合わせ（状態 ai_answered・運営には届けない）。
  // qaLogId 必須。運営に回すのは POST /inquiries/{id}/reopen（「運営に問い合わせる」）か追記
  aiAnswered: z.boolean().optional(),
});
export type CreateInquiryRequest = z.infer<typeof CreateInquiryRequestSchema>;

// ─── Inquiry Q&A (PRODUCT_DEV-690 問い合わせ前の AI Q&A) ───────────
// 問い合わせフォームの手前に挟む同期 Q&A。使い方ガイドカタログを根拠に LLM がその場で答え、
// 回答は運営の承認を経ずユーザへ直接届く（上の draftAnswer 経路とはそこが異なる）。
// やりとりは DDB に保存しない。未解決なら web 側が全ターンを本文へ畳んで
// CreateInquiryRequest として投稿するため、問い合わせ側の契約は変更していない。

/**
 * 直近のやりとり 1 往復。web は積み上げた全ターンのうち**末尾 2 件だけ**を送る。
 * 受け口を max(2) で絞るのはサーバ側でトークン量を頭打ちにするため（ガイドカタログ全件を
 * system に積むので、全履歴を渡すと API Gateway の 29 秒キャップに近づく）。
 */
export const InquiryQaTurnSchema = z.object({
  question: z.string().max(500),
  answerText: z.string().max(4000),
});
export type InquiryQaTurn = z.infer<typeof InquiryQaTurnSchema>;

export const InquiryQaRequestSchema = z.object({
  // PRODUCT_DEV-908: 問い合わせフォームの本文をそのまま送るので、本文と同じ 4000 字まで受ける
  // （PD-690 の AI に聞く画面は 500 字までしか送ってこないので、広げても困らない）
  question: z.string().min(1).max(INQUIRY_MESSAGE_MAX_LENGTH),
  history: z.array(InquiryQaTurnSchema).max(2).optional(),
  // PRODUCT_DEV-908: 問い合わせフォームから送るときの件名とカテゴリ（AI が答えられるかの判断に使う）
  category: InquiryCategorySchema.optional(),
  title: z.string().max(100).optional(),
});
export type InquiryQaRequest = z.infer<typeof InquiryQaRequestSchema>;

export const InquiryQaResponseSchema = z.object({
  success: z.literal(true),
  answerText: z.string().max(4000),
  // カタログ突合済みの参照だけが入る（未知 id は engine 側で drop 済み）
  references: z.array(InquiryAnswerReferenceSchema).max(5),
  // PRODUCT_DEV-908: ガイドや FAQ を根拠に答えられたか。false なら web は AI の回答を出さず、
  // そのまま運営への問い合わせとして投稿する（利用者に送り先を意識させない）。
  // 未設定 = PD-908 以前の API（答えられた扱い）
  answerable: z.boolean().optional(),
  // PRODUCT_DEV-908: この回答の記録の ID。運営へ回すときに CreateInquiryRequest.qaLogId で返す
  qaLogId: z.string().max(200).optional(),
});
export type InquiryQaResponse = z.infer<typeof InquiryQaResponseSchema>;

// ─── Inquiry Q&A 利用ログ (PRODUCT_DEV-731) ─────────────────────────
// PRODUCT_DEV-690 は「やりとりを保存しない」設計だったが、運営が「AI に何を聞かれているか」を
// 一切観測できず、ガイド拡充・回答品質の監視・不具合報告の取りこぼし検知ができなかったため、
// 保存する方針へ明示的に反転させた。
//
// 専用テーブル UnizoInquiryQaLogs に置く（UnizoInquiries へ相乗りさせない）。読み手は
// **運営向けの全テナント横断・週次集計 1 本だけ**なので、PK にテナントを入れず日付だけで
// バケットする。これで週次集計が 7 回の Query で完結し、Scan も GSI も要らない。
// テナント単位の抽出が要件化したら GSI_ByTenant を後付けできるよう tenantId は素の属性で持つ。
//
// TTL は貼らない（`ttl` 属性を書かない）＝ログは消えない。テーブル側には
// timeToLiveAttribute だけ宣言してあるので、保持期間を導入したくなったらコードで
// `ttl` を書き始めるだけで済む。
export const DDBInquiryQaLogSchema = z.object({
  PK: z.string(), // DATE#<YYYY-MM-DD(JST)>
  SK: z.string(), // QA#<ISO timestamp>#<uuid> または HANDOFF#<ISO timestamp>#<uuid>
  // "qa" = AI への質問 1 回、"handoff" = そこから運営への問い合わせに進んだ印
  kind: z.enum(["qa", "handoff"]),
  tenantId: z.string(),
  tenantName: z.string(),
  userId: z.string(),
  userName: z.string(),
  createdAt: z.string(), // ISO8601
  jstDate: z.string(), // YYYY-MM-DD(JST)。PK と同じ日付を素の属性でも持つ（集計側が読みやすい）

  // ---- kind === "qa" ----
  // PRODUCT_DEV-908: 問い合わせフォームの本文をそのまま聞くので、リクエストと同じ上限まで広げた
  question: z.string().max(INQUIRY_MESSAGE_MAX_LENGTH).optional(),
  // status === "error"（LLM が落ちた）のときは無い
  answerText: z.string().max(4000).optional(),
  references: z.array(InquiryAnswerReferenceSchema).max(5).optional(),
  // 0 = 使い方ガイドに根拠が無かった。ガイド拡充の直接のネタになる
  referenceCount: z.number().int().min(0).optional(),
  status: z.enum(["answered", "error"]).optional(),
  // 0 = その問い合わせの 1 問目。週次の「AIへの問い合わせ件数」はこれを数える
  // （リクエストの history が空＝新しい問い合わせの始まり）
  turnIndex: z.number().int().min(0).optional(),
  model: z.string().max(100).optional(),
  // PRODUCT_DEV-908: AI がガイド・FAQ を根拠に答えられたか（false は運営へ回した）。PD-908 以前のログには無い
  answerable: z.boolean().optional(),
  // 将来 web が会話 ID を送るようになったとき用。現時点では書かない
  sessionId: z.string().max(64).optional(),

  // ---- kind === "handoff" ----
  inquiryId: z.string().max(64).optional(),
  qaTurnCount: z.number().int().min(0).optional(),
});
export type DDBInquiryQaLog = z.infer<typeof DDBInquiryQaLogSchema>;

export interface InquiryResponse {
  success: boolean;
  inquiry: InquiryView;
}

export interface InquiriesResponse {
  success: boolean;
  inquiries: InquiryView[];
}

// ==========================================
// 自由記録モード非同期処理 (Speed Recording Async)
// ==========================================

/**
 * 自由記録アップロードURL取得API
 * POST /speed-recording/upload-url
 * Meeting事前作成 + S3 presigned URL生成
 *
 * contentType / extension はクライアントブラウザが対応するMIMEに応じて指定。
 * Safari は audio/mp4 (拡張子 m4a)、それ以外は audio/webm (拡張子 webm)。
 * 後方互換のため default 付き optional とし、未指定時は webm 扱い。
 */
export const SpeedRecordingUploadUrlRequestSchema = z.object({
  presetId: z.string(),
  contentType: z
    .enum(["audio/webm", "audio/mp4"])
    .default("audio/webm")
    .optional(),
  extension: z.enum(["webm", "m4a"]).default("webm").optional(),
  groupId: z.string().nullable().optional(),
  // PRODUCT_DEV-403: 続きの商談 × 自由記録モードで親商談から社名・担当者を引き継ぐ
  sourceMeetingId: z.string().optional(),
  // PRODUCT_DEV-796: 今日の予定（日程調整の行）から始めた自由記録
  scheduleId: z.string().min(1).max(64).optional(),
  // PRODUCT_DEV-1017: 手帳の自分の予定の行から始めた自由記録。形が不正でも 400 にしない（録音を失わせない）
  selfPlanId: z.string().regex(SELF_PLAN_ID).optional().catch(undefined),
  // PRODUCT_DEV-1225: 外部カレンダーの予定の行から始めた自由記録。形が不正でも 400 にしない（録音を失わせない）
  externalEventKey: z.string().regex(EXTERNAL_EVENT_KEY_RE).optional().catch(undefined),
  // PRODUCT_DEV-1041: 予定の行から始めた自由記録の商談日時（予定の開始時刻）
  meetingDate: PlannedMeetingDateSchema,
  meetingTime: PlannedMeetingTimeSchema,
  // PRODUCT_DEV-721: 記録先の部署（画面のいまの部署）。所属していなければサーバが捨てる
  deptId: z.string().min(1).max(64).optional(),
  // PRODUCT_DEV-1030: 非公開の予定から始めた自由記録の公開範囲。不正な値は非公開に倒す
  // （400 にしない＝録音を止めない。黙って公開にもしない。送らなければ公開）
  visibility: RecordVisibilitySchema.optional().catch("private"),
  sharedWith: SharedWithSchema.optional().catch(undefined),
  company: z.string().nullable().optional(),
  contacts: z.array(ContactSchema).nullable().optional(),
  // PRODUCT_DEV-655(第2弾): 顧客詳細（個人）から自由記録を始めたときに、顧客マスタの
  // お客様情報を extractedData.customer_info の初期値として引き継ぐ（JSON 文字列）。
  customerInfo: z.string().nullable().optional(),
  // PRODUCT_DEV-1270: 送り直し。前の送信で作った記録（まだ音声を待っているもの）に音声を入れ直す。
  // 本人の自由記録で、まだ処理中のときだけ使い、そうでなければ 409（画面は新しく作り直す）
  meetingId: z.string().min(1).max(128).optional(),
});

export type SpeedRecordingUploadUrlRequest = z.infer<
  typeof SpeedRecordingUploadUrlRequestSchema
>;

export interface SpeedRecordingUploadUrlResponse {
  success: boolean;
  meetingId: string;
  uploadUrl: string;
  s3Key: string;
}

/**
 * 自由記録サブミットAPI
 * POST /speed-recording/submit
 * SQSキューへの非同期処理投入
 */
export const SpeedRecordingSubmitRequestSchema = z.object({
  meetingId: z.string(),
  // PRODUCT_DEV-485: クライアント計測の録音秒数。gpt-4o-transcribe は duration を
  // 返さないため、収録時間はフロント計測値を採用する（未指定時は worker 側で 0 にフォールバック）。
  durationSec: z.number().int().nonnegative().optional(),
});

export type SpeedRecordingSubmitRequest = z.infer<
  typeof SpeedRecordingSubmitRequestSchema
>;

export interface SpeedRecordingSubmitResponse {
  success: boolean;
  meetingId: string;
  status: "processing";
}

/**
 * 対話モード音声アップロードURL取得API（PRODUCT_DEV-514）
 * POST /recording/audio-upload-url
 * 既存 Meeting（dialogue モード、会話末で作成済み）に紐づく音声を
 * 故障解析用に S3 へ保存するための presigned PUT URL を発行する。
 */
export const RecordingAudioUploadUrlRequestSchema = z.object({
  meetingId: z.string(),
  contentType: z
    .enum(["audio/webm", "audio/mp4"])
    .default("audio/webm")
    .optional(),
  extension: z.enum(["webm", "m4a"]).default("webm").optional(),
  /**
   * PRODUCT_DEV-1310: 振り返りの音声。商談の録音（`_dialogue_audio`）を上書きしないよう、
   * 録った人と日時を入れた別の名前で保存する。省略時は従来どおり商談の録音
   */
  purpose: z.enum(["review"]).optional(),
});

export type RecordingAudioUploadUrlRequest = z.infer<
  typeof RecordingAudioUploadUrlRequestSchema
>;

export interface RecordingAudioUploadUrlResponse {
  success: boolean;
  uploadUrl: string;
  s3Key: string;
}

/**
 * SQSメッセージ型（speed-recording-worker用）
 *
 * contentType は worker が Whisper API へ渡すファイル種別の判定に使用。
 * 後方互換: 未指定/undefinedの場合は audio/webm として扱う。
 */
export interface SpeedRecordingProcessorMessage {
  tenantId: string;
  userId: string;
  meetingId: string;
  presetId: string;
  s3Key: string;
  contentType?: string;
  // PRODUCT_DEV-485: クライアント計測の録音秒数。worker が収録時間に採用（未指定は 0）。
  durationSec?: number;
  // PRODUCT_DEV-1085: 受け付けたときにフラグ transcribeGptTranscribe が ON だったか。
  // 無い（古い・sweeper が積み直した）メッセージは今のモデルで起こす。
  transcribeNext?: boolean;
}

/**
 * SQSメッセージ型（dialogue-completion-worker用 / PRODUCT_DEV-525）
 *
 * 対話モードの GateKeeper 補完を非同期化するキュー。producer は meetings/post.ts、
 * consumer は dialogue-completion/worker。presetId は worker が preset 駆動で
 * extractedData を検証するため使用（未指定可）。
 */
export interface DialogueCompletionMessage {
  tenantId: string;
  userId: string;
  meetingId: string;
  conversationLog: string;
  presetId?: string;
  /**
   * PRODUCT_DEV-612（連鎖モデル）: 起票元。既定（未指定）は新規商談の作成。
   *
   * `"review"` は既存商談への振り返り追記（producer は meetings/[meetingId]/patch.ts）。
   * worker はこのとき振り返り項目の抽出指示（ONP `MINUTES_REVIEW_EXTRACTION`）を preset へ足す。
   *
   * `"minutes"` は議事録本文から作った商談（producer は meetings/post.ts）。
   * worker はこのとき抽出テンプレートへ箇条書き書式ルールを連結する
   * （議事録は材料が長く、散文だと項目が読めなくなるため）。
   *
   * PRODUCT_DEV-1204: `"reviewEdit"` は振り返りの手直しで増えた話（producer は meetings/[meetingId]/patch.ts）。
   * conversationLog には増えた行だけが入る。商談記録が完了済み（status:"completed"）かつドラフト（reviewed:false）の
   * ときだけ積まれる。worker は商談記録の項目へ追記するだけで、振り返りの欄・status・対話ログ・後段・通知には触らない。
   * この値を知らない古い worker は新規作成として扱うが、完了済みの記録は冪等の判定で飛ばすので書き換えない
   * （producer が completed に限っているのはこのため）。memberReviewUserId は付けない。
   */
  mode?: "review" | "minutes" | "reviewEdit" | "regenerate";
  /**
   * PRODUCT_DEV-1174: `mode:"regenerate"` のときだけ。材料の出どころ（`"minutes"` は議事録から作った記録で、
   * 箇条書きの書式で作り直す）。producer は meetings/[meetingId]/patch.ts で、記録は `status:"processing"`、
   * presetId は選び直したプリセット。worker は項目を作り直して企業名・担当者・お客様の氏名とフリガナ・振り返りを戻し、
   * 仮登録にして `aiGeneratedAt` を付ける。後段（文字起こし）・採点・通知には流さない。
   * ⚠ この値を知らない古い worker は新規作成として扱う（記録が processing のため）。worker と API は同じ
   * スタック（UnizoMeetingStack）で配るので、入れ替わりの間だけの話。配るのを巻き戻すときは
   * dialogue-completion のキューが空になってから戻す。meeting-sweeper は別のスタック（UnizoSpeechStack）で、
   * 古い sweeper でも再生成中の記録を片付けないよう、PATCH は処理中にするときに更新日時を進める
   */
  regenerateStyle?: "minutes" | "voice";
  /** PRODUCT_DEV-1095: 録音中に同じ項目へ来た値の移り変わり。無ければ今までどおり */
  liveValueHistory?: LiveValueHistory;
  /**
   * PRODUCT_DEV-1045: 議事録ができたときに minutes-finalize-worker が積んだ、その議事録。
   * worker は商談記録の `awaitingMinutesId` と一致し、議事録の持ち主が作った処理中の商談記録のときだけ仕上げる
   * （議事録に置かれた商談記録の ID はサーバが照合していないため、ここで照合する）。
   */
  minutesId?: string;
  /**
   * PRODUCT_DEV-1046: 記録者以外の人の振り返り（`mode:"review"` のときだけ。`userId` と同じ値）。
   * worker は商談記録の項目・status に触らず、`memberReviews[memberReviewUserId]` にだけ保存する
   */
  memberReviewUserId?: string;
}

// ============================================================
// PRODUCT_DEV-1088 商談記録の点数（記録の質）
// ============================================================
// 点の式・数え方・採点基準の文の正本は meetingScoreRubric.ts / meetingScore.ts。
// 点数は商談のレコード（DDBMeeting）には持たせず、別の表（UnizoMeetingScores）に置く。
// 商談を返す API はどれも点数を知らないままにして、返す口を1本（GET /meetings/{meetingId}/score）に絞るため。

/** C・D・E の段階（人の採点基準と同じ5段階）。⚠ Admin の人の採点の表（MeetingHumanScores）も同じ型で保存している。値を変えるときは保存済みの人の採点を rubricVersion で読み分ける */
export const MeetingScoreLevelSchema = z.union([
  z.literal(0),
  z.literal(3),
  z.literal(5),
  z.literal(8),
  z.literal(10),
]);
export type MeetingScoreLevel = z.infer<typeof MeetingScoreLevelSchema>;

/** C 課題の深掘り / D 次の一手の具体さ / E 考察 */
export const MeetingScoreAspectIdSchema = z.enum(["issueDepth", "nextAction", "insight"]);
export type MeetingScoreAspectId = z.infer<typeof MeetingScoreAspectIdSchema>;

/** 観点1つの判定。LLM が型どおりに返さなかった観点は level を null にして、理由を unjudgedReason に残す */
export const MeetingScoreAspectSchema = z.object({
  level: MeetingScoreLevelSchema.nullable(),
  /** なぜその段階か */
  reason: z.string(),
  /** 根拠にした文（記録・振り返りからの抜き出し）。0 点のときは空文字 */
  evidence: z.string(),
  /** 根拠の文が、LLM に渡した記録の本文にそのまま見つかったか（作文された根拠を見分ける。段階は変えない） */
  evidenceInRecord: z.boolean().optional(),
  /** なぜ1つ下の段階ではないか（基準 2026-10-15.1 から。それより前の点数と 0 点には無い） */
  whyNotLower: z.string().optional(),
  /** なぜ1つ上の段階ではないか（基準 2026-10-15.1 から。それより前の点数と 10 点には無い） */
  whyNotHigher: z.string().optional(),
  unjudgedReason: z.string().optional(),
  /** 判定を複数回行ったときの、各回の段階（未判定の回は null）。1回のときは持たない */
  runLevels: z.array(MeetingScoreLevelSchema.nullable()).optional(),
});
export type MeetingScoreAspect = z.infer<typeof MeetingScoreAspectSchema>;

/** A「聞くと決めたことに応えた」の内訳 */
export const MeetingScoreCoverageSchema = z.object({
  /** 0〜60 */
  points: z.number(),
  weightAnswered: z.number(),
  weightTotal: z.number(),
  requiredTotal: z.number(),
  requiredAnswered: z.number(),
  optionalTotal: z.number(),
  optionalAnswered: z.number(),
  /** 漏れた項目（「対象外」は入らない） */
  missing: z.array(z.object({ key: z.string(), label: z.string(), required: z.boolean() })),
});
export type MeetingScoreCoverage = z.infer<typeof MeetingScoreCoverageSchema>;

/**
 * 採点の結果。scored = 3観点とも判定できた / partial = 判定できなかった観点がある（0 点として足している）
 * judge_failed = LLM を呼べなかった（A・B だけの点）。Admin の人の採点（admin/contract.ts）もこれを使う
 */
export const MeetingScoreStatusSchema = z.enum(["scored", "partial", "judge_failed"]);
export type MeetingScoreStatus = z.infer<typeof MeetingScoreStatusSchema>;

/**
 * 採点のきっかけ。created = 記録の完了 / reviewed = 振り返りの保存 / edited = 人が記録の項目を直して保存 /
 * rescore = 人が実行した付け直し
 */
export const MeetingScoreReasonSchema = z.enum(["created", "reviewed", "edited", "rescore"]);
export type MeetingScoreReason = z.infer<typeof MeetingScoreReasonSchema>;

/**
 * 採点1回ぶんの記録（UnizoMeetingScores）。上書きせず履歴として残し、最新を指す行を別に持つ。
 * PK: TENANT#{tenantId}
 * SK: MEETING#{meetingId}#SCORE#{scoredAt}（履歴） / MEETING#{meetingId}#LATEST（最新の写し）
 */
export const DDBMeetingScoreSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  tenantId: z.string(),
  meetingId: z.string(),
  /** 採点した時刻（ISO 8601・UTC） */
  scoredAt: z.string(),
  reason: MeetingScoreReasonSchema,
  /** 合計（30〜100 の整数） */
  total: z.number(),
  coverage: MeetingScoreCoverageSchema,
  hasReview: z.boolean(),
  aspects: z.record(MeetingScoreAspectIdSchema, MeetingScoreAspectSchema),
  /**
   * scored = 3観点とも判定できた / partial = 判定できなかった観点がある（0 点として足している）
   * judge_failed = LLM を呼べなかった（A・B だけの点）
   */
  status: MeetingScoreStatusSchema,
  /** 採点基準の版（MEETING_SCORE_RUBRIC_VERSION） */
  rubricVersion: z.string(),
  /** 判定に使ったモデル。LLM を呼べなかったときは null */
  model: z.string().nullable(),
  /** LLM に判定させた回数（複数回のときは観点ごとに多数決。各回の段階は aspects の runLevels） */
  judgeRuns: z.number(),
  /** 採点した時点の記録の指紋。あとで記録が変わったかを見分ける */
  inputFingerprint: z.string(),
  /** 採点に使ったプリセット（基準の文をプリセットごとに見直すため） */
  presetId: z.string().optional(),
});
export type DDBMeetingScore = z.infer<typeof DDBMeetingScoreSchema>;

/** 採点キューのメッセージ。商談の本文は載せない（scorer が商談とプリセットを読む） */
export interface MeetingScoreMessage {
  tenantId: string;
  meetingId: string;
  reason: MeetingScoreReason;
}

// ============================================================
// PRODUCT_DEV-424 メモ / ToDo 機能
// ============================================================

// AI 抽出 ToDo 候補（SuggestedTodoSchema）は DDBMeetingSchema より前に定義している（PRODUCT_DEV-998）。

/**
 * DynamoDB メモ型
 * PK: TENANT#{tenantId}#USER#{userId}（Q1案A: 物理強制）
 * SK: MEMO#{createdAt}#{memoId}（新着降順 Query）
 */
export interface DDBMemo {
  PK: string; // TENANT#{tenantId}#USER#{userId}
  SK: string; // MEMO#{createdAt}#{memoId}
  GSI1PK: string; // TENANT#{tenantId}#USER#{userId}
  GSI1SK: string; // MEMO#{memoId} （1件取得用）
  memoId: string;
  tenantId: string;
  userId: string;
  type: "voice" | "manual";
  content: string;
  title?: string; // Phase 5: メモタイトル (worker が AI 生成 or user が手動編集)
  audioS3Key?: string;
  durationSec?: number; // D3: 音声の長さ(秒)。voice メモのみ、worker が保存
  transcriptStatus?: "pending" | "transcribing" | "extracting" | "ready" | "failed";
  suggestedTodos?: SuggestedTodo[]; // AI 抽出候補（Q2案A: Memo 同梱）
  companyId?: string; // Phase 4 で利用
  meetingId?: string; // Phase 4 で利用
  /**
   * PRODUCT_DEV-782: 手帳の「自分の予定」から録ったメモ。予定の ID と、**作った時点の**
   * 予定のタイトル・日付の控え（予定を後で消しても「どの予定のメモか」を出せるように）。
   */
  selfPlanId?: string;
  selfPlanTitle?: string;
  selfPlanDate?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  status?: "active" | "trashed";
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// メモ作成リクエスト
export const CreateMemoSchema = z.object({
  type: z.enum(["voice", "manual"]).default("manual"),
  content: z.string().min(1, "メモ内容は必須です").max(5000, "メモは5000文字以内です"),
  title: z.string().max(120).optional(), // Phase 5
  companyId: z.string().optional(),
  meetingId: z.string().optional(),
});
export type CreateMemoRequest = z.infer<typeof CreateMemoSchema>;

// メモ更新リクエスト
export const UpdateMemoSchema = z.object({
  content: z.string().min(1).max(5000).optional(),
  title: z.string().max(120).nullable().optional(), // Phase 5
  companyId: z.string().nullable().optional(),
  meetingId: z.string().nullable().optional(),
  // Phase 7 #12/#13: AI 候補(suggestedTodos)の採択/却下/差し戻しを永続化するため
  // フロントから配列ごと差し替えできるようにする (後方互換 optional)
  suggestedTodos: z.array(SuggestedTodoSchema).optional(),
});
export type UpdateMemoRequest = z.infer<typeof UpdateMemoSchema>;

// メモ単体レスポンス用のサブ型
export interface MemoItem {
  memoId: string;
  type: "voice" | "manual";
  content: string;
  title?: string; // Phase 5
  audioS3Key?: string;
  durationSec?: number; // D3
  transcriptStatus?: DDBMemo["transcriptStatus"];
  suggestedTodos?: SuggestedTodo[];
  companyId?: string;
  meetingId?: string;
  /**
   * PRODUCT_DEV-782: どの自分の予定から録ったか（`DDBMemo` 参照）。
   * PRODUCT_DEV-1040: GET /memos・GET /memos/{memoId} は、予定が残っていれば予定の**今の**
   * タイトル・日付を返す（予定を動かすとついていく）。消した予定は控えのまま。
   */
  selfPlanId?: string;
  selfPlanTitle?: string;
  selfPlanDate?: string;
  createdAt: string;
  updatedAt: string;
  // PRODUCT_DEV-506: 登録者の userId。テナント管理者向けレスポンスでのみ付与され、
  // 一般ユーザー向けレスポンスでは省略される（プライバシー）。フロントは users マスタで名前解決。
  createdBy?: string;
}

export interface MemosResponse {
  success: boolean;
  memos: MemoItem[];
}

export interface MemoResponse {
  success: boolean;
  memo: MemoItem;
}

export interface DeleteMemoResponse {
  success: boolean;
  memoId: string;
}

/**
 * DynamoDB ToDo 型
 * PK: TENANT#{tenantId}#USER#{userId}
 * SK: TODO#{dueAt|9999}#{todoId}（期限昇順 Query、期限なしは末尾）
 */
export interface DDBTodo {
  PK: string;
  SK: string;
  GSI1PK: string; // TENANT#{tenantId}#USER#{userId}
  GSI1SK: string; // TODO#{todoId} （1件取得用）
  todoId: string;
  tenantId: string;
  userId: string;
  title: string;
  dueAt?: string; // ISO8601、未設定=期限なし
  note?: string; // 補足メモ (D7)
  status: "pending" | "done";
  sourceMemoId?: string;
  // PRODUCT_DEV-586: 議事録の Nextアクションから「落とした」ToDo の逆引き用（単一・任意）。
  sourceMinutesId?: string;
  /** PRODUCT_DEV-998: 作った時点の議事録の件名の控え（手帳・一覧のチップに出す。議事録を後で直しても変えない）。 */
  sourceMinutesTitle?: string;
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id。議事録詳細の「ToDo化済み」判定に使う（任意）。
  // PRODUCT_DEV-998: 商談記録の候補から作ったときは候補の id（SuggestedTodo.id）が入る。
  sourceActionId?: string;
  /**
   * PRODUCT_DEV-998: 商談記録から作ったToDoの商談 ID。商談記録の「この商談のToDo」の逆引き用。
   * 書けるのは商談のToDo API（`/meetings/{meetingId}/todos`）だけで、商談を編集できるかを確かめてから入れる
   * （`POST /todos` からは送らせない）。ToDoの持ち主（`userId`）は商談記録を作った人。
   */
  sourceMeetingId?: string;
  /** PRODUCT_DEV-998: 作った時点の商談の名前の控え（手帳のチップに出す。商談を後で直しても変えない）。 */
  sourceMeetingTitle?: string;
  sourceType: "manual" | "ai_extracted";
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// ToDo 作成リクエスト
export const CreateTodoSchema = z.object({
  title: z.string().min(1, "タイトルは必須です").max(200, "タイトルは200文字以内です"),
  dueAt: z.string().nullable().optional(),
  note: z.string().max(2000, "補足は2000文字以内です").optional(),
  sourceMemoId: z.string().optional(),
  // PRODUCT_DEV-586: 議事録詳細から ToDo を作成する場合の紐づけ（任意・後方互換）。
  sourceMinutesId: z.string().optional(),
  // PRODUCT_DEV-998: 議事録の件名の控え（任意・後方互換）。sourceMinutesId があるときだけ保存する。
  // /todos の Lambda は議事録を読めないので、画面が表示中の議事録の件名を送る（チップの文字に使うだけ）。
  sourceMinutesTitle: z.string().max(200).optional(),
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id（任意・後方互換）。
  sourceActionId: z.string().optional(),
  sourceType: z.enum(["manual", "ai_extracted"]).default("manual"),
});
export type CreateTodoRequest = z.infer<typeof CreateTodoSchema>;

// ToDo 一括作成（AI 抽出候補の採択用、Phase 3）
export const BatchCreateTodosSchema = z.object({
  sourceMemoId: z.string(),
  // PRODUCT_DEV-998: 議事録の件名の控えは単件作成だけ（一括作成はメモの候補用なので受け付けない）
  todos: z.array(CreateTodoSchema.omit({ sourceType: true, sourceMemoId: true, sourceMinutesTitle: true })).min(1).max(50),
});
export type BatchCreateTodosRequest = z.infer<typeof BatchCreateTodosSchema>;

// ToDo 更新リクエスト
export const UpdateTodoSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  dueAt: z.string().nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
  status: z.enum(["pending", "done"]).optional(),
});
export type UpdateTodoRequest = z.infer<typeof UpdateTodoSchema>;

// ToDo レスポンス用サブ型
export interface TodoItem {
  todoId: string;
  title: string;
  dueAt?: string;
  note?: string;
  status: "pending" | "done";
  sourceMemoId?: string;
  // PRODUCT_DEV-586: 議事録の Nextアクションから作成された ToDo の逆引き用（単一・任意）。
  sourceMinutesId?: string;
  // PRODUCT_DEV-998: 作った時点の議事録の件名の控え（無い古い ToDo は画面が「議事録」と出す）。
  sourceMinutesTitle?: string;
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id。議事録詳細の「ToDo化済み」判定に使う（任意）。
  sourceActionId?: string;
  // PRODUCT_DEV-998: 商談記録から作ったToDoの商談 ID と、作った時点の商談の名前の控え。
  sourceMeetingId?: string;
  sourceMeetingTitle?: string;
  sourceType: "manual" | "ai_extracted";
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  // PRODUCT_DEV-506: 登録者の userId。テナント管理者向けレスポンスでのみ付与される（プライバシー）。
  // PRODUCT_DEV-1117: ToDo では**持ち主**（DDB の `userId`）を返す。他の人の分を見られる管理者にだけ付き、自分の分にも付く。
  createdBy?: string;
  /**
   * PRODUCT_DEV-998: 持ち主以外（商談記録の管理者・参加者）が追加したときの、追加した人の userId。
   * 持ち主が自分で追加したものには付かない。手帳の「◯◯さんが追加」に使う。
   * （`createdBy` は編集できるかの判定に使われているので、別の項目にしている）
   */
  addedBy?: string;
}

export interface TodosResponse {
  success: boolean;
  todos: TodoItem[];
}

export interface TodoResponse {
  success: boolean;
  todo: TodoItem;
}

export interface DeleteTodoResponse {
  success: boolean;
  todoId: string;
}

// ============================================================
// 商談記録のToDo（PRODUCT_DEV-998）
// ============================================================

/**
 * POST /meetings/{meetingId}/suggest-todos。
 * `force` なしは「まだ作っていなければ作る」（初めて編集画面を開いたとき）。作成済みならそのまま返す。
 * `force: true` は作り直す（「再生成」）。追加済みのToDoの候補は残し、同じ件名の候補は出さない。
 */
export const SuggestMeetingTodosSchema = z.object({
  force: z.boolean().optional(),
});
export type SuggestMeetingTodosRequest = z.infer<typeof SuggestMeetingTodosSchema>;

export interface SuggestMeetingTodosResponse {
  success: boolean;
  suggestedTodos: SuggestedTodo[];
  suggestedTodosGeneratedAt?: string;
  /** 候補の元にした議事録の名前（無ければ商談記録だけから作った）。 */
  sourceMinutesTitles: string[];
}

/** POST /meetings/{meetingId}/todos。候補をToDoにする。ToDoは商談記録を作った人のものとして作る。 */
export const CreateMeetingTodoSchema = z.object({
  title: z.string().min(1, "タイトルは必須です").max(200, "タイトルは200文字以内です"),
  dueAt: z.string().nullable().optional(),
  /** 元の候補の id（SuggestedTodo.id）。商談のToDoは候補からだけ作る（手で足す経路は無い） */
  sourceActionId: z.string().min(1).max(64),
});
export type CreateMeetingTodoRequest = z.infer<typeof CreateMeetingTodoSchema>;

/** PATCH /meetings/{meetingId}/todos/{todoId}。商談の画面から直せるのは件名と期限だけ（完了は持ち主が PATCH /todos で）。 */
export const UpdateMeetingTodoSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  dueAt: z.string().nullable().optional(),
});
export type UpdateMeetingTodoRequest = z.infer<typeof UpdateMeetingTodoSchema>;

/** GET /meetings/{meetingId}/todos。商談記録を見られる人に、持ち主（記録者）のこの商談のToDoを返す。 */
export interface MeetingTodosResponse {
  success: boolean;
  /** ToDoの持ち主（商談記録を作った人）の userId */
  ownerId: string;
  todos: TodoItem[];
}

// ============================================================
// 自分の予定（PRODUCT_DEV-782・手帳の時間割）
// ============================================================

/**
 * 自分だけで完結する予定（相手のいる日程調整とは別物）。
 *
 * PK: TENANT#{tenantId}#USER#{userId}（本人だけが読み書きする）
 * SK: PLAN#{date}#{planId}（日付の範囲で Query する。同じ日の中の並びは画面側で時刻順に直す）
 *
 * ⚠ 日付と時刻は **JST の壁時計の文字列**で持つ（"YYYY-MM-DD" / "HH:mm"）。ISO に直すと
 * 終日の予定が UTC で前日に落ちる。
 */
export interface DDBSelfPlan {
  PK: string;
  SK: string;
  GSI1PK: string; // TENANT#{tenantId}#USER#{userId}
  GSI1SK: string; // PLAN#{planId}（1件取得用）
  planId: string;
  tenantId: string;
  userId: string;
  title: string;
  date: string;
  allDay: boolean;
  /** 終日なら持たない。 */
  startTime?: string;
  endTime?: string;
  note?: string;
  /**
   * 会議・商談の予定か（PRODUCT_DEV-1017）。true なら手帳の行が日程調整の予定と同じく
   * 時間で「会議録音 → 記録作成 → 記録を開く」に替わる。無い（古い予定）は false と同じ。
   */
  isMeeting?: boolean;
  /**
   * PRODUCT_DEV-1024: 商談記録のプリセット（任意）。会議・商談の予定（`isMeeting`）でだけ持つ。
   * この予定から商談記録を作るとき、最初から選ばれている（自動作成は PRODUCT_DEV-1046）。
   * 無い・会議・商談でない予定では属性ごと無い。
   */
  presetId?: string;
  /**
   * PRODUCT_DEV-1030: この予定から作る記録の公開範囲（会議・商談の予定のときだけ意味を持つ）。
   * 予定そのものは本人だけが読むので、ここは「記録を作るときの初期値」。
   */
  recordVisibility?: RecordVisibility;
  recordSharedWith?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
}

const SELF_PLAN_DATE = /^\d{4}-\d{2}-\d{2}$/;
const SELF_PLAN_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * 作成・更新の本文。**更新も全項目を送る**（モーダルは常に全項目を持っているので、
 * 部分更新にすると「終日に変えたのに時刻が残る」の組み合わせを作れてしまう）。
 */
export const SelfPlanBodySchema = z
  .object({
    title: z.string().trim().min(1, "タイトルは必須です").max(120),
    date: z.string().regex(SELF_PLAN_DATE),
    allDay: z.boolean().default(false),
    startTime: z.string().regex(SELF_PLAN_TIME).optional(),
    endTime: z.string().regex(SELF_PLAN_TIME).optional(),
    note: z.string().max(2000).optional(),
    // PRODUCT_DEV-1017: 送らない古い画面は「会議・商談ではない」として扱う
    isMeeting: z.boolean().default(false),
    // PRODUCT_DEV-1024: 商談記録のプリセット（任意）。送らない古い画面は「選ばない」。空文字も「選ばない」
    presetId: z.string().max(128).optional(),
    // PRODUCT_DEV-1030: 作る記録の公開範囲。送らない古い画面は、作成なら公開・更新なら今の値のまま
    // （更新で公開に戻すときは "public" を明示して送る）
    recordVisibility: RecordVisibilitySchema.optional(),
    recordSharedWith: SharedWithSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.allDay) return;
    if (!v.startTime || !v.endTime) {
      ctx.addIssue({ code: "custom", message: "開始と終了の時刻が必要です" });
      return;
    }
    // "HH:mm" 同士は文字列比較で前後が決まる
    if (v.endTime <= v.startTime) {
      ctx.addIssue({ code: "custom", message: "終了は開始より後にしてください" });
    }
  })
  // 終日なら時刻は捨てる（残すと、あとで時間指定に戻したときに古い時刻が出てくる）
  .transform((v) =>
    v.allDay ? { ...v, startTime: undefined, endTime: undefined } : v,
  )
  // PRODUCT_DEV-1024: プリセットは会議・商談の予定でだけ持つ（スイッチを切ったら捨てる。空文字も捨てる）
  .transform((v) => ({
    ...v,
    presetId: v.isMeeting && v.presetId ? v.presetId : undefined,
  }));
export type SelfPlanBody = z.infer<typeof SelfPlanBodySchema>;

/** 一覧で一度に取れる日数の上限（月カレンダーの6週ぶん＋余裕）。 */
export const SELF_PLAN_LIST_MAX_DAYS = 62;

export interface SelfPlanItem {
  planId: string;
  title: string;
  date: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  note?: string;
  /** 会議・商談の予定か（PRODUCT_DEV-1017）。古いサーバは返さない（false と同じ）。 */
  isMeeting?: boolean;
  /** 商談記録のプリセット（PRODUCT_DEV-1024）。会議・商談の予定で選んだときだけ。 */
  presetId?: string;
  /** PRODUCT_DEV-1030: この予定から作る記録の公開範囲（未設定は公開）。 */
  recordVisibility?: RecordVisibility;
  recordSharedWith?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SelfPlansResponse {
  success: boolean;
  plans: SelfPlanItem[];
}

export interface SelfPlanResponse {
  success: boolean;
  plan: SelfPlanItem;
}

export interface DeleteSelfPlanResponse {
  success: boolean;
  planId: string;
}

// ============================================================
// PRODUCT_DEV-424 Phase 2: 音声メモ（録音 + 文字起こし）
// ============================================================

/**
 * POST /memos/audio-upload-url リクエスト
 * クライアント側で録音した音声の MIME を指定。
 */
export const MemoAudioUploadUrlSchema = z.object({
  contentType: z.string().optional(), // 例: "audio/webm", "audio/mp4"
  extension: z.string().optional(), // 例: "webm", "m4a"
  // PRODUCT_DEV-782: 手帳の自分の予定から録るとき。タイトル・日付の控えはサーバが予定から引く
  selfPlanId: z.string().min(1).max(64).optional(),
  // PRODUCT_DEV-1270: 送り直し。前の送信で作った音声メモ（まだ音声を待っているもの）に音声を入れ直す。
  // 本人のメモで文字起こし前のときだけ使い、そうでなければ 409（画面は新しく作り直す）
  memoId: z.string().min(1).max(64).optional(),
});
export type MemoAudioUploadUrlRequest = z.infer<typeof MemoAudioUploadUrlSchema>;

export interface MemoAudioUploadUrlResponse {
  success: boolean;
  memoId: string;
  uploadUrl: string;
  s3Key: string;
}

export interface MemoTranscribeResponse {
  success: boolean;
  memoId: string;
  status: "transcribing";
}

/**
 * SQS メッセージ（memo-transcribe-worker 用）
 * speed-recording のメッセージとは別 Queue / 別 worker で処理する。
 */
export interface MemoTranscriptionProcessorMessage {
  tenantId: string;
  userId: string;
  memoId: string;
  s3Key: string;
  contentType?: string;
  // PRODUCT_DEV-459: 文字起こし起動時刻 (ISO)。worker で処理時間(elapsed)を算出し、
  // 一定時間以上かかった完成のみ通知するための閾値判定に使う。後方互換のため optional。
  enqueuedAt?: string;
  // PRODUCT_DEV-485: クライアント計測の録音秒数。worker が durationSec に採用（未指定は API duration）。
  durationSec?: number;
  // PRODUCT_DEV-1085: 受け付けたときにフラグ transcribeGptTranscribe が ON だったか。無いメッセージは今のモデル。
  transcribeNext?: boolean;
}

// ===========================================================================
// PRODUCT_DEV-583: マイ顧客 (My Customers)
// 顧客マスタ(DDBCompany)を「自分の担当顧客」として一覧/詳細/編集/引き継ぎ/ブックマーク/
// 録音起点プレフィルするための request/response 契約。すべて additive・後方互換
// (既存スキーマは非改変)。web(apps/unizo/web)がこれらを import する。
// 一覧アイテムの導出ロジックは apps/unizo/api/lib/myCustomers/derive.ts が正本で、
// 下記 MyCustomerListItemSchema はその構造を web 向けにミラーしたもの。
// ===========================================================================

// --- 共通 enum ---
// PRODUCT_DEV-917: dept = いまの部署で記録された商談がある顧客（`?deptId=` と一緒に送る）。
export const MyCustomersScopeSchema = z.enum(["mine", "dept", "all"]);
export type MyCustomersScopeContract = z.infer<typeof MyCustomersScopeSchema>;
export const MyCustomersSortSchema = z.enum(["recent", "oldest", "dealCount"]);
export type MyCustomersSortContract = z.infer<typeof MyCustomersSortSchema>;

// --- tenant-users GET: サブ担当/引き継ぎ先ピッカー用のアクティブなテナントメンバー ---
export const TenantUserSummarySchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(TENANT_ROLES),
});
export type TenantUserSummary = z.infer<typeof TenantUserSummarySchema>;
export const TenantUsersResponseSchema = z.object({
  users: z.array(TenantUserSummarySchema),
});
export type TenantUsersResponse = z.infer<typeof TenantUsersResponseSchema>;

// --- PRODUCT_DEV-583(R3): マイ顧客メンバーシップ（per-user・商談ブックマーク型テーブル UnizoMyCustomers） ---
// PK=TENANT#{tid}#USER#{uid}, SK=CUSTOMER#{companyId}。1行が auto(reviewed 記録による自動登録) と
// bookmarked(手動保存) の両フラグを持つ。両方 false なら行は削除する（存在＝どちらか true）。
// 主担当(ownerId)/サブ担当(subAssigneeIds) は本テーブル導入により廃止（DDBCompany のフィールドは
// 後方互換のため残置・不使用）。「マイ顧客」= 自分の membership 行に存在する顧客。
export const DDBMyCustomerSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // CUSTOMER#<companyId>
  tenantId: z.string(),
  userId: z.string(),
  companyId: z.string(),
  auto: z.boolean(), // reviewed 記録で自動登録された
  bookmarked: z.boolean(), // 手動で保存(ブックマーク)された
  companyName: z.string().optional(), // 非正規化スナップショット（一覧の軽量表示用）
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBMyCustomer = z.infer<typeof DDBMyCustomerSchema>;

// --- my-customers GET (一覧) ---
export const MyCustomerListItemSchema = z.object({
  companyId: z.string(),
  name: z.string(),
  nameKana: z.string(),
  type: z.enum(["corporate", "individual"]),
  sub: z.string(),
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  subAssigneeIds: z.array(z.string()),
  // PRODUCT_DEV-583(R3): membership フラグ。auto=自分が記録した(自動登録) / bookmarked=手動保存。
  auto: z.boolean(),
  bookmarked: z.boolean(),
  dealCount: z.number(),
  lastMeetingAt: z.string().nullable(),
  lastMeetingBy: z.string().optional(),
  lastMeetingByName: z.string().optional(),
  lastMeetingTitle: z.string().optional(),
  lastPresetId: z.string().optional(),
  lastRecordingMode: z.enum(["dialogue", "speed"]).optional(),
  isOwner: z.boolean(),
  // PRODUCT_DEV-606: 顧客(会社)の登録日時。「顧客登録順」ソート用（DDBCompany.createdAt 由来）。
  // 後方互換のため optional（既存の永続データ/消費者を壊さない）。
  createdAt: z.string().datetime().optional(),
  // PRODUCT_DEV-1239: 顧客番号（DDBCompany.systemIntegrationId）。顧客一覧の番号検索用。
  // 2つのフラグ（companyExternalSystemId・customerNumberLabel）が ON のテナントにだけ載せる。後方互換のため optional。
  systemIntegrationId: z.string().optional(),
});
export type MyCustomerListItemContract = z.infer<typeof MyCustomerListItemSchema>;
export const MyCustomerListResponseSchema = z.object({
  customers: z.array(MyCustomerListItemSchema),
});
export type MyCustomerListResponse = z.infer<typeof MyCustomerListResponseSchema>;

// --- my-customers/[companyId] GET (詳細) ---
export const MyCustomerDealSchema = z.object({
  id: z.string(),
  title: z.string(),
  date: z.string(), // "YYYY-MM-DD"
  by: z.string(), // 記録者 userId
  byName: z.string().optional(),
  // PRODUCT_DEV-721 U5: 記録した時点の部署。画面の「編集できるか」の事前判定に使う（未設定の商談には載せない）
  deptId: z.string().optional(),
  presetId: z.string().optional(),
  reviewed: z.boolean(),
  // PRODUCT_DEV-1214: 入っている案件フォルダ（商談グループ）。顧客詳細の「この顧客の案件フォルダ」の件数・最後の日に使う
  groupId: z.string().optional(),
  // PRODUCT_DEV-1265: 入っている全部の案件フォルダ
  groupIds: z.array(z.string()).optional(),
});
export type MyCustomerDeal = z.infer<typeof MyCustomerDealSchema>;
export const SubAssigneeSummarySchema = z.object({
  userId: z.string(),
  name: z.string(),
});
export type SubAssigneeSummary = z.infer<typeof SubAssigneeSummarySchema>;
export const MyCustomerDetailSchema = DDBCompanySchema.extend({
  dealCount: z.number(),
  deals: z.array(MyCustomerDealSchema),
  canEdit: z.boolean(),
  ownerName: z.string().optional(),
  subAssignees: z.array(SubAssigneeSummarySchema),
  // PRODUCT_DEV-583: 呼び出しユーザーがこの顧客をブックマーク済みか（詳細の初期表示用）。
  bookmarked: z.boolean(),
  // PRODUCT_DEV-909: 無効化（論理削除）した顧客のときだけ true。返すのは管理者にだけ
  // （管理者以外には無効化した顧客の詳細は 404）。省略は有効な顧客（後方互換の追加）。
  inactive: z.boolean().optional(),
});
export type MyCustomerDetail = z.infer<typeof MyCustomerDetailSchema>;
export const MyCustomerDetailResponseSchema = z.object({
  customer: MyCustomerDetailSchema,
});
export type MyCustomerDetailResponse = z.infer<
  typeof MyCustomerDetailResponseSchema
>;

// --- my-customers/[companyId] PATCH (フル編集) ---
// PRODUCT_DEV-583(R3): 主担当/サブ担当は廃止。編集はフラグ有効テナントの認証ユーザーなら可。
// ownerId/subAssigneeIds は書き込ませない（許可フィールドのみのホワイトリスト）。
// useApiPatch が tenantId を注入するが非 strict schema が strip する。
export const UpdateMyCustomerRequestSchema = z.object({
  name: z.string().optional(),
  nameKana: z.string().optional(),
  aliases: z.array(z.string().min(1)).optional(),
  type: z.enum(["corporate", "individual"]).optional(),
  location: z.string().optional(),
  // PRODUCT_DEV-875: 人が書き換える入口なのでメールアドレスの書式も見る
  contacts: z.array(EditableContactSchema).optional(),
  systemIntegrationId: z.string().optional(),
  gbizCorporateNumber: z.string().optional(),
  individualProfile: IndividualProfileSchema.optional(),
});
export type UpdateMyCustomerRequest = z.infer<
  typeof UpdateMyCustomerRequestSchema
>;

// --- my-customers/[companyId]/sub-assignees POST ---
export const AddSubAssigneeRequestSchema = z.object({
  userId: z.string().min(1),
});
export type AddSubAssigneeRequest = z.infer<typeof AddSubAssigneeRequestSchema>;

// --- my-customers/[companyId]/owner POST (主担当の割り当て/付け替え) ---
// PRODUCT_DEV-583: 未所有(ownerId 未設定)なら任意の在籍メンバーを主担当に設定可、
// 所有済みは現主担当または admin のみ変更可（認可はハンドラで判定）。
export const AssignOwnerRequestSchema = z.object({
  ownerId: z.string().min(1),
});
export type AssignOwnerRequest = z.infer<typeof AssignOwnerRequestSchema>;

// --- my-customers/[companyId]/bookmark POST/DELETE ---
export const BookmarkResponseSchema = z.object({
  companyIds: z.array(z.string()),
});
export type BookmarkResponse = z.infer<typeof BookmarkResponseSchema>;

// --- my-customers/[companyId]/recording-context GET (録音起点プレフィル) ---
export const RecordingContextResponseSchema = z.object({
  company: z.object({
    companyId: z.string(),
    name: z.string(),
    type: z.enum(["corporate", "individual"]),
    contacts: z.array(ContactSchema),
    // PRODUCT_DEV-655(第2弾): 個人顧客の既知情報を録音開始時に引き継ぐための追加。
    // 顧客詳細から記録を始めたのに氏名から聞き直される問題の解消に使う（後方互換の optional）。
    nameKana: z.string().optional(),
    individualProfile: IndividualProfileSchema.optional(),
  }),
  lastPresetId: z.string().optional(),
  lastRecordingMode: z.enum(["dialogue", "speed"]).optional(),
});
export type RecordingContextResponse = z.infer<
  typeof RecordingContextResponseSchema
>;

// --- my-customers/handover POST (主担当の一括引き継ぎ) ---
export const HandoverRequestSchema = z.object({
  targetUserId: z.string().min(1),
  // 未指定なら「自分が主担当の全顧客」を対象にする。
  companyIds: z.array(z.string()).optional(),
  // true=引き継ぎ後も元主担当をサブ担当として残す。
  keepAsSub: z.boolean(),
});
export type HandoverRequest = z.infer<typeof HandoverRequestSchema>;
export const HandoverResponseSchema = z.object({
  transferred: z.array(z.string()), // 引き継いだ companyId
  skipped: z.array(z.string()), // 主担当不一致等でスキップした companyId
});
export type HandoverResponse = z.infer<typeof HandoverResponseSchema>;

// --- my-customers/today-copy GET (本日レビュー済み商談のコピー文集約) ---
export const TodayCopyResponseSchema = z.object({
  text: z.string(),
});
export type TodayCopyResponse = z.infer<typeof TodayCopyResponseSchema>;
// ============================================================
// PRODUCT_DEV-586 議事録（録音タブ / 長尺プログレッシブ文字起こし）
// ============================================================
// メモ(DDBMemo)・商談記録(DDBMeeting)とは別の独立レコード種別。
// 60分級の録音を無音境界チャンクで分割し、チャンク毎に文字起こし→順序結合→
// 議事録専用の固定フィールド構造化(AI要約/決定事項/Nextアクション)を 1 回だけ実行する。
// 全文(文字起こし)は S3 に保存し、DDB はポインタ(transcriptS3Key)のみ保持する。
// 議事録は全テナントに100%解放済み(PRODUCT_DEV-614 でフラグ撤去)。

/**
 * Nextアクション行（議事録から抽出 or 手動追加。ToDo へ採択可能）
 */
/** PRODUCT_DEV-1256: Nextアクションを誰がやるか */
export const MINUTES_ACTION_SIDES = ["ours", "theirs", "unknown"] as const;
export const MinutesActionSideSchema = z.enum(MINUTES_ACTION_SIDES);
export type MinutesActionSide = z.infer<typeof MinutesActionSideSchema>;

export const MinutesActionSchema = z.object({
  id: z.string(),
  text: z.string().min(1).max(1000),
  due: z.string().nullable().optional(), // ISO8601 or "YYYY/MM/DD"、null=期限なし
  owner: z.string().max(100).optional(), // 担当（任意）
  /**
   * PRODUCT_DEV-1256: 誰がやるか。AI が議事録を作るときに書き出す（手で足した行・古い議事録には無い）。
   * ours = 記録している人の側（社内の会議なら全員）/ theirs = お客さま側 / unknown = 読み取れない。
   * 画面はこれと owner・due から「あなたのToDo候補」の印を付ける
   */
  side: MinutesActionSideSchema.optional(),
  done: z.boolean().default(false),
});
export type MinutesAction = z.infer<typeof MinutesActionSchema>;

/** 議事録の処理ステータス（アップロード開始以降をサーバが管理） */
export type MinutesProcessStatus =
  | "uploading" // シェル作成〜チャンクアップロード中
  | "transcribing" // チャンク文字起こし進行中
  | "structuring" // 全文結合後の構造化(要約/決定事項/Next)実行中
  | "ready" // 完了
  | "failed";

/**
 * PRODUCT_DEV-1045: 議事録ができるのを待っている商談記録（`DDBMeetingMinutes.pendingMeeting`）。
 * 商談記録の側には `awaitingMinutesId` で同じつながりを持たせる。
 */
export const MinutesPendingMeetingSchema = z.object({
  meetingId: z.string().min(1).max(64),
  presetId: z.string().min(1).max(128),
});
export type MinutesPendingMeeting = z.infer<typeof MinutesPendingMeetingSchema>;

/**
 * DynamoDB 議事録型
 * PK: TENANT#{tenantId}#USER#{userId}（メモ同様に per-user 物理強制）
 * SK: MINUTES#{createdAt}#{minutesId}（新着降順 Query）
 */
export interface DDBMeetingMinutes {
  PK: string; // TENANT#{tenantId}#USER#{userId}
  SK: string; // MINUTES#{createdAt}#{minutesId}
  GSI1PK: string; // TENANT#{tenantId}（owner 非依存。GSI_MinutesById）
  GSI1SK: string; // MINUTES#{minutesId} （1件取得用）
  /**
   * PRODUCT_DEV-612: GSI_MinutesByMeeting（商談→議事録の逆引き）。**sparse index**。
   * `meetingId` が設定されているアイテムにだけ書き、解除時は属性ごと REMOVE する
   * （空文字を入れるとゴミキーで索引に残るため）。
   */
  GSI2PK?: string; // TENANT#{tenantId}
  GSI2SK?: string; // MEETING#{meetingId}#{minutesId}
  minutesId: string;
  tenantId: string;
  userId: string;
  /**
   * PRODUCT_DEV-996: 録音したときに選んでいた部（商談記録の `deptId` と同じ決め方。
   * 本人が所属している部だけ入る）。無い議事録は作成者の既定の部で数える（古い商談記録と同じ）。
   */
  deptId?: string;

  // --- メタ / 一覧表示 ---
  title: string; // AI 生成 or 手動編集
  date: string; // "YYYY-MM-DD"（会議日、ユーザ編集可）
  time?: string; // "HH:mm:ss"
  durationSec?: number; // 録音長（最大 ~3600）

  // --- 構造化フィールド（固定契約。preset 駆動ではない） ---
  summary?: string; // AI要約（録音 5 分以上で自動生成）
  decisions?: string[]; // 決定事項
  nextActions?: MinutesAction[]; // Nextアクション
  attendees?: string; // 出席者（任意・手動入力のフリーテキスト）
  companyId?: string; // 関連企業（企業マスタ紐付け、単一）
  meetingId?: string; // 関連商談（任意）
  /**
   * PRODUCT_DEV-1266: 入っている案件フォルダ（商談記録の `groupIds` と同じ持ち方。1件目の `groupId` は持たない）。
   * 関連商談と結びついた議事録は、商談記録のフォルダと合わせて読む（画面の `pairGroupIds`）。
   */
  groupIds?: string[];
  /**
   * PRODUCT_DEV-796: この議事録を録った（または後からつないだ）日程調整の scheduleId。
   * `null` は「つないでいたが外した」（未設定と同じく「つながりなし」。保存は属性ごと消す）。
   * ⚠ 持ち主の照合をしていない値（商談記録の `scheduleId` の注意書きを参照）。
   */
  scheduleId?: string | null;
  /**
   * PRODUCT_DEV-1017: 手帳の自分の予定（会議・商談）の行から録ったときの、その予定。作成時にだけ付く。
   * ⚠ 持ち主を照合していない値（`DDBMeetingSchema.selfPlanId` と同じ）。
   */
  selfPlanId?: string;
  /**
   * PRODUCT_DEV-1225: 外部カレンダーの予定から録ったときの、その予定のキー。作成時にだけ付く。
   * ⚠ 持ち主を照合していない値（`selfPlanId` と同じ）。
   */
  externalEventKey?: string;
  /**
   * PRODUCT_DEV-1030: 公開範囲（未設定は公開）。判定は `canViewRecord`（持ち主は `userId`）。
   * 変えられるのは本人だけで、PATCH で**単独で**送る。
   */
  visibility?: RecordVisibility;
  /** PRODUCT_DEV-1030: 限定公開で共有する人の userId（限定公開のときだけ持つ）。 */
  sharedWith?: string[];
  /**
   * PRODUCT_DEV-796: 「次回アポ」の行から録音したとき、その次回アポが書かれていた**過去の**商談の ID。
   * 今日の予定で「議事録あり」を出すための鍵（会社が分からない次回アポでも効く）。
   *
   * ⚠ **`meetingId` とは別物。** `meetingId` は「この議事録から作った／つないだ商談記録」
   * （GSI_MinutesByMeeting の鍵）で、こちらは「録音のきっかけになった前回の商談」。
   * 索引（GSI）には載せない。後から付け替える API も無い（録音開始時にだけ入る）。
   */
  sourceMeetingId?: string;
  /**
   * PRODUCT_DEV-1045: 議事録の作成中に「商談記録も作る」を選んだときの、先に作っておいた商談記録。
   * 議事録ができたら minutes-finalize-worker がこの商談記録に議事録の中身を渡して仕上げさせ、属性ごと消す。
   * 同じ商談記録を `meetingId` にもつなぐ（つながりの表示・逆引きはそちらが正本）。
   * 置く・外すのは持ち主だけで、作成中の議事録にだけ置ける（PATCH で**単独で**送る）。
   */
  pendingMeeting?: MinutesPendingMeeting;

  // --- 全文ポインタ（S3、インライン保存しない） ---
  transcriptS3Key?: string; // minutes/{tenantId}/{minutesId}/transcript.txt
  transcriptChars?: number; // 全文の文字数（プレビュー/メトリクス用）
  // PRODUCT_DEV-603: クリーン化(Gatekeeper)実行時のみ保存する生(未クリーン)全文ポインタ。
  // minutes-raw/{tenantId}/{minutesId}/transcript.txt（S3で30日失効）。監査用・admin限定閲覧。
  // 未設定 = クリーン化していない（本文が生そのもの）→ 生データ導線は出さない。
  rawTranscriptS3Key?: string;
  /**
   * PRODUCT_DEV-1248: 英語の発言を訳して作った議事録（フラグ minutesJaEnMixed が ON のテナントで、
   * 訳が1つでもあった会議だけ finalize が付ける）。詳細の上に案内を出す鍵
   */
  languageMode?: MinutesLanguageMode;
  /**
   * PRODUCT_DEV-1248: 訳つき全文（`MinutesTranscriptSegment[]` の JSON）の S3 キー。
   * minutes/{tenantId}/{minutesId}/transcript.ja-en.json。本文（transcriptS3Key）は原文のまま。
   * 空文字は「無し」（作り直しで訳つき全文を作れなかった）
   */
  translatedTranscriptS3Key?: string;

  // --- プログレッシブ・パイプライン進捗 ---
  processStatus: MinutesProcessStatus;
  expectedChunks?: number; // finalize 時に確定するチャンク総数
  chunksDone?: number; // 文字起こし済チャンク数（progress 用）
  failureReason?: string;
  // 録音中にクライアントが定期送信する生存signal（ISO8601）。これが途絶えた uploading は
  // 「クライアントが死んで finalize を呼べなかった」とみなし minutes-sweeper が回収する。
  heartbeatAt?: string;
  // PRODUCT_DEV-1087: 録音中の着信で一時停止した時刻（ISO8601）。再開したら消す。
  // 立っている間は minutes-sweeper が確定するまでの待ちを延ばす（通話中は heartbeat が止まるため）。
  pausedAt?: string;

  // --- 監査 / 論理削除（メモ同一規約） ---
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  /**
   * PRODUCT_DEV-1270: AI が要約・決定事項などを書いたときの version。今の version と同じなら
   * そのあと人が直していない（作り直しで AI の項目を入れ替えてよい）
   */
  structuredVersion?: number;
  /** PRODUCT_DEV-1270: 端末が送れなかった区切りを残している（音声が届いていなくても消さない） */
  deviceKeepsChunks?: boolean;
  /** PRODUCT_DEV-1270: 全文を組んだときの区切りの数（同じ数での作り直しを飛ばす） */
  structuredChunks?: number;
  status?: "active" | "trashed";
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number; // trashed 時のみ epoch 秒（アクティブ時は未設定 = 永続）
}

/** 処理中（アップロード〜構造化）の状態。PRODUCT_DEV-1045: サーバの条件付き保存もこれを使う。 */
export const MINUTES_IN_PROGRESS_STATUSES = [
  "uploading",
  "transcribing",
  "structuring",
] as const satisfies readonly MinutesProcessStatus[];

/** 処理中（アップロード〜構造化）判定。card/list/detail で共有し状態集合の重複を防ぐ。 */
export const isMinutesInProgress = (s: MinutesProcessStatus): boolean =>
  (MINUTES_IN_PROGRESS_STATUSES as readonly MinutesProcessStatus[]).includes(s);

// 議事録更新（詳細画面での手動編集）
export const UpdateMinutesSchema = z.object({
  title: z.string().max(120).nullable().optional(),
  summary: z.string().max(20000).nullable().optional(),
  decisions: z.array(z.string().max(2000)).optional(),
  nextActions: z.array(MinutesActionSchema).optional(),
  attendees: z.string().max(2000).nullable().optional(),
  companyId: z.string().nullable().optional(),
  meetingId: z.string().nullable().optional(),
  date: z.string().optional(),
  /**
   * PRODUCT_DEV-796: 日程調整へ後からつなぐ（文字列）・外す（null）。
   * つなぐのは議事録の持ち主だけ（外すのは管理者も可）。null は属性ごと消す。
   * ⚠ **単独で送る。** ほかの項目と一緒に送ると 400（下書きの保存と別の、version を
   * 条件にしない保存にするため）。
   */
  scheduleId: z.string().min(1).max(64).nullable().optional(),
  /**
   * PRODUCT_DEV-1030: 公開範囲の変更。本人だけ。
   * ⚠ **単独で送る**（`scheduleId` と同じく、ほかの項目と一緒なら 400）。
   */
  visibility: RecordVisibilitySchema.optional(),
  sharedWith: SharedWithSchema.optional(),
  /**
   * PRODUCT_DEV-1045: 作成中の議事録に、できたら仕上げる商談記録を置く（null は取り消す）。
   * 持ち主だけ。作成中でない・すでに置いてある（置くとき）／置いていない（取り消すとき）は 409。
   * ⚠ **単独で送る**（`scheduleId` と同じく、ほかの項目と一緒なら 400）。
   */
  pendingMeeting: MinutesPendingMeetingSchema.nullable().optional(),
  /**
   * PRODUCT_DEV-1266: 案件フォルダに入れる（add）・外す（remove）。商談記録の `batch-move` の add / remove と同じ。
   * 見えている人なら誰でもできる（商談記録のフォルダの出し入れと同じ）。
   * ⚠ **単独で送る**（`scheduleId` と同じく、ほかの項目と一緒なら 400。下書きの version を進めない）。
   */
  groupChange: z
    .object({ mode: z.enum(["add", "remove"]), groupId: z.string().min(1).max(64) })
    .optional(),
});
export type UpdateMinutesRequest = z.infer<typeof UpdateMinutesSchema>;

// 一覧アイテム（全文・構造化詳細は含めない軽量表現）
export interface MinutesListItem {
  minutesId: string;
  title: string;
  date: string;
  time?: string;
  durationSec?: number;
  processStatus: MinutesProcessStatus;
  summary?: string;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * 作成者の userId。PRODUCT_DEV-996 で議事録を商談記録と同じく会社内の全員が読めるように
   * したので、**全員に返す**（以前は PRODUCT_DEV-506 で管理者向けだけ）。
   * ⚠ 「createdBy が無い＝自分の」と読んでいた箇所は、PRODUCT_DEV-996 で作成者の照合へ直した。
   */
  createdBy?: string;
  /** PRODUCT_DEV-996: 録音したときの部（`DDBMeetingMinutes.deptId`）。 */
  deptId?: string;
  /**
   * PRODUCT_DEV-996: 閲覧者がこの議事録を編集・削除できるか（作成者本人 or 管理者）。
   * 会社内の全員が読めるようになったので、他人の議事録を閲覧のみで出すために使う。
   */
  canEdit?: boolean;
  /** PRODUCT_DEV-612: 関連商談（未紐づけなら undefined）。 */
  meetingId?: string;
  /** PRODUCT_DEV-1266: 入っている案件フォルダ（どこにも入っていなければ undefined）。 */
  groupIds?: string[];
  /** PRODUCT_DEV-796: 録った／つないだ日程調整（未紐づけなら undefined）。 */
  scheduleId?: string;
  /** PRODUCT_DEV-796: 次回アポの行から録ったときの元商談 ID。 */
  sourceMeetingId?: string;
  /**
   * PRODUCT_DEV-1017: 自分の予定の行から録ったときの、その予定。`scheduleId` と同じく中身の無い
   * つなぎの鍵として全員に返す（商談記録・軽量索引も同じ）。予定の中身は自分の予定の API が本人に閉じている。
   * 突き合わせる側は本人の議事録だけを使う（`onlyOwnMinutes`）。
   */
  selfPlanId?: string;
  /**
   * PRODUCT_DEV-1225: 外部カレンダーの予定から録ったときの、その予定のキー。⚠ 持ち主を照合していない値
   * （`selfPlanId` と同じ）。突き合わせる側は本人の議事録だけを使う。予定の中身は保存していない。
   */
  externalEventKey?: string;
  /** PRODUCT_DEV-1030: 公開範囲（未設定は公開）。 */
  visibility?: RecordVisibility;
  /** PRODUCT_DEV-1030: 限定公開で共有する人（限定公開のときだけ）。 */
  sharedWith?: string[];
  /**
   * PRODUCT_DEV-612: 閲覧者がこの議事録の詳細（`/minutes/{id}`）を開けるか。
   * `GET /minutes?meetingId=` のリンクの出し分けに使う。
   * PRODUCT_DEV-996 で会社内の全員が詳細を読めるようになったので、常に true。
   */
  canOpen?: boolean;
}

export interface MinutesListResponse {
  success: boolean;
  minutes: MinutesListItem[];
}

// 詳細（構造化フィールドを含む。全文は transcript-url 経由の署名 GET で別取得）
export interface MinutesDetail extends MinutesListItem {
  /**
   * 失敗の理由 (PRODUCT_DEV-805)。**`processStatus === "failed"` のときだけ入る。**
   *
   * 画面は「議事録の作成に失敗しました」と一律に出すのではなく、これがあればこれを出す。
   * 音声をほとんど拾えなかったのか、構造化で落ちたのかで、本人が次に取る行動が違う。
   */
  failureReason?: string;
  decisions?: string[];
  nextActions?: MinutesAction[];
  attendees?: string;
  meetingId?: string;
  // transcriptS3Key の有無。PRODUCT_DEV-996: 全文を読める人（作成者本人 or records.readAll）
  // にだけ実値を返し、それ以外には常に false（transcriptChars も返さない）。
  transcriptAvailable: boolean;
  transcriptChars?: number;
  // PRODUCT_DEV-603: 生(未クリーン)全文が保全されているか（rawTranscriptS3Key の有無）。
  // true のときだけ admin 詳細に「生データ（未クリーン）」導線を表示する。
  rawTranscriptAvailable: boolean;
  /**
   * PRODUCT_DEV-1045: 議事録ができたら仕上げる商談記録（作成中に「商談記録も作る」を選んだとき）。
   * 議事録ができて仕上げに回したら無くなる。
   */
  pendingMeeting?: MinutesPendingMeeting;
  /** PRODUCT_DEV-1248: 英語の発言を訳して作った議事録（詳細の上に案内を出す） */
  languageMode?: MinutesLanguageMode;
}

export interface MinutesResponse {
  success: boolean;
  minutes: MinutesDetail;
}

export interface DeleteMinutesResponse {
  success: boolean;
  minutesId: string;
}

/**
 * 録音開始のリクエスト（PRODUCT_DEV-796）。**すべて任意。**
 *
 * マイページ「今日の予定」の行から録音したとき、どの予定のものかを議事録に持たせる。
 * 以前のクライアントは `{ tenantId }` だけを送る（または body 無し）ので、
 * 読めない body は「つながりなし」として扱い、録音開始を失敗させない。
 */
export const MinutesUploadUrlRequestSchema = z.object({
  scheduleId: z.string().min(1).max(64).optional(),
  sourceMeetingId: z.string().min(1).max(64).optional(),
  /** PRODUCT_DEV-1017: 手帳の自分の予定（会議・商談）の行から録ったとき、その予定。 */
  selfPlanId: z.string().regex(SELF_PLAN_ID).optional(),
  /**
   * PRODUCT_DEV-1225: 外部カレンダー（Google / Microsoft 365）の予定から録ったとき、その予定のキー
   * （`<provider>:primary:<eventId>`）。**形が不正でも 400 にしない**（録音を失わせない）。
   */
  externalEventKey: z.string().regex(EXTERNAL_EVENT_KEY_RE).optional().catch(undefined),
  /**
   * PRODUCT_DEV-996: 録音時に選んでいた部。サーバは本人が所属している部のときだけ残す
   * （商談記録の作成と同じ `resolveCreateMeetingDeptId`）。
   */
  deptId: z.string().min(1).max(64).optional(),
  /**
   * PRODUCT_DEV-1030: 非公開の予定から録ったときの公開範囲。不正な値は非公開に倒す
   * （400 にしない＝録音を止めない。黙って公開にもしない。送らなければ公開）。
   */
  visibility: RecordVisibilitySchema.optional().catch("private"),
  sharedWith: SharedWithSchema.optional().catch(undefined),
  /**
   * PRODUCT_DEV-1270: この端末は、送れなかった区切りを残して、つながったら送り直す（フラグ offlineRecordingSave）。
   * 片付け・確定は、音声が届いていなくてもこの議事録を消さずに「失敗」で残す（あとで区切りが届けば作る）
   */
  deviceKeepsChunks: z.boolean().optional().catch(undefined),
});
export type MinutesUploadUrlRequest = z.infer<typeof MinutesUploadUrlRequestSchema>;

// 録音開始のシェル作成レスポンス（チャンクの署名PUTは chunk-upload-url で別取得）
export interface MinutesAudioUploadUrlResponse {
  success: boolean;
  minutesId: string;
}

// 録音音声のコンテナ種別（S3 キー拡張子・presign ContentType の許可値）
export const MinutesAudioExtensionSchema = z.enum(["webm", "m4a"]);
export const MinutesAudioContentTypeSchema = z.enum([
  "audio/webm",
  "audio/mp4",
]);

// チャンク毎の署名 PUT URL（seq = 0 始まりの連番）。extension/contentType は許可値に限定
// （S3 キーに素の文字列を差し込まないための入力バリデーション）。
export const MinutesChunkUploadUrlSchema = z.object({
  seq: z.number().int().min(0),
  contentType: MinutesAudioContentTypeSchema.optional(),
  extension: MinutesAudioExtensionSchema.optional(),
});
export type MinutesChunkUploadUrlRequest = z.infer<typeof MinutesChunkUploadUrlSchema>;

export interface MinutesChunkUploadUrlResponse {
  success: boolean;
  minutesId: string;
  seq: number;
  uploadUrl: string;
  s3Key: string;
}

// 録音終了: チャンク総数を確定し finalize（結合→構造化）を起動
// totalChunks 省略時はサーバが「その時点でアップロード済みのチャンク数」を採用する
// （離脱・中断で総数を数え切れなかったクライアントや、詳細画面の手動確定から呼ばれる）。
export const MinutesFinalizeSchema = z.object({
  totalChunks: z.number().int().min(0).optional(),
  durationSec: z.number().optional(),
  // 録音していたクライアント自身が離脱する（ページ破棄）ときの確定。生きている録音を
  // 他画面から誤って切り詰めないための生存ガードを、この宣言のときだけ免除する。
  abandoned: z.boolean().optional(),
  /**
   * 何も録れていない録音を**破棄する**宣言 (PRODUCT_DEV-787)。
   *
   * 議事録は録音しながら音声を分割送信するので、**開始した瞬間にレコードの器を作る**
   * （送り先の ID が要るため）。商談記録・音声メモは停止して送信するときに作るので、
   * 途中で離れても何も残らない。この差のせいで、議事録だけ「押しただけ」で
   * 空のレコードが残っていた。
   *
   * ⚠ **サーバは自分で条件を検証する。** クライアントが送ってきたからといって消さない
   * （まだ音声が 1 件も届いていない・finalize も走っていない、を必ず確かめる）。
   * 条件を満たさなければ通常の確定として扱い、**録れているものは決して消さない**。
   */
  discard: z.boolean().optional(),
});
export type MinutesFinalizeRequest = z.infer<typeof MinutesFinalizeSchema>;

export interface MinutesFinalizeResponse {
  success: boolean;
  minutesId: string;
  /** 破棄した場合は無い（レコードごと消えているため状態が存在しない）。 */
  processStatus?: MinutesProcessStatus;
  /**
   * 中身が無かったのでレコードごと消した (PRODUCT_DEV-787)。
   * 画面はこれが true のとき、詳細へ遷移せず一覧へ戻る（行き先が存在しない）。
   */
  discarded?: boolean;
}

/**
 * POST /minutes/{id}/heartbeat（PRODUCT_DEV-586 / PRODUCT_DEV-1087）。
 * 本文は任意。古い web は `{ tenantId }` だけ、または本文なしで呼ぶ。
 */
export interface MinutesHeartbeatRequest {
  /** 着信で一時停止した（true）・再開した（false）。省略時は生存の知らせだけ */
  paused?: boolean;
}

export interface MinutesHeartbeatResponse {
  success: boolean;
  minutesId: string;
  /** いまの処理状態。uploading 以外なら、見回りがもう確定させている */
  processStatus?: MinutesProcessStatus;
  /** 議事録がもう無い（見回りが中身なしで消した・ゴミ箱へ移した） */
  gone?: boolean;
}

/** PRODUCT_DEV-1248: 議事録の言語の扱い。"ja-en" = 英語の発言を日本語に訳して含めた */
export type MinutesLanguageMode = "ja-en";

/**
 * PRODUCT_DEV-1248: 文字起こしの1文。英語の文には日本語訳（ja）が付く。日本語の文・訳していない文は ja が null
 */
export interface MinutesTranscriptSegment {
  text: string;
  ja: string | null;
}

// プログレッシブ表示: 録音中/処理中に afterSeq より後の新規チャンクのみ（デルタ）を返す。
// クライアントは text を順次追記し、次回は lastSeq を afterSeq として渡す（O(N²) 回避）。
export interface MinutesTranscriptProgressResponse {
  success: boolean;
  text: string; // afterSeq より後の新規チャンクを順序結合したデルタ
  /**
   * PRODUCT_DEV-1248: デルタに訳のある区切りが1つでもあるときだけ入る、デルタ全体を文に分けたもの
   * （訳の無い区切りは区切りごと1文・ja は null）。無ければ text をそのまま出す
   */
  segments?: MinutesTranscriptSegment[];
  lastSeq: number; // このレスポンスに含まれる最大 seq（無ければ要求時の afterSeq）
  completedChunks: number; // これまでに文字起こし済のチャンク総数
  processStatus: MinutesProcessStatus;
}

// 完了後の全文取得: S3 の署名 GET URL を返す（ブラウザが直接取得）
export interface MinutesTranscriptUrlResponse {
  success: boolean;
  url: string;
  expiresIn: number;
  /** PRODUCT_DEV-1248: 訳つき全文（MinutesTranscriptSegment[] の JSON）の署名 GET URL。訳つき全文があるときだけ */
  translatedUrl?: string;
}

/**
 * SQS メッセージ（minutes-finalize-worker 用）
 * チャンク文字起こしは S3 ObjectCreated → SQS の S3 イベント通知で起動するため
 * 専用メッセージ型は持たない。finalize のみ本メッセージで起動する。
 */
export interface MinutesFinalizeMessage {
  tenantId: string;
  userId: string;
  minutesId: string;
  enqueuedAt?: string;
  /**
   * PRODUCT_DEV-1270: 作り直し。確定したあとに遅れて区切りが届いた（電波が切れていた）ので、
   * 全文を組み直す。商談記録の作成などはもう済んでいるので動かさない
   */
  rebuild?: boolean;
}

// ─── 活動サマリ / 個人・テナント設定 (PRODUCT_DEV-673) ───────────────────────
//
// テーブル: UnizoPreferences（プロダクト×テナント×個人の設定）
//   個人     PK: PRODUCT#{productId}#TENANT#{tenantId}#USER#{userId} / SK: PREF#{key}
//   テナント PK: PRODUCT#{productId}#TENANT#{tenantId}               / SK: TENANTPREF#{key}
//
// 値の解決順は **個人 → テナント既定 → コード既定**。要件の「管理者が設定した値が
// 初期値として配られる」がこの順序で成立する（useTenantFeatureFlag と同じ考え方）。
// PK に productId を含めるのは AgentJobsRepository と同じ作法で、将来 shared-domain へ
// 持ち上げるときにデータ移行を要らなくするため。
//
// 集計の純ロジック（期間解決・集計・推移）は ./activitySummary.ts を正とし、
// ここには API の入出力とDDBアイテムの形だけを置く。

/** 管理者が最後に見ていた UI。個人設定として記録し、次回そこから開く。 */
export const UiModeSchema = z.enum(["personal", "manager"]);
export type UiMode = z.infer<typeof UiModeSchema>;

/**
 * 個人設定の SK 名前空間。キー文字列はここを唯一の正とする。
 *
 * PRODUCT_DEV-698: 日報・週報・月報の配信設定は**個人設定ではなくなった**
 * （`SUMMARY_MAIL_*` を撤去）。誰にいつ出させるかは部の運用であって本人の好みではない、
 * というのが 2026-08-21 の判断。いまここに残るのは `uiMode` だけ。
 * ⚠ 既に保存済みの `PREF#SUMMARY_MAIL_*` アイテムは**消していない**（読まれなくなるだけ）。
 */
export const USER_PREFERENCE_KEYS = {
  uiMode: "UI_MODE",
  // PRODUCT_DEV-699: localStorage に逃がしていた個人設定をサーバへ移す。
  // 端末をまたいで引き継がれず、iOS Safari の ITP（7日間未アクセスで自動削除）等で
  // 黙って消えていた。localStorage は**下書き（キャッシュ）**として残し、正はこちら。
  lastPresetId: "LAST_PRESET_ID",
  presetChosen: "PRESET_CHOSEN",
  recordMode: "RECORD_MODE",
  dockCollapsed: "DOCK_COLLAPSED",
  notificationViewedAt: "NOTIFICATION_VIEWED_AT",
  lastSeenReleaseVersion: "LAST_SEEN_RELEASE_VERSION",
  schedulingHintDismissed: "SCHEDULING_HINT_DISMISSED",
  aiVoiceVolume: "AI_VOICE_VOLUME",
  searchHistory: "SEARCH_HISTORY",
  /**
   * PRODUCT_DEV-826: **自分宛の「部のまとめ」メールを受け取らないか**。
   *
   * 「誰にいつ出させるか」は部の運用なので配信設定は個人に持たせない、という
   * PRODUCT_DEV-698 の判断は変えていない。これは**出させる側の受信の好み**で、
   * 管理者が複数いる部で「自分は要らない」を表現するためだけのもの。
   * ⚠ 本人宛の「確認してください」はここでは止まらない（止められると提出が落ちる）。
   */
  teamReportMailOptOut: "TEAM_REPORT_MAIL_OPT_OUT",
  /**
   * PRODUCT_DEV-917: **いま「どの部署として」見ているか**（兼務している人だけ意味を持つ）。
   *
   * 端末ではなくサーバに置く。端末ごとに違う部署で開くと、記録した先が端末で変わる。
   * ⚠ **所属から外れた部署の id が残りうる**（異動・部署の削除）。読む側は
   * 「いまの所属に無ければ先頭へ落とす」こと。ここで消しに行かない。
   */
  activeDeptId: "ACTIVE_DEPT_ID",
  /**
   * PRODUCT_DEV-1070: **最初に開く画面（HOME）**。手帳を使っている会社（`diaryHome`）だけが意味を持つ。
   * 未設定（null）は、活動サマリを見られる人（マネージャー）なら活動サマリ、それ以外はマイ手帳
   * （PRODUCT_DEV-1092。判定の正本は web の `resolveHome`）。手帳を使っていない会社では読まない（商談一覧のまま）。
   */
  homeScreen: "HOME_SCREEN",
  /**
   * PRODUCT_DEV-783: **初回オンボーディングの進み具合**（どの STEP まで済んだか・ホームの案内を消したか・
   * メニューの「はじめての方へ」を開いたか）。画面に設定項目は出さない、アプリが裏で覚えるだけの値。
   * 端末ではなくサーバに置くのは、スマホで途中まで見て PC で続きから開けるようにするため。
   */
  onboarding: "ONBOARDING",
} as const;

/** 日報・週報・月報の3種。表示順もこの並び。 */
export const REPORT_KINDS = ["daily", "weekly", "monthly"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** 画面にもメールの件名にも出る呼び名。api と web で綴り直さない。 */
export const REPORT_LABEL: Record<ReportKind, string> = {
  daily: "日報",
  weekly: "週報",
  monthly: "月報",
};

/**
 * 配信設定の SK 名前空間（PRODUCT_DEV-698）。
 *
 * **テナントと部署で同じ文字列を使う。** SK の名前空間が `TENANTPREF#` と `DEPTPREF#` で
 * 分かれているので衝突しないし、同じキーにしておくと解決側が「部署 → テナント」を
 * 1つのキーで順に引ける（キーが2種あると、片方だけ綴り間違えても型が通ってしまう）。
 */
export const REPORT_PREFERENCE_KEYS = {
  daily: "REPORT_DAILY",
  weekly: "REPORT_WEEKLY",
  monthly: "REPORT_MONTHLY",
} as const satisfies Record<ReportKind, string>;

/**
 * 「使う種類」の SK 名前空間（PRODUCT_DEV-939）。値のスキーマと既定は
 * `ReportsInUseSchema` / `DEFAULT_REPORTS_IN_USE`（下）にある。
 *
 * ⚠ **配信設定（`REPORT_PREFERENCE_KEYS`）とは別のキー。** あちらは「メールを送るか」で、
 * 止めても下書きは作られ続ける。こちらは**その種類を使うかどうか**。
 *
 * 配信設定と同じく、テナントと部署で同じ文字列を使う（SK の名前空間が `TENANTPREF#` と
 * `DEPTPREF#` で分かれているので衝突しないし、解決側が1つのキーで「部署 → テナント」を順に引ける）。
 */
export const REPORT_IN_USE_PREFERENCE_KEYS = {
  daily: "REPORT_DAILY_IN_USE",
  weekly: "REPORT_WEEKLY_IN_USE",
  monthly: "REPORT_MONTHLY_IN_USE",
} as const satisfies Record<ReportKind, string>;

/** テナント設定の SK 名前空間。 */
export const TENANT_PREFERENCE_KEYS = {
  fiscalMonthCloseDay: "FISCAL_MONTH_CLOSE_DAY",
  businessCalendar: "BUSINESS_CALENDAR",
  fiscalWeekCloseDay: "FISCAL_WEEK_CLOSE_DAY",
  dailyDueOffsetDays: "DAILY_DUE_OFFSET_DAYS",
  weeklyDueOffsetDays: "WEEKLY_DUE_OFFSET_DAYS",
  monthlyDueOffsetDays: "MONTHLY_DUE_OFFSET_DAYS",
  /**
   * 3種で共通だった期限の時刻。**PRODUCT_DEV-826 以降は書かない**が、
   * 種類ごとの時刻が未設定のときのフォールバックとして**読み続ける**。
   * 消すと、これまで 10:00 を設定していたテナントが黙って 9:00 になる。
   */
  submitDueTime: "SUBMIT_DUE_TIME",
  // PRODUCT_DEV-826: 提出期限の時刻を種類ごとに持つ。
  // 「日報の期限は◯日後の◯時」が1行で読めないと、日付と時刻の対応が追えなかった。
  dailyDueTime: "DAILY_DUE_TIME",
  weeklyDueTime: "WEEKLY_DUE_TIME",
  monthlyDueTime: "MONTHLY_DUE_TIME",
  // PRODUCT_DEV-698（9/14）: 上司宛（部のまとめの確定版）を深夜に送らない
  teamMailQuietHours: "TEAM_MAIL_QUIET_HOURS",
  ...REPORT_PREFERENCE_KEYS,
  // PRODUCT_DEV-939: 使う種類（配信設定とは別の層。定義は下の REPORT_IN_USE_PREFERENCE_KEYS）
  ...REPORT_IN_USE_PREFERENCE_KEYS,
} as const;

/**
 * PRODUCT_DEV-721 U7: 部署で持てる締め日・提出期限のキー。
 *
 * テナントと同じ文字列を使う（解決側が「部署 → テナント」を1つのキーで順に引ける）。ただし**日報の締め日だけは
 * 新しいキー**（`DAILY_CLOSE_OFFSET_DAYS`）。テナントは `REPORT_DAILY.sendOffsetDays` に持つが、部署の
 * `REPORT_DAILY` には締め日を部署で持てなかった頃の値が残っているので、それを締め日として読まないため。
 * 旧・3種共通の期限の時刻（`SUBMIT_DUE_TIME`）は部署では持たない。
 */
export const DEPT_PERIOD_PREFERENCE_KEYS = {
  fiscalMonthCloseDay: "FISCAL_MONTH_CLOSE_DAY",
  fiscalWeekCloseDay: "FISCAL_WEEK_CLOSE_DAY",
  dailyCloseOffsetDays: "DAILY_CLOSE_OFFSET_DAYS",
  dailyDueOffsetDays: "DAILY_DUE_OFFSET_DAYS",
  weeklyDueOffsetDays: "WEEKLY_DUE_OFFSET_DAYS",
  monthlyDueOffsetDays: "MONTHLY_DUE_OFFSET_DAYS",
  dailyDueTime: "DAILY_DUE_TIME",
  weeklyDueTime: "WEEKLY_DUE_TIME",
  monthlyDueTime: "MONTHLY_DUE_TIME",
} as const;

/**
 * 部のまとめの「まとめ方」（PRODUCT_DEV-1142 ③・10/15 ユーザ決定）。見る上司が何を重視するかで選ぶ3つの型。
 * - follow: フォロー重視（どこに手を打つ？）。**未設定はこれ**（今の並びと同じ順）
 * - result: 成果重視（何が決まった？）
 * - activity: 活動量重視（みんな動いてる？）
 * 材料と AI の出力の形は3つとも同じで、変わるのは厚く書くところ（指示）と並べ方（画面・メール・読み上げの順）。
 * **部署だけ**が持つ（その部署の manager が選ぶ。10/16 決定）。
 */
export const TeamSummaryFocusSchema = z.enum(["follow", "result", "activity"]);
export type TeamSummaryFocus = z.infer<typeof TeamSummaryFocusSchema>;

/** 部署設定の SK 名前空間。配信設定（PRODUCT_DEV-698）と使う種類（PRODUCT_DEV-939）と締め日・提出期限（PRODUCT_DEV-721 U7）。 */
export const DEPT_PREFERENCE_KEYS = {
  ...REPORT_PREFERENCE_KEYS,
  ...REPORT_IN_USE_PREFERENCE_KEYS,
  ...DEPT_PERIOD_PREFERENCE_KEYS,
  // PRODUCT_DEV-1142 ③: 部のまとめのまとめ方（部署だけが持つ。テナントには無い）
  teamSummaryFocus: "TEAM_SUMMARY_FOCUS",
} as const;

/**
 * 設定1件の DDB アイテム。
 * value は設定ごとに形が違う（文字列 / 数値 / 曜日と時刻のオブジェクト）ため
 * unknown で受け、キーごとのスキーマで読み出し時に検証する。
 */
export const DDBPreferenceSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  productId: z.string(),
  tenantId: z.string(),
  userId: z.string().optional(), // テナント/部署スコープには無い
  deptId: z.string().optional(), // 部署スコープにだけ入る
  key: z.string(),
  value: z.unknown(),
  updatedAt: z.string(),
  updatedBy: z.string().optional(),
});
export type DDBPreference = z.infer<typeof DDBPreferenceSchema>;

/**
 * 配信の時刻（JST・"HH:mm"）＝①下書きの作成の時刻。
 *
 * PRODUCT_DEV-874（2026-09-25）: **"24:00" も許す**（締め日が終わった時点＝翌 0:00）。
 * 「締め日当日 0:00」が締め日の始まりなのか終わりなのか分からず、終わりのつもりで 0:00 を
 * 選ぶ設定が起きた（実機で指摘）。画面は締め日当日の 0 時を出さず、終わりは 24:00 で選ばせる。
 * 読む側（`preferences/resolve.ts` の safeParse）も同じリリースで出るので、広げても壊れない。
 */
export const ReportTimeSchema = z.string().regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/);

/**
 * 日報・週報・月報の配信設定（PRODUCT_DEV-673 → PRODUCT_DEV-698 でスコープを移動）。
 *
 * ⚠ **日報の「送る曜日」はここに持たない。** 営業日はテナントの属性なので
 * `BusinessCalendarSchema` 側にあり、日報の配信・自動確定と月報の「翌営業日」が
 * 同じカレンダーを見る（PRODUCT_DEV-821）。ここに曜日をもう1つ足すと、
 * 種類ごとに営業日がばらけて必ず食い違う。
 *
 * 週報だけ `weekday`（0=日 〜 6=土）を持つ。**これは営業日ではなく「週報を出す曜日」**。
 * ただし PRODUCT_DEV-826 で、その曜日が休業日に当たったら翌営業日へずらすようにした
 * （振替休日の月曜に週報が飛んでいた）。
 *
 * ⚠ **種類ごとにしか意味を持たないキーが3つある**（`weekday` / `sendOffsetDays` /
 * `businessDayOffset`）。3種を1つの形で持つ以上ここは避けられないが、**読む側で
 * 「その種類のときだけ見る」を守ること**。他の種類で読むと、画面に出ていない値が
 * 効いているように見える。
 */
export const ReportScheduleSchema = z.object({
  /**
   * **本人宛**（「◯◯の下書きができています。確認して提出してください」）を送るか。
   *
   * ⚠ **PRODUCT_DEV-826 より前は、これが本人宛と部のまとめの両方を意味していた。**
   * 保存済みの値には `teamEnabled` が無いので、まとめ側は**このフラグに従わせる**
   * （`isTeamReportMailEnabled`）。既定 `true` に倒すと、配信を止めていたテナントで
   * まとめだけ復活してしまう。
   */
  enabled: z.boolean(),
  /**
   * **部のまとめ**（管理者宛）を送るか（PRODUCT_DEV-826）。
   *
   * 未設定は `enabled` に従う（＝旧データの意味をそのまま保つ）。読むときは必ず
   * `isTeamReportMailEnabled()` を通すこと——`?? true` と書くと上記の巻き戻しが起きる。
   */
  teamEnabled: z.boolean().optional(),
  time: ReportTimeSchema,
  /** 週報のみ。0=日 〜 6=土。 */
  weekday: z.number().int().min(0).max(6).optional(),
  /**
   * 日報のみ。**日報の締め日**（0=当日・1=翌営業日）。下書きはこの日に作る。
   *
   * PRODUCT_DEV-826。「その日のうちに確認して出す」運用と「翌朝まとめて出す」運用が
   * あり、後者は当日配信では成り立たなかった。**対象日が休業日なら作らない**。
   *
   * ⚠ **1 は「翌営業日」**（PRODUCT_DEV-874）。以前は暦の翌日で、金曜の日報が土曜に
   * 届いていた。提出期限は営業日で数えるので、締めも営業日で揃える。
   *
   * ⚠ **テナント単位の値**（PRODUCT_DEV-874。画面では「締め日と休業日」に置く）。
   * PRODUCT_DEV-721 U7: 機能フラグ ON のテナントは部署の `DAILY_CLOSE_OFFSET_DAYS` が優先（部署の `REPORT_DAILY` のこの値は読まない）。
   * 部署の例外に入っていても読まない — 解決は `resolveReportSchedules` がテナントの値で上書きする。
   */
  sendOffsetDays: z.number().int().min(0).max(1).optional(),
  /**
   * 月報のみ。締め日から**何営業日後**に下書きを作るか（0 = 締め日当日）。
   *
   * PRODUCT_DEV-826。暦日でずらすと休業日の並びで着地が動くので営業日で数える。
   * PRODUCT_DEV-874 で 0（締め日当日）を許した。締め日が休業日なら前の営業日に前倒しする
   * （提出期限は締め日から数えるので、後ろへずらすと翌営業日の自動提出と逆転する）。
   */
  businessDayOffset: z.number().int().min(0).max(20).optional(),
});
export type ReportSchedule = z.infer<typeof ReportScheduleSchema>;

/**
 * 本人宛（確認依頼）を送るか（PRODUCT_DEV-826）。
 *
 * **本人宛と部のまとめは別のスイッチ**になった。3つの述語をここに集めてあるのは、
 * 呼び出し側が `schedule.enabled` を直に見ると「まとめだけ ON」の設定が無視され、
 * 画面には ON と出ているのに何も届かない、という壊れ方をするため。
 */
export const isPersonalReportMailEnabled = (s: ReportSchedule): boolean =>
  s.enabled;

/**
 * 部のまとめ（管理者宛）を送るか（PRODUCT_DEV-826）。
 *
 * ⚠ **未設定は `enabled` に従う。`?? true` にしないこと。** PRODUCT_DEV-826 より前の
 * 保存値には `teamEnabled` が無く、`true` に倒すと**配信を止めていたテナントで
 * まとめだけが再開する**（止めたのに届く、という一番まずい戻り方）。
 */
export const isTeamReportMailEnabled = (s: ReportSchedule): boolean =>
  s.teamEnabled ?? s.enabled;

/**
 * その種類のメールを1通でも送るか（PRODUCT_DEV-826）。
 *
 * 配信の枠に入るかどうかの判定はこれで見る。`enabled` だけで見ると
 * 「本人宛 OFF・まとめ ON」の設定で枠に入らず、まとめが永久に送られない。
 */
export const isAnyReportMailEnabled = (s: ReportSchedule): boolean =>
  isPersonalReportMailEnabled(s) || isTeamReportMailEnabled(s);

/**
 * 3種まとめた形。**null は「この層では決めていない」**という意味で、
 * 「送らない」（`enabled: false`）とは別物。取り違えると、部署で解除したつもりが
 * 「送らない」で固定されてテナント既定に戻せなくなる。
 */
export const ReportSchedulesSchema = z.object({
  daily: ReportScheduleSchema.nullable(),
  weekly: ReportScheduleSchema.nullable(),
  monthly: ReportScheduleSchema.nullable(),
});
export type ReportSchedules = z.infer<typeof ReportSchedulesSchema>;

/** PATCH 用。省略したキーは変更しない。null 指定で解除（＝上位の層に従う）。 */
export const PatchReportSchedulesSchema = z
  .object({
    daily: ReportScheduleSchema.nullable().optional(),
    weekly: ReportScheduleSchema.nullable().optional(),
    monthly: ReportScheduleSchema.nullable().optional(),
  })
  .strict();
export type PatchReportSchedules = z.infer<typeof PatchReportSchedulesSchema>;

/**
 * どの層も決めていなかったときの既定（PRODUCT_DEV-698 → PRODUCT_DEV-874）。
 *
 * **下書きは締め日の夕方に作る**（日報 当日17:00 / 週報 締め日＝金曜 18:00 / 月報 締め日 18:00）。
 * 自動提出は提出期限（`resolveSubmitDue` の既定）で、その間が本人の加筆できる時間になる。
 * 週報の曜日 5（金）は `DEFAULT_WEEK_CLOSE_DAY` と揃えてある（締め日当日に下書きを作る）。
 *
 * ⚠ **変えると、明示設定していないテナントの時刻がすべて動く**（2026-09-11 時点で
 * prod は全テナントが既定のまま）。
 *
 * **api と web の両方が要るのでここに置く。** 片方に置くと必ずもう片方で綴り直され、
 * 「画面には 18:30 と出るのに 18:00 に届く」というずれ方をする。
 */
export const DEFAULT_REPORT_SCHEDULES: Record<ReportKind, ReportSchedule> = {
  daily: { enabled: true, time: "17:00", sendOffsetDays: 0 },
  weekly: { enabled: true, time: "18:00", weekday: 5 },
  monthly: { enabled: true, time: "18:00", businessDayOffset: 0 },
};

/*
 * ─── 使う種類 / 使わない種類（PRODUCT_DEV-939） ───────────────────────────────
 *
 * 「そもそも報告義務がない」「日報はしないが週報と月報はやる」という組織があるため、
 * 3種それぞれを**まるごと止められる**ようにする。
 *
 * ⚠ **配信設定（`ReportSchedule.enabled` / `teamEnabled`）とは別の層。** あちらは
 * 「メールを送るか」で、止めても下書きは作られ続ける（`listPreparations` の注記）。
 * こちらは**その種類を使うかどうか**で、OFF にすると下書きも作られず画面にも出ない。
 * 同じ意味に見えるので、片方を消してもう片方に寄せないこと——「メールは要らないが
 * 日報は書く」も「日報そのものをやらない」も、どちらも実在する運用である。
 */

/**
 * 3種まとめた形。**null は「この層では決めていない」**で、`false`（使わない）とは別物。
 * 配信設定の `ReportSchedules` と同じ約束——部署で null に戻すと会社全体の設定に従う。
 */
export const ReportsInUseSchema = z.object({
  daily: z.boolean().nullable(),
  weekly: z.boolean().nullable(),
  monthly: z.boolean().nullable(),
});
export type ReportsInUse = z.infer<typeof ReportsInUseSchema>;

/**
 * PATCH 用。省略したキーは変更しない。null 指定で解除（＝上位の層に従う）。
 *
 * ⚠ **`.strict()` なので、この項目を知らない古い API へ送ると保存が丸ごと 400 になる。**
 * web の資材と Lambda の更新は別の経路なので、新しい画面が古い API を叩く窓が実際に開く。
 * `draftToPatch` は締め日・①②・メール通知を1回の PATCH でまとめて送るため、そのあいだ
 * **設定画面の保存全体が「Invalid request body」になる**（管理者にはデプロイ順が原因だとは
 * 見えない）。同じ形で足した `teamMailQuietHours` から引き継いでいる性質で、窓は短いが
 * 開くこと自体は避けられない。新しい項目をここに足すときは同じ窓が開くと思っておくこと。
 */
export const PatchReportsInUseSchema = z
  .object({
    daily: z.boolean().nullable().optional(),
    weekly: z.boolean().nullable().optional(),
    monthly: z.boolean().nullable().optional(),
  })
  .strict();
export type PatchReportsInUse = z.infer<typeof PatchReportsInUseSchema>;

/**
 * どの層も決めていなかったときの既定（PRODUCT_DEV-939）。**3種とも使う**。
 *
 * ⚠ ここを `false` に倒さないこと。既存のテナントは1つも設定を持っていないので、
 * 既定が「使わない」だと**全テナントの日報・週報・月報が一斉に止まる**。
 */
export const DEFAULT_REPORTS_IN_USE: Record<ReportKind, boolean> = {
  daily: true,
  weekly: true,
  monthly: true,
};

/** 「深夜に当たるときは朝に送る」の朝の時刻の選択肢（PRODUCT_DEV-698 9/14）。 */
export const TEAM_MAIL_MORNING_HOURS = [6, 7, 8, 9] as const;

/**
 * 上司宛（部のまとめの確定版）を深夜に送らない設定（PRODUCT_DEV-698 9/14）。テナント単位。
 *
 * 上司宛は**提出期限②の15分後**に届く（確定版は期限の後に作るため）。期限が「当日24:00」なら
 * 0:15 に届くので、チェックを入れると朝の時刻へ寄せる。深夜は **22:00 から朝の時刻まで**
 * （ユーザ判断）。寄せ方の正本は `reportTimeline.ts` の `applyQuietHours`。
 */
export const TeamMailQuietHoursSchema = z.object({
  enabled: z.boolean(),
  morningHour: z.union([z.literal(6), z.literal(7), z.literal(8), z.literal(9)]),
});
export type TeamMailQuietHours = z.infer<typeof TeamMailQuietHoursSchema>;

/**
 * 既定は**寄せない（期限の15分後にそのまま送る）・朝は7時**。
 * 「締切＝上司メールが自然」「既定は即時配信」というユーザ判断による。
 */
export const DEFAULT_TEAM_MAIL_QUIET_HOURS: TeamMailQuietHours = {
  enabled: false,
  morningHour: 7,
};

/**
 * 休業日カレンダー（PRODUCT_DEV-821）。
 *
 * 「このテナントが休む日」の定義。日報の配信と自動確定、月報の
 * 「締め日の翌営業日」がこれを見る。判定そのものと優先順位の正本は
 * `packages/shared-domain/unizo/businessCalendar.ts`（ここは入れ物の形だけ）。
 *
 * ⚠ **持つのは休みの側**。画面も「休業日」で、月火水木金土日祝を ON にすると休みになる。
 * 一度は営業日で持っていたが、祝日だけ「休みにする」チェックだったため同じ画面で
 * ON の意味が逆になっていた。片方だけ画面で反転させると翻訳の層が永久に残る。
 *
 * ⚠ **7曜日すべては休業日にできない。** 全部入れると日報が永久に作られず自動確定も
 * 止まるが、画面には「送らない」トグルが別にあるので、事故としか解釈できない。
 *
 * ⚠ **臨時休業日・臨時営業日（日付指定）は後続。** 器は businessCalendar.ts 側に
 * `closedDates` / `openDates` として開けてあり、ここに足すだけで入る。
 */
export const BusinessCalendarSchema = z.object({
  /** 休業する曜日（0=日 〜 6=土）。空なら毎日営業。 */
  closedWeekdays: z
    .array(z.number().int().min(0).max(6))
    .refine((v) => new Set(v).size < 7, {
      message: "全曜日を休業日にはできません",
    }),
  /** 祝日を休業日にするか。 */
  closedOnHolidays: z.boolean(),
});
export type BusinessCalendarValue = z.infer<typeof BusinessCalendarSchema>;

/*
 * PRODUCT_DEV-699: 個人設定の値スキーマ
 * ------------------------------------
 * 端末ローカル（localStorage）にしか無かったものをサーバへ移すぶん。
 * **値の形はここを唯一の正とする**（api の resolve と web の読み書きが同じ物を見る）。
 */

/**
 * 記録モード。**`"dialogue"` を `"dialog"` に詰めないこと。**
 * この語彙は商談レコードの `recordingMode` と `lastRecordingMode`（顧客ごとの前回）で
 * 既に永続化されている。設定側だけ綴りを変えると、同じ概念に2つの語彙ができる。
 * 記録ドック内部の `"dialog"` のほうが後発の孤立した綴りなので、そちらを寄せる。
 */
export const RecordModeSchema = z.enum(["dialogue", "speed"]);
export type RecordModePreference = z.infer<typeof RecordModeSchema>;

/**
 * PRODUCT_DEV-1070: 最初に開く画面（HOME）。diary = マイ手帳 / meetings = 商談一覧。
 * 会社の初期表示ページ（auth-service の Tenants・whoami）と同じ語彙を土台にする
 * （片方だけ値を足すと、保存できた値を読む側が黙って未設定＝マイ手帳として扱う）。
 * PRODUCT_DEV-1092: 個人の設定にだけ summary = マネージャー画面の活動サマリを足す（部の数字を見られる人だけが選べ、
 * 見られなくなった人の summary はマイ手帳として扱う）。**会社の既定には入れない**（役割で意味が変わる値を
 * 会社全体の既定にできないため）。保存済みの "diary" / "meetings" の意味は変えない。
 * 未設定（null）の解釈は役割で分かれる（`USER_PREFERENCE_KEYS.homeScreen` 参照）。
 */
export const HomeScreenSchema = z.enum([...TenantHomeScreenSchema.options, "summary"]);
export type HomeScreenPreference = z.infer<typeof HomeScreenSchema>;

/**
 * PRODUCT_DEV-1070: 個人設定の HOME。画面（diary / meetings / summary）に加えて
 * **`"default"`＝自分では決めていない（既定に合わせる）**。家のマークで戻した・基本設定で既定と同じ画面を
 * 選んだときに入れる（会社が初期表示ページを後で変えたら付いていく。10/08 見直し）。既定はマネージャーなら
 * 活動サマリ → 会社の初期表示ページ → マイ手帳（PRODUCT_DEV-1092）。
 * ⚠ null（未設定）にしないのは、別の端末に残った下書きが次に開いたときサーバへ書き戻されるため
 * （PreferencesContext はサーバが null のキーを端末の値で埋める）。読む側は `"default"` を未設定と同じに扱う。
 */
export const HOME_SCREEN_FOLLOW_DEFAULT = "default" as const;
export const PersonalHomeScreenSchema = z.enum([...HomeScreenSchema.options, HOME_SCREEN_FOLLOW_DEFAULT]);
export type PersonalHomeScreen = z.infer<typeof PersonalHomeScreenSchema>;

/**
 * PRODUCT_DEV-783: 初回オンボーディングの進み具合。web は丸ごと送るが、**サーバはいまの値に OR で重ねて保存する**
 * （`apps/unizo/api/lib/preferences/onboarding.ts`。読み込みに失敗した端末からの書き込みで進み具合を消さないため）。
 * どの項目も省略は false と同じで、「済んだ」へ一方向にしか変わらない。null を送ると項目ごと消える（最初から）。
 * ⚠ 知らない項目は**捨てる**（zod の既定）。項目を足すときは API を先に出す（古い API に送ると黙って落ちる）。
 */
export const OnboardingStateSchema = z.object({
  /** STEP 1 商談記録 を最後まで見た */
  step1: z.boolean().optional(),
  /** STEP 2 マイ手帳 を最後まで見た */
  step2: z.boolean().optional(),
  /** STEP 3 便利な機能 を最後まで見た */
  step3: z.boolean().optional(),
  /** ホームの「はじめての方へ」の案内で「表示しない」を押した */
  hidden: z.boolean().optional(),
  /** 記録がまだ無い人に、アプリを開いたとき自動で1度出した（2回目からは自動で出さない） */
  autoShown: z.boolean().optional(),
  /**
   * 自動で出すかを判定し終えた（記録がある人は出さないと決まった）。**判定は1人1回**にするための印で、
   * これが無いと記録のある人にも、アプリを開くたびに記録の一覧を取りに行ってしまう。
   */
  autoChecked: z.boolean().optional(),
  /** メニューの「はじめての方へ」を1度でも開いた（メニューの赤ポチを消す） */
  menuOpened: z.boolean().optional(),
});
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

/** AI 音声出力の音量。0..1（`@shared-ui/audio/realtimeAudioOutput` の VOLUME_MIN/MAX と同じ）。 */
export const AiVoiceVolumeSchema = z.number().min(0).max(1);

/** 検索履歴の絞り込み条件。web 側の `GroupFilter` / `ReviewedFilter` と同じ語彙。 */
export const SearchHistoryItemSchema = z.object({
  id: z.string(),
  searchTerm: z.string(),
  filterPeriod: z.enum(["all", "today", "week", "month", "custom"]),
  filterDateRange: z.object({ from: z.string(), to: z.string() }).optional(),
  selectedCompany: z.string(),
  groupFilter: z.enum(["all", "grouped", "ungrouped"]),
  reviewedFilter: z.enum(["all", "unreviewed", "reviewed"]),
  isPinned: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type SearchHistoryItemContract = z.infer<typeof SearchHistoryItemSchema>;

/** 検索履歴の保持上限。web 側の MAX_ITEMS と同じ値をここから配る。 */
export const SEARCH_HISTORY_MAX_ITEMS = 10;

/**
 * 検索履歴。**配列は丸ごと置換**する（差分マージにすると、デバウンス中の
 * 連続操作＝追加してすぐピン留め、で片方を取りこぼす）。
 */
export const SearchHistorySchema = z
  .array(SearchHistoryItemSchema)
  .max(SEARCH_HISTORY_MAX_ITEMS);

/** GET /user-preferences。未設定は null（呼び出し側がフォールバックを決める）。 */
export const UserPreferencesSchema = z.object({
  uiMode: UiModeSchema.nullable(),
  // PRODUCT_DEV-699
  lastPresetId: z.string().nullable(),
  presetChosen: z.boolean().nullable(),
  recordMode: RecordModeSchema.nullable(),
  dockCollapsed: z.boolean().nullable(),
  notificationViewedAt: z.string().nullable(),
  lastSeenReleaseVersion: z.string().nullable(),
  schedulingHintDismissed: z.boolean().nullable(),
  aiVoiceVolume: AiVoiceVolumeSchema.nullable(),
  searchHistory: SearchHistorySchema.nullable(),
  /** PRODUCT_DEV-826: 自分宛の「部のまとめ」を受け取らない。null は受け取る。 */
  teamReportMailOptOut: z.boolean().nullable(),
  /** PRODUCT_DEV-917: いま見ている部署。null は「まだ選んでいない」＝先頭の所属。 */
  activeDeptId: z.string().nullable(),
  /** PRODUCT_DEV-1070: 最初に開く画面。null と "default" は未設定（マネージャーは活動サマリ、それ以外は会社の初期表示ページ → マイ手帳・PRODUCT_DEV-1092）。 */
  homeScreen: PersonalHomeScreenSchema.nullable(),
  /** PRODUCT_DEV-783: 初回オンボーディングの進み具合。null はまだ何もしていない。 */
  onboarding: OnboardingStateSchema.nullable(),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

/** PATCH /user-preferences。省略したキーは変更しない。null 指定で設定解除。 */
export const PatchUserPreferencesSchema = z
  .object({
    uiMode: UiModeSchema.nullable().optional(),
    // PRODUCT_DEV-699
    lastPresetId: z.string().min(1).nullable().optional(),
    presetChosen: z.boolean().nullable().optional(),
    recordMode: RecordModeSchema.nullable().optional(),
    dockCollapsed: z.boolean().nullable().optional(),
    notificationViewedAt: z.string().datetime().nullable().optional(),
    lastSeenReleaseVersion: z.string().min(1).nullable().optional(),
    schedulingHintDismissed: z.boolean().nullable().optional(),
    aiVoiceVolume: AiVoiceVolumeSchema.nullable().optional(),
    searchHistory: SearchHistorySchema.nullable().optional(),
    // PRODUCT_DEV-826
    teamReportMailOptOut: z.boolean().nullable().optional(),
    // PRODUCT_DEV-917
    activeDeptId: z.string().min(1).nullable().optional(),
    // PRODUCT_DEV-1070
    homeScreen: PersonalHomeScreenSchema.nullable().optional(),
    // PRODUCT_DEV-783
    onboarding: OnboardingStateSchema.nullable().optional(),
  })
  .strict();
export type PatchUserPreferences = z.infer<typeof PatchUserPreferencesSchema>;

/**
 * 週の締め曜日。0=日曜 〜 6=土曜（`Date#getUTCDay()` と同じ並び）。
 *
 * 週は**この曜日で終わる7日間**。既定は金曜（土〜金）。PRODUCT_DEV-874 で日曜（月〜日）から変えた。
 */
export const FiscalWeekCloseDaySchema = z.number().int().min(0).max(6);

/**
 * 提出期限の時刻（JST・"HH:mm"）。
 *
 * PRODUCT_DEV-874: **"24:00" を許す**（その日の終わり＝翌 0:00）。日報の既定の期限が
 * 「当日 24:00」で、「その日のうちに書けば間に合う」をそのまま表すため。
 * 期限は自動提出を動かす値なので、画面は 15 分刻みでしか入れさせない（ここは形だけ見る。
 * 1分刻みの旧データは `resolveSubmitDue` が次の枠へ繰り上げる）。
 */
export const SubmitDueTimeSchema = z
  .string()
  .regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/);

/**
 * GET /admin/tenant-preferences。
 *
 * **締め日と提出期限は別物**。「19日締め・22日提出」のように、締めてから提出までに
 * 猶予を置く運用があるため、期限は締め日からの日数（オフセット）で持つ。
 * 締め日を動かしても猶予の日数はそのままでよい、という関係になる。
 *
 * - `fiscalMonthCloseDay` … 営業月の締め日（1〜31）。null はカレンダー月
 * - `fiscalWeekCloseDay` … 週の締め曜日（0=日 〜 6=土）。null は金曜（土〜金。PRODUCT_DEV-874）
 * - `dailyDueOffsetDays` / `weeklyDueOffsetDays` / `monthlyDueOffsetDays`
 *   … 締め日の何日後が提出期限か。日報の締め日はその日そのものなので 0 = 当日
 *   （PRODUCT_DEV-826 で日報にも持たせた。それまで日報だけ期限の概念が無く、
 *   「9/3 の日報は翌朝 9:00 まで」という運用が表現できなかった）
 * - `dailyDueTime` / `weeklyDueTime` / `monthlyDueTime`
 *   … 期限の時刻（JST）。**種類ごとに持つ**（PRODUCT_DEV-826）。実質どれも同じ時刻に
 *   なる運用が多いが、日付側と離れた1項目だと「日報の期限は何時か」が読めなかった
 * - `submitDueTime` … 旧・3種共通の時刻。**新しい画面からは書かない**が、
 *   種類ごとの値が未設定のときのフォールバックとして読む（既存テナントの着地を変えない）
 */
export const TenantPreferencesSchema = z.object({
  fiscalMonthCloseDay: z.number().int().min(1).max(31).nullable(),
  fiscalWeekCloseDay: FiscalWeekCloseDaySchema.nullable(),
  dailyDueOffsetDays: z.number().int().min(0).max(7).nullable(),
  weeklyDueOffsetDays: z.number().int().min(0).max(14).nullable(),
  monthlyDueOffsetDays: z.number().int().min(0).max(31).nullable(),
  submitDueTime: SubmitDueTimeSchema.nullable(),
  // PRODUCT_DEV-826: 種類ごとの期限の時刻。null は submitDueTime → 09:00 の順で倒れる
  dailyDueTime: SubmitDueTimeSchema.nullable(),
  weeklyDueTime: SubmitDueTimeSchema.nullable(),
  monthlyDueTime: SubmitDueTimeSchema.nullable(),
  /**
   * PRODUCT_DEV-698: 日報・週報・月報の配信。**テナントの既定**で、
   * 部署がこれを上書きできる。null は「決めていない」＝ `DEFAULT_REPORT_SCHEDULES`。
   */
  reports: ReportSchedulesSchema,
  /**
   * PRODUCT_DEV-939: 使う種類の**テナントの既定**で、部署がこれを上書きできる。
   * null は「決めていない」＝ `DEFAULT_REPORTS_IN_USE`（3種とも使う）。
   *
   * ⚠ **この項目を返さない古い API がある**（デプロイの前後）ので、読む側は
   * `?? DEFAULT_REPORTS_IN_USE` で受ける。undefined を「使わない」と読むと、
   * デプロイの数分間だけ全テナントの種類が消えたように見える。
   */
  reportsInUse: ReportsInUseSchema.optional(),
  /**
   * PRODUCT_DEV-821: 休業日。null は「決めていない」＝ `DEFAULT_BUSINESS_CALENDAR`
   * （土日と祝日が休み）。部署ごとの例外は持たせていない（会社の休業日は
   * 部署で変わらない、という前提。変わる運用が出たら reports と同じ2層にできる）。
   */
  businessCalendar: BusinessCalendarSchema.nullable(),
  /**
   * PRODUCT_DEV-698（9/14）: 上司宛を深夜に送らない設定。null は「決めていない」＝
   * `DEFAULT_TEAM_MAIL_QUIET_HOURS`（寄せない）。⚠ **この項目を返さない古い API がある**
   * （デプロイの前後）ので、読む側は必ず `?? DEFAULT_TEAM_MAIL_QUIET_HOURS` で受ける。
   */
  teamMailQuietHours: TeamMailQuietHoursSchema.nullable().optional(),
});
export type TenantPreferences = z.infer<typeof TenantPreferencesSchema>;

export const PatchTenantPreferencesSchema = z
  .object({
    fiscalMonthCloseDay: z.number().int().min(1).max(31).nullable().optional(),
    fiscalWeekCloseDay: FiscalWeekCloseDaySchema.nullable().optional(),
    dailyDueOffsetDays: z.number().int().min(0).max(7).nullable().optional(),
    weeklyDueOffsetDays: z.number().int().min(0).max(14).nullable().optional(),
    monthlyDueOffsetDays: z.number().int().min(0).max(31).nullable().optional(),
    submitDueTime: SubmitDueTimeSchema.nullable().optional(),
    // PRODUCT_DEV-826
    dailyDueTime: SubmitDueTimeSchema.nullable().optional(),
    weeklyDueTime: SubmitDueTimeSchema.nullable().optional(),
    monthlyDueTime: SubmitDueTimeSchema.nullable().optional(),
    reports: PatchReportSchedulesSchema.optional(),
    // PRODUCT_DEV-939: 使う種類
    reportsInUse: PatchReportsInUseSchema.optional(),
    businessCalendar: BusinessCalendarSchema.nullable().optional(),
    // PRODUCT_DEV-698（9/14）
    teamMailQuietHours: TeamMailQuietHoursSchema.nullable().optional(),
  })
  .strict();
export type PatchTenantPreferences = z.infer<typeof PatchTenantPreferencesSchema>;

/**
 * PRODUCT_DEV-721 U7: 部署の締め日・提出期限。null は「決めていない」＝会社全体の設定に従う。
 */
export const DeptPeriodPreferencesSchema = z.object({
  fiscalMonthCloseDay: z.number().int().min(1).max(31).nullable(),
  fiscalWeekCloseDay: FiscalWeekCloseDaySchema.nullable(),
  /** 日報の締め日（0=当日・1=翌営業日）。テナントの `reports.daily.sendOffsetDays` にあたる */
  dailyCloseOffsetDays: z.number().int().min(0).max(1).nullable(),
  dailyDueOffsetDays: z.number().int().min(0).max(7).nullable(),
  weeklyDueOffsetDays: z.number().int().min(0).max(14).nullable(),
  monthlyDueOffsetDays: z.number().int().min(0).max(31).nullable(),
  dailyDueTime: SubmitDueTimeSchema.nullable(),
  weeklyDueTime: SubmitDueTimeSchema.nullable(),
  monthlyDueTime: SubmitDueTimeSchema.nullable(),
});
export type DeptPeriodPreferences = z.infer<typeof DeptPeriodPreferencesSchema>;

/** PATCH の部署の締め日・提出期限。省略は変更なし・null は解除（会社全体の設定に従う）。 */
export const PatchDeptPeriodPreferencesSchema = DeptPeriodPreferencesSchema.partial().strict();

/**
 * 部署設定（PRODUCT_DEV-698）。**テナント設定の例外**として使う。
 *
 * PRODUCT_DEV-721 U7: 日報・部の数字が部署ごとに分かれたので、配信設定と使う種類に加えて締め日と提出期限も部署で持つ
 * （`period`。設定するのはその部署の manager）。
 */
export const DepartmentPreferencesSchema = z.object({
  reports: ReportSchedulesSchema,
  /**
   * PRODUCT_DEV-939: 使う種類の例外。null は「決めていない」＝会社全体の設定に従う。
   * ⚠ この項目を返さない古い API がある（デプロイの前後）ので optional。
   */
  reportsInUse: ReportsInUseSchema.optional(),
  /** PRODUCT_DEV-721 U7: 締め日・提出期限。⚠ 返さない古い API がある（デプロイの前後）ので optional */
  period: DeptPeriodPreferencesSchema.optional(),
  /**
   * PRODUCT_DEV-1142 ③: 部のまとめのまとめ方。null は「決めていない」＝フォロー重視。
   * ⚠ 返さない古い API がある（デプロイの前後）ので optional。
   */
  teamSummaryFocus: TeamSummaryFocusSchema.nullable().optional(),
});
export type DepartmentPreferences = z.infer<typeof DepartmentPreferencesSchema>;

export const PatchDepartmentPreferencesSchema = z
  .object({
    reports: PatchReportSchedulesSchema.optional(),
    // PRODUCT_DEV-939
    reportsInUse: PatchReportsInUseSchema.optional(),
    // PRODUCT_DEV-721 U7: 部署の締め日・提出期限
    period: PatchDeptPeriodPreferencesSchema.optional(),
    // PRODUCT_DEV-1142 ③: 部のまとめのまとめ方。null で「決めていない」に戻す
    teamSummaryFocus: TeamSummaryFocusSchema.nullable().optional(),
  })
  .strict();
export type PatchDepartmentPreferences = z.infer<
  typeof PatchDepartmentPreferencesSchema
>;

/**
 * 画面が「いつ自動送信されるか」を嘘なく書くために要る、テナント側の解決済みの値
 * （PRODUCT_DEV-821）。
 *
 * 管理者ビューの `GET /admin/tenant-preferences` は admin 限定なので、一般利用者の
 * 画面はこれを読めない。それでもマイページと日報画面は「◯曜は自動送信されません」を
 * 出しているため、**書ける材料をここで配る**。設定を変える口ではないので値だけ。
 */
export type UserVisibleTenantSchedule = {
  /** 解決済みの休業日（未設定なら `DEFAULT_BUSINESS_CALENDAR`）。 */
  businessCalendar: BusinessCalendarValue;
  /** 解決済みの日報の配信設定（テナント既定。部署の例外はここでは見ない）。 */
  daily: ReportSchedule;
  /**
   * 解決済みの「使う種類」（PRODUCT_DEV-939）。**こちらは閲覧者の部署の例外まで見る**。
   *
   * 時刻（`daily`）と違って、ずれると**画面に出る／出ない**が変わる。部署が日報を
   * 止めているのに本人の画面に日報のタブが出ていたら、出せない提出物を促すことになる。
   * そのため、この項目のためだけに所属部署を1回引いている（`user-preferences/get.ts`）。
   *
   * ⚠ **optional のままにする。** 返さない古い API を相手にしたときは
   * `DEFAULT_REPORTS_IN_USE`（3種とも使う）へ倒すこと。undefined を「使わない」と
   * 読むと、デプロイの前後だけ全員の種類が消えたように見える。
   */
  reportsInUse?: Record<ReportKind, boolean>;
  /**
   * PRODUCT_DEV-721 U6: 機能フラグ ON のテナントで、所属する部署ごとの使う種類。画面は「いまの部署」で引く
   * （部署を切り替えるたびに取り直さない）。`reportsInUse` はそのうち既定の部署（先頭の所属）の値
   */
  reportsInUseByDeptId?: Record<string, Record<ReportKind, boolean>>;
};

export type UserPreferencesResponse = {
  success: boolean;
  preferences: UserPreferences;
  /**
   * ⚠ **optional のままにする。** 新しい web が古い API を叩く瞬間（デプロイの
   * 順序次第で起きる）に undefined が来る。型で required にすると、その瞬間だけ
   * 画面が落ちる。読む側は未設定でも成立する文言へ倒すこと。
   */
  tenantSchedule?: UserVisibleTenantSchedule;
}

export type TenantPreferencesResponse = {
  success: boolean;
  preferences: TenantPreferences;
  /**
   * PRODUCT_DEV-721 U7: 機能フラグ ON のテナントで、会社共通の設定（休業日）を変えたことで
   * ①（下書きの作成）と②（提出期限）が逆転・同時になった部署。**保存は通す**（2026-09-17 ユーザ判断）。
   * 直すのはその部署の manager。
   */
  timelineWarnings?: Array<{ deptId: string | null; kind: ReportKind }>;
}

/**
 * GET /admin/department-preferences — **全部署ぶんをまとめて返す**。
 * 画面が部署を切り替えるたびに取りに行かなくて済むようにする（部署は多くても数十）。
 * 設定を1つも持たない部署はキーごと入らない。
 */
export type DepartmentPreferencesResponse = {
  success: boolean;
  byDeptId: Record<string, DepartmentPreferences>;
}

/** PATCH /admin/department-preferences/{deptId} */
export type DepartmentPreferenceResponse = {
  success: boolean;
  deptId: string;
  preferences: DepartmentPreferences;
}

/** 活動サマリの期間。日/週/月は同じ画面のタブ切替。 */
export const SummaryPeriodSchema = z.enum(["daily", "weekly", "monthly"]);
export type SummaryPeriodKind = z.infer<typeof SummaryPeriodSchema>;

/**
 * me=自分ぶん / team=部下ぶん。
 * **team と「userId を明示して他人を指す」場合の認可は middleware ではなく
 * `apps/unizo/api/lambda/activity-summary/get.ts` の baseHandler 内で 403 を返す**
 * （同じ Lambda で自分の集計も返すため、router 単位の needsAdminRole では分けられない）。
 * ここを「middleware で担保済み」と誤読してハンドラ内の判定を消さないこと。
 */
export const ActivitySummaryScopeSchema = z.enum(["me", "team"]);
export type ActivitySummaryScope = z.infer<typeof ActivitySummaryScopeSchema>;

/** 期間中に面談した顧客の1社ぶん。「今週会った顧客（9社）」のチップに使う。 */
export interface ActivitySummaryCompany {
  companyId: string;
  companyName: string;
  recordCount: number;
  lastMeetingOn: string; // "YYYY-MM-DD"
  /** この顧客と面談したメンバー（scope=team のときだけ入る）。 */
  members?: Array<{ userId: string; userName: string; recordCount: number }>;
  /** 直近の記録（scope=team のときだけ入る・新しい順・最大5件）。 */
  recentMeetings?: Array<{
    meetingId: string;
    title: string | null;
    occurredOn: string;
    userName: string;
  }>;
  /**
   * 期間内の最新の記録の分類（プリセット ID・scope=team のときだけ入る。PRODUCT_DEV-698）。
   * 表示名は web が presets から解決する（`ActivityCategoryCount` と同じ理由）。
   */
  presetId?: string | null;
  /**
   * 期間内の最新の記録に書かれた次アクション（先頭のみ・scope=team のときだけ入る）。
   * 会社別の「状況」列に**そのまま**出す。AI の要約ではなく記録の事実（PRODUCT_DEV-698 9/11）。
   * 無ければ null（画面は直近記録のタイトルで代える）。
   */
  latestNextAction?: string | null;
}

/**
 * 分類（プリセット）別の記録件数（PRODUCT_DEV-673 PR8・scope=team）。
 * 表示名は web が root loader の presets から解決する（削除済みプリセットで
 * 記録された過去の商談も名前が引けるよう、ここでは ID だけを返す）。
 */
export interface ActivityCategoryCount {
  /** プリセット未設定の記録は null。 */
  presetId: string | null;
  recordCount: number;
}

/**
 * 接触が途絶えている会社（PRODUCT_DEV-673 PR8・scope=team）。
 * **集計期間ではなく「最後に記録された日」からの経過日数**で拾う
 * （売上に直結する見落としを拾うのが目的なので、期間で切ると意味が無い）。
 */
export interface StaleCompany {
  companyId: string;
  companyName: string;
  lastContactOn: string; // "YYYY-MM-DD"
  daysSince: number;
  /**
   * 最後に記録した人（PRODUCT_DEV-698）。「誰に声を掛ければよいか」がこの一覧の次の行動なので、
   * 会社名だけでは足りない。名前は部署の名簿で引き、引けなければ空文字。
   */
  lastContactUserId?: string;
  lastContactUserName?: string;
}

/** 期間より後に予定されている次回アポ1件。「来週の予定（5件）」に使う。 */
/**
 * 今日を予定日とする商談（PRODUCT_DEV-675）。
 *
 * 過去の商談記録に書かれた「次回アポ」が今日のもの。**マイページの「今日の予定」の材料**で、
 * カレンダー連携が無くても Unizo が自前で持っている情報だけで組める。
 *
 * `hasRecordToday` は「その相手について今日すでに記録があるか」。
 * 記録済みのものに「記録する」を出し続けると、押していいのか分からなくなる。
 */
export interface TodayAppointment {
  /** 元になった商談 ID。ここから「続きを記録」へ繋ぐ。 */
  sourceId: string;
  companyId: string | null;
  companyName: string | null;
  /** 元の商談のタイトル（次回アポの用件そのものではない点に注意）。 */
  title: string | null;
  /**
   * その相手について**対象日に**商談記録があるか。
   * ⚠ 名前に today が入っているが、判定するのは `date` で指定された日
   * （前後の日も見られるようにした PRODUCT_DEV-675 以降）。
   * PRODUCT_DEV-782: 手帳の過ぎた日（web の `pastDayPlanMaterials` が索引から組む）では、
   * 元商談の続きとして**予定の日以降に**作った記録も数える（後日記録した分）。
   */
  hasRecordToday: boolean;
  /**
   * 対象日にこの相手で作られた商談記録の ID（PRODUCT_DEV-675 8/31）。
   *
   * ⚠ **`sourceId` と別物。** あちらは「次回アポ」が書かれていた**過去の**商談で、
   * こちらは**その予定に対して今日作られた**商談。画面はこれで「記録を開く」の
   * 行き先を決める（記録したのにボタンが「記録作成」のままなのを直すため）。
   * 記録が無ければ null。複数あれば最初の 1 件。
   * PRODUCT_DEV-782: 手帳の過ぎた日では、後日作った記録の ID も入る（`hasRecordToday` と同じ）。
   */
  recordedMeetingId?: string | null;
}

/**
 * 日程調整から作られた商談記録（PRODUCT_DEV-796）。
 *
 * 日程調整の予定はサーバが組まない（web が日程調整の一覧から行を作る）ので、
 * **突き合わせは web が行う**。サーバは「どの日程調整から作った記録か」だけを返す。
 */
export interface TodayScheduledRecord {
  meetingId: string;
  scheduleId: string;
}

export type TodayAppointmentsResponse = {
  success: boolean;
  /**
   * JST の今日（"YYYY-MM-DD"）。**`date` と同じとは限らない。**
   * 見出しを「今日の予定」「明日の予定」「8/30(土) の予定」と出し分けるのに使う。
   */
  today: string;
  /**
   * このレスポンスが対象とする日（"YYYY-MM-DD"）。省略時は `today` と同じ
   * （PRODUCT_DEV-675 より前のクライアントは `date` を送らないので常に今日）。
   */
  date?: string;
  appointments: TodayAppointment[];
  /**
   * PRODUCT_DEV-796: さかのぼり範囲〜対象日の商談記録のうち、日程調整から作ったもの。
   * 古いサーバは返さないので、web は未設定を空として扱う。
   */
  scheduledRecords?: TodayScheduledRecord[];
  /**
   * PRODUCT_DEV-796: さかのぼり範囲〜今日の、本人の**いま在る**商談記録の ID（ゴミ箱・無効化は含まない）。
   * web が議事録の `meetingId` から記録を見つけたとき、その記録がまだ在るかを確かめるのに使う
   * （議事録の `meetingId` は、商談記録をゴミ箱へ入れても残る）。古いサーバは返さない。
   */
  activeMeetingIds?: string[];
};

export interface ActivitySummaryNextAppointment {
  sourceId: string;
  companyName: string | null;
  title: string | null;
  nextActionOn: string; // "YYYY-MM-DD"
}

/** scope=team の1メンバーぶん。 */
export interface ActivitySummaryMember {
  userId: string;
  userName: string;
  metrics: ActivityMetrics;
  comparison: MetricsComparison;
  /** 最終活動日。活動が無ければ null。 */
  lastActivityOn: string | null;
  /**
   * その期間の提出状態（PRODUCT_DEV-673 PR6）。
   * 管理者は「本人提出」と「自動提出」を区別して読むため、真偽値にまとめない。
   */
  submitStatus: SummarySubmitStatus;
  /**
   * 提出物（下書き）があるか（PRODUCT_DEV-874）。上司の画面で、提出状態が "none" の人を
   * **「未提出」（下書きはあるが本人がまだ出していない）と「未作成」（記録が無く下書きも無い）**
   * に分けるために使う。下書きの作成から自動提出までのあいだは、ここが true で "none"。
   * optional なのは古い API への保険（無ければ従来どおり「未作成」に倒れる）。
   */
  hasDraft?: boolean;
  /** 晴れ／雨。上司の「目を通したいところ」の主要な拾い源。 */
  flag: SummaryFlag;
  /** 本人の所感（先頭のみ）。上司が開く前に当たりを付けるための抜粋。 */
  noteExcerpt: string | null;
  /**
   * 予定はあったのに記録が無い件数（PRODUCT_DEV-698）。数え方は
   * `apps/unizo/api/lib/activitySummary/unrecorded.ts`。**今日の予定は数えない**。
   * optional なのは、新しい web が古い API を叩く瞬間に undefined が来るため（0 と読む）。
   */
  unrecordedCount?: number;
  /**
   * 週・月の表示でだけ入る、期間内の日報を何日ぶん誰が出したか（PRODUCT_DEV-698 9/11）。
   * 「提出の状況」を人ごとの帯にする材料。数え方は `apps/unizo/api/lib/activitySummary/dailySubmit.ts`
   * （過ぎた営業日を 本人 / AI / 未提出 に1日ずつ。今日は提出済みのときだけ）。
   */
  dailySubmitCounts?: { confirmed: number; auto: number; none: number };
}

export type ActivitySummaryResponse = {
  success: boolean;
  scope: ActivitySummaryScope;
  range: PeriodRange;
  metrics: ActivityMetrics;
  /** 1つ前の期間の集計。「先週比」の分母ではなく差分の相手。 */
  previous: ActivityMetrics;
  comparison: MetricsComparison;
  trend: ActivityTrend;
  companies: ActivitySummaryCompany[];
  nextAppointments: ActivitySummaryNextAppointment[];
  /** scope=team のときだけ入る。 */
  members?: ActivitySummaryMember[];
  /** 分類（プリセット）別の記録件数。scope=team のときだけ入る。 */
  categories?: ActivityCategoryCount[];
  /** 接触が途絶えている会社。scope=team のときだけ入る。 */
  staleCompanies?: StaleCompany[];
  /**
   * 集計した枠。scope=team のときだけ入る（PRODUCT_DEV-698）。
   *
   * `scope=team` は**閲覧者の部署**で切る。`deptId: null` は「未所属」枠、
   * `name` が空文字なら**部署を1つも作っていないテナント**（＝全員が1枠で、
   * 画面は従来どおりの見出しを出す）。
   */
  /** PRODUCT_DEV-721 U6: `concurrentMemberCount` はうち兼務している人の数（機能フラグ ON の部署の枠だけ） */
  teamScope?: {
    deptId: string | null;
    name: string;
    memberCount: number;
    concurrentMemberCount?: number;
  };
  /** 部のまとめ（AI）。scope=team のときだけ入る。まだ生成されていなければ null。 */
  teamSummary?: TeamSummaryView | null;
  /**
   * この期間の**実際の締め時刻**（PRODUCT_DEV-698 9/15）。scope=team のときだけ入る。確定版がまだ無いときに
   * 「商談の中身からの判定は ○/○ ○:○○ の後に出ます」と出すために使う。確定版を積む auto-confirm と同じ
   * `resolveEffectiveDeadlineMoment`（部署の①を含む）で出す。`dueAt` は ISO 8601、`label` は画面の呼び方。
   */
  teamSubmitDue?: { dueAt: string; label: string } | null;
  /**
   * 「目を通したいところ」のルールの材料（PRODUCT_DEV-698 9/15）。scope=team のときだけ入る。
   * 確定版を作るワーカーと同じ `collectAttentionRuleInputs` で組む。確定版が無いときに web が区分へ割り付ける。
   */
  attentionRuleInputs?: AttentionRuleInput[];
  /**
   * いま先頭に出す確定版（PRODUCT_DEV-698 ③）。scope=team のときだけ入る。並びは **月報 → 週報 → 日報**。
   * 月報・週報は確定した日を含めて2営業日のあいだだけ、日報は直近で確定した日。
   * `landing=1` で開いたときはこの先頭の期間を返し、画面は残りを「ほかに届いている確定版」として出す。
   * 数え方は `apps/unizo/api/lib/activitySummary/confirmedPeriods.ts`。
   */
  confirmedPeriods?: ActivitySummaryConfirmedPeriod[];
  /**
   * この期間の**締め時刻より後に本人が提出した人数**（PRODUCT_DEV-698 ③ 決定仕様）。scope=team で、
   * 締め時刻（`teamSubmitDue.dueAt`）を過ぎているときだけ入る（途中の期間では入らない）。
   * 確定版のまとめは締め時刻の時点の提出で作るので、後から出たぶんは反映されない。画面はそれを
   * 「確定後に N 件の提出があります」と出す。数え方は `lib/activitySummary/confirmedPeriods.ts` の
   * `countLateSubmissions`。optional なのは古い API への保険（無ければ何も出さない）。
   */
  lateSubmissionCount?: number;
  /**
   * 部署の中での位置（PRODUCT_DEV-1219）。自分を見るとき・週と月・`peers=1` のときだけ入る。
   * 記録した人が3人未満、部署が決まらない、または本人がもうその部署にいない（異動前の部署）ときは入らない。中身の決め方は
   * `apps/unizo/api/lib/activitySummary/peerComparison.ts`。
   */
  peerComparison?: PeerComparison;
  /**
   * 予定はあったのに記録が無い件数（PRODUCT_DEV-1219）。自分を見るとき・`peers=1` のときだけ入る。
   * 数え方は管理者画面のメンバー別と同じ（部署の誰かが記録していれば記録済み）。
   * まだ数えられない期間（今日始まったばかり）では入らない。
   */
  unrecordedCount?: number;
  generatedAt: string;
}

/**
 * 活動サマリの「部署と比べる」（PRODUCT_DEV-1219）。**名前と順位は持たない**。
 */
export interface PeerComparison {
  /** その期間に1件以上記録した人の数（自分を含む） */
  recordedMemberCount: number;
  /** 記録した人の記録件数の平均（小数1桁） */
  average: number;
  /** 上位3人の平均（小数1桁）。記録した人が6人未満なら null */
  topAverage: number | null;
}

/**
 * いま先頭に出す確定版の1件（PRODUCT_DEV-698 ③）。`ActivitySummaryResponse.confirmedPeriods` の要素。
 */
export interface ActivitySummaryConfirmedPeriod {
  period: SummaryPeriodKind;
  periodKey: string;
  /** その期間を開く基準日（期間の最終日。日タブへ切り替えたときに休業日を開かないため）。 */
  anchor: string;
  /** 期間の呼び方（`PeriodRange.label`）。 */
  label: string;
  /** 確定した時刻（実際の締め時刻。ISO 8601）。 */
  confirmedAt: string;
}

// ─── 日報 / 週報 / 月報（提出物）(PRODUCT_DEV-673) ───────────────────────────
//
// テーブル: UnizoActivitySummaries
//   PK: `TENANT#{tenantId}#USER#{userId}`（サマリの対象者）
//   SK: `SUMMARY#{period}#{periodKey}` 例 `SUMMARY#weekly#2026-W29`
//
// 数値そのものは毎回 ActivitySource から集計し直す（このテーブルには保存しない）。
// ここに載るのは **人が触ったもの と AI が書いたもの**＝本文・所感・フラグ・提出状態・
// 上司コメント・ハイライトと、提出時点の指標スナップショット。
//
// **提出は2段階**（要件まとめ §5）。設定時刻に自動送信された状態が `auto`、本人が確認して
// 提出した状態が `confirmed`。上司はこの2つを区別して読むため、1つの真偽値にまとめない。

/** 提出状態。none=未作成 / auto=自動提出 / confirmed=本人提出 */
export const SummarySubmitStatusSchema = z.enum(["none", "auto", "confirmed"]);
export type SummarySubmitStatus = z.infer<typeof SummarySubmitStatusSchema>;

/** 晴れ=手応えあり / 雨=相談したい。任意で、未選択は none（＝普通として提出される）。 */
export const SummaryFlagSchema = z.enum(["none", "sunny", "rain"]);
export type SummaryFlag = z.infer<typeof SummaryFlagSchema>;

/** AI 下書きの生成状態。SQS ワーカーが更新し、フロントは completed までポーリングする。 */
export const SummaryAiStatusSchema = z.enum([
  "idle",
  "generating",
  "completed",
  "failed",
]);
export type SummaryAiStatus = z.infer<typeof SummaryAiStatusSchema>;

/** 顧客ごとのハイライト1件（「サンプル商事 — 価格帯に懸念。決裁は8月上旬の役員会。」）。 */
export const SummaryHighlightSchema = z.object({
  companyName: z.string(),
  text: z.string().max(500),
  /** 出典の商談。UI から詳細へ飛ばすために持つ。 */
  meetingId: z.string().optional(),
});
export type SummaryHighlight = z.infer<typeof SummaryHighlightSchema>;

/**
 * 本文のもとになった記録1件（PRODUCT_DEV-673）
 *
 * **AI が書いた本文が出鱈目でないことを、本人と上司がその場で確かめられるようにする。**
 * サマリを作るときに実際に読んだ記録を、そのまま並べて出典として残す。
 *
 * 形の正本は `./activitySummary.ts` の `ActivityEvent` で、ここはそのうち
 * **画面から辿るのに要る分だけ**を保存用に写したもの。集計値（面談人数・次回アポ）は
 * `metricsSnapshot` が持っているので重ねて持たない。
 */
export const SummarySourceKindSchema: z.ZodType<ActivitySourceKind> = z.enum([
  "meeting",
  "customer",
  "minutes",
  "schedule",
]);

export const SummarySourceSchema = z.object({
  kind: SummarySourceKindSchema,
  /** 起点レコードの ID。画面はこれで詳細へ飛ぶ。 */
  sourceId: z.string(),
  /** 活動日（JST の "YYYY-MM-DD"）。 */
  occurredOn: z.string(),
  title: z.string().max(200).optional(),
  companyName: z.string().max(200).optional(),
});
export type SummarySource = z.infer<typeof SummarySourceSchema>;

/** 出典として保存する上限。超えたぶんは `sourceTotal` で件数だけ伝える。 */
export const SUMMARY_SOURCE_LIMIT = 100;

/** 上司からのコメント1件。やり取りは残す（消さない）。 */
export const SummaryCommentSchema = z.object({
  commentId: z.string(),
  authorId: z.string(),
  /** 投稿時点の表示名スナップショット（退職後も誰の発言か読める）。 */
  authorName: z.string(),
  body: z.string().min(1).max(2000),
  createdAt: z.string(),
});
export type SummaryComment = z.infer<typeof SummaryCommentSchema>;

/**
 * 集計値のスキーマ。**形の正本は `./activitySummary.ts` の `ActivityMetrics`** で、
 * ここは提出時スナップショットを DDB に保存/検証するための zod 表現。
 * `z.ZodType<ActivityMetrics>` と明示しているので、両者がズレると型エラーになる。
 */
export const ActivityMetricsSchema: z.ZodType<ActivityMetrics> = z.object({
  recordCount: z.number(),
  // PRODUCT_DEV-1142 から、顧客 ID の無い記録（仮登録の会社・個人のお客さま）も名前で数える。
  // それより前に保存した metricsSnapshot は ID のある記録だけの数なので、配信日をまたぐ比べは定義が違う
  companyCount: z.number(),
  contactCount: z.number(),
  newCustomerCount: z.number(),
  nextAppointmentCount: z.number(),
  nextAppointmentFrom: z.string().nullable(),
  nextAppointmentTo: z.string().nullable(),
  draftCount: z.number(),
});

export const DDBActivitySummarySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // SUMMARY#<period>#<periodKey>（PRODUCT_DEV-721 U6: ON のテナントは DEPT#<deptId>#SUMMARY#<period>#<periodKey>）
  tenantId: z.string(),
  userId: z.string(),
  // PRODUCT_DEV-721 U6: 部署ごとの日報の部署。部署の無い日報には無い
  deptId: z.string().optional(),
  period: SummaryPeriodSchema,
  periodKey: z.string(),
  /** 期間の初日・最終日（"YYYY-MM-DD"）。後から締め日設定が変わっても当時の範囲が読める。 */
  periodFrom: z.string(),
  periodTo: z.string(),

  /** AI が書いた本文。テキストとして本人が直せる。 */
  body: z.string().max(20000).nullable().optional(),
  /** 本人の所感（任意）。 */
  note: z.string().max(4000).nullable().optional(),
  flag: SummaryFlagSchema.optional(),
  /**
   * PRODUCT_DEV-1142: 本人が本文・所感・調子を**変えた**時刻（保存の前と中身が違ったときだけ付く）。
   * 週報・月報・部のまとめは、本人が直した本文を材料にするのに使う。
   * `updatedBy` は提出だけ・所感だけの保存でも付くので、本文を直したかの印にはならない。
   * 本文の印は AI が作り直すと消える。**配信前の行には無い**（直していない扱い）。
   */
  bodyEditedAt: z.string().nullable().optional(),
  noteEditedAt: z.string().nullable().optional(),
  flagEditedAt: z.string().nullable().optional(),

  submitStatus: SummarySubmitStatusSchema,
  /** 自動確定した時刻（PRODUCT_DEV-874 からは提出期限の時刻）。 */
  autoConfirmedAt: z.string().nullable().optional(),
  /** cron が下書きの器を作った時刻（PRODUCT_DEV-874。①の15分前）。 */
  draftPreparedAt: z.string().nullable().optional(),
  /** 本人が確認して提出した時刻。再提出のたびに更新する。 */
  confirmedAt: z.string().nullable().optional(),

  comments: z.array(SummaryCommentSchema).optional(),
  highlights: z.array(SummaryHighlightSchema).optional(),

  /**
   * 本文のもとになった記録（PRODUCT_DEV-673）。生成時に実際に読んだものだけを入れる。
   * 表示上限 `SUMMARY_SOURCE_LIMIT` で切っているので、切ったかどうかは `sourceTotal` で判る。
   * **生成前・旧データは undefined**（空配列＝「参考にした記録が無い」と区別する）。
   */
  sources: z.array(SummarySourceSchema).max(SUMMARY_SOURCE_LIMIT).optional(),
  /** 参考にした記録の総数（`sources` を切る前の件数）。 */
  sourceTotal: z.number().optional(),

  aiStatus: SummaryAiStatusSchema.optional(),
  aiErrorMessage: z.string().nullable().optional(),
  aiGeneratedAt: z.string().nullable().optional(),

  /** 提出/生成時点の集計値。後から数え直しても当時の数字が読める。 */
  metricsSnapshot: ActivityMetricsSchema.optional(),

  version: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});
export type DDBActivitySummary = z.infer<typeof DDBActivitySummarySchema>;

// ─── チームのまとめ（PRODUCT_DEV-698）────────────────────────────────────────
//
// 同じ `UnizoActivitySummaries` テーブルに **PK の名前空間だけ足して**載せる。
//   PK: `TENANT#{tenantId}#TEAM#{deptId ?? "UNASSIGNED"}`
//   SK: `SUMMARY#{period}#{periodKey}`（個人と同じ形）
//
// 個人の提出物とはフィールドが違う（提出状態・所感・上司コメントを持たない）ので、
// `DDBActivitySummary` を optional だらけにせず別スキーマにする。**上司が読む/聞くための
// 生成物**であって、誰かが提出するものではない。

/** 部署に属さない人をまとめる枠の ID。PK に使うので空文字にはしない。 */
export const UNASSIGNED_DEPT_KEY = "UNASSIGNED";

/**
 * 「目を通したいところ」の区分（PRODUCT_DEV-698 9/15）。組み立てと並べ方は `./teamAttention`。
 *
 * ⚠ **ここから下の attention の形は契約の一部。** 確定版に保存したら書き直さないので、
 * 区分・項目の**名前の変更や削除は保存済みのアイテムを黙って見えなくする**（画面は知らない区分を捨てる）。
 * 足すのは可（optional で）。変えるなら版を分けて読み替えを用意する。
 * 依存を持たせない（contract.ts を読む Admin にカレンダーや祝日表を連れて行かない）。
 */
export const ATTENTION_LEVELS = ["high", "mid", "low", "good"] as const;
export type AttentionLevel = (typeof ATTENTION_LEVELS)[number];

/**
 * 「要フォロー 高」の種類（PRODUCT_DEV-698 ②'）。上司宛メールには**この種類の呼び方だけ**を載せ、
 * AI が書いた見出しは載せない（見出しに取引先名が混ざっても、取り消せないメールに残さないため）。
 * `rain` はルール、それ以外は AI が返す。
 */
export const ATTENTION_HIGH_KINDS = ["lost", "complaint", "negotiation_failed", "rain"] as const;
export type AttentionHighKind = (typeof ATTENTION_HIGH_KINDS)[number];

export const AttentionPointSchema = z.object({
  level: z.enum(ATTENTION_LEVELS),
  /** AI が商談の文脈から拾ったか、ルールで拾ったか。 */
  source: z.enum(["ai", "rule"]),
  /**
   * 「高」の種類（PRODUCT_DEV-698 ②'）。「高」以外には付けない。
   * **無いアイテムがある**（9/17 より前に保存したもの・AI が種類を返さなかったもの）。
   */
  kind: z.enum(ATTENTION_HIGH_KINDS).optional(),
  /** 要点の見出し（「条件交渉が不成立」等）。 */
  headline: z.string(),
  /** 理由の一文。**事実だけ**。 */
  reason: z.string().nullable(),
  /** 根拠になった商談の日付（"YYYY-MM-DD"）。並べ順に使う。無ければ null。 */
  occurredOn: z.string().nullable(),
});
export type AttentionPoint = z.infer<typeof AttentionPointSchema>;

export const AttentionPersonSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  /** その人のいちばん高い区分。枠はここに置く。 */
  level: z.enum(ATTENTION_LEVELS),
  /** 区分の高い順。 */
  points: z.array(AttentionPointSchema),
});
export type AttentionPerson = z.infer<typeof AttentionPersonSchema>;

/** 「目を通したいところ」のルールで拾うための、メンバー1人ぶんの材料。 */
export interface AttentionRuleInput {
  userId: string;
  userName: string;
  flag: "none" | "sunny" | "rain";
  /** 本人の所感の抜粋。雨・晴れの理由に使う。 */
  noteExcerpt: string | null;
  /** 予定があったのに記録が無い件数。**まだ数えられない期間は null**。 */
  unrecordedCount: number | null;
  /** 記録が続けて無い営業日数。数えないときは null。 */
  noRecordBusinessDays: number | null;
  /** その期間の最終活動日。並べ順に使う。 */
  lastActivityOn: string | null;
}

/**
 * PRODUCT_DEV-1322: 週報・月報のまとめの全体像（3段落）。メールは流れと持ち越しを載せ、人ごとは画面で読む。
 */
export const TeamSummaryOverviewSchema = z.object({
  /** この期間の部の動きの中心（どんな相談・商談が多く、何が決まったか） */
  flow: z.string().max(4000),
  /** 人ごとの取り組み（主な記録者は全員） */
  people: z.string().max(4000),
  /** 来週（来月）へ持ち越す案件・流れ */
  carryover: z.string().max(4000),
});
export type TeamSummaryOverview = z.infer<typeof TeamSummaryOverviewSchema>;

/**
 * PRODUCT_DEV-1322: マネージャーへのヒント1件。**読むのはマネージャー**で、部下に声をかける・一緒に考える・
 * 同行する・手を貸すためのヒント（部下への指示や評価ではない）。記録の文脈からの推測なので、画面とメールに注記を付ける。
 */
export const TeamSummaryHintSchema = z.object({
  /** 関わる相手（部のメンバーの氏名）。部全体の話なら "部全体" */
  name: z.string().max(200),
  /** お客さまの呼び名。部全体なら空文字 */
  company: z.string().max(200),
  /** 根拠の商談記録の id */
  meetingIds: z.array(z.string()).max(20),
  /** "next"（次の一手）/ "review"（振り返り。月報だけ） */
  kind: z.string().max(20),
  /** 関わり方の種類（plan / precheck / join / backup / share / praise） */
  how: z.string().max(20),
  /** 記録から分かる事実 */
  fact: z.string().max(400),
  /** マネージャーができる関わり方 */
  suggestion: z.string().max(400),
});
export type TeamSummaryHint = z.infer<typeof TeamSummaryHintSchema>;

export const DDBTeamSummarySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#TEAM#<deptId|UNASSIGNED>
  SK: z.string(), // SUMMARY#<period>#<periodKey>
  tenantId: z.string(),
  /** null は「未所属」枠。 */
  deptId: z.string().nullable(),
  period: SummaryPeriodSchema,
  periodKey: z.string(),
  periodFrom: z.string(),
  periodTo: z.string(),

  /** AI が書いた部のまとめ。**誰も編集しない**（個人の日報と違い提出物ではない）。 */
  body: z.string().max(20000).nullable().optional(),
  /**
   * 同じ生成で書かせる「メンバーの動き」「取引先の動き」（PRODUCT_DEV-698 9/11）。
   * 読み上げには使わない（音声は body だけ）。9/11 より前のアイテムには無い。
   */
  membersBody: z.string().max(20000).nullable().optional(),
  companiesBody: z.string().max(20000).nullable().optional(),
  /**
   * PRODUCT_DEV-1073: `body` を作った項目（1件1文）。「すぐ手当てが要ること」と「前に進んだこと」。
   * 上司宛メールはこれを1件1行で載せる（200字で切ると文の途中で切れ、どの件が抜けたか分からない）。
   * **この項目が無いアイテム**（10/8 より前・旧形式の DB 上書きで作ったもの）は、従来どおり `body` の冒頭を載せる。
   */
  bodyItems: z
    .object({ urgent: z.array(z.string()), progress: z.array(z.string()) })
    .nullable()
    .optional(),
  /**
   * PRODUCT_DEV-1142 ③: 同じ項目の1行「誰／顧客：何が（いつ）」。画面のカードとメールが見出しの下に並べる
   * （`bodyItems` は読み上げと同じ文の形で、メールの古い形のために残す）。**無いアイテム**（③より前）は `bodyItems` を使う。
   */
  bodyLines: z
    .object({ urgent: z.array(z.string()), progress: z.array(z.string()) })
    .nullable()
    .optional(),
  /** PRODUCT_DEV-1142 ③: 作ったときのまとめ方。並べ方を決める。無いアイテムはフォロー重視（今の並び）。 */
  focus: TeamSummaryFocusSchema.optional(),
  /**
   * PRODUCT_DEV-1322: 週報・月報の「ひとこと」（1〜2文）。上司宛メールの冒頭に載る。
   * **これがあるアイテムだけ**メールを新しい形（ひとこと → TOPICS → 流れ → …）で組む。無いアイテム（日報・1322 より前）は今の形
   */
  catchCopy: z.string().max(400).nullable().optional(),
  /** PRODUCT_DEV-1322: 週報・月報の全体像（3段落）。無いアイテムには無い */
  overview: TeamSummaryOverviewSchema.nullable().optional(),
  /** PRODUCT_DEV-1322: マネージャーへのヒント（5件まで。プログラムで切る）。無いアイテムには無い */
  hints: z.array(TeamSummaryHintSchema).max(10).nullable().optional(),
  /**
   * 「目を通したいところ」（PRODUCT_DEV-698 9/15）。確定版を作るときに、AI が商談の文脈から
   * 拾った要点とルールで拾った要点をまとめて保存する（確定した時点の内容で固定する）。
   * 形と並べ方は `./teamAttention`。**この項目が無いアイテム**（9/15 より前・生成失敗）は、
   * 画面がルールの分だけをその場で数えて出す。
   */
  attention: z.array(AttentionPersonSchema).nullable().optional(),
  /** 生成時点の対象人数。「6名分」の表示に使う。 */
  memberCount: z.number().optional(),
  /** PRODUCT_DEV-721 U6: 生成時点の、うち兼務している人の数（機能フラグ ON の部署の枠だけ） */
  concurrentMemberCount: z.number().optional(),

  aiStatus: SummaryAiStatusSchema.optional(),
  aiErrorMessage: z.string().nullable().optional(),
  aiGeneratedAt: z.string().nullable().optional(),

  /** 読み上げ音声（PRODUCT_DEV-698 PR6）。S3 キーと概算の再生時間。 */
  audioKey: z.string().nullable().optional(),
  audioDurationSec: z.number().nullable().optional(),

  /**
   * この期間のメールを送った時刻（PRODUCT_DEV-698）。**2026-09-14 より前の行だけが持つ。**
   *
   * 当時は①の枠で本人宛と上司宛を1つの印でまとめて押さえていた。いまは下の2つに分けたので
   * 新しくは書かない。⚠ **この印がある行は、本人宛も上司宛も送り済みとして扱う**
   * （切り替えの前に①で送った期間へ、②の後にもう一度上司宛を送らないため）。
   */
  mailSentAt: z.string().nullable().optional(),
  /**
   * 本人宛（下書きができたお知らせ）を送った時刻（PRODUCT_DEV-698 9/14）。①の枠で押さえる。
   *
   * **二重送信を止めるための印**。配信の cron は 15 分おきに回るので、書き込みは条件付きで
   * **送る前に押さえる**（送ってから押さえると、送信後の失敗で二重送信になる）。
   */
  personalMailSentAt: z.string().nullable().optional(),
  /**
   * 上司宛（部のまとめの確定版）を送った時刻（PRODUCT_DEV-698 9/14）。
   * 提出期限の後、確定版ができてから押さえる。押さえ方は `personalMailSentAt` と同じ。
   */
  teamMailSentAt: z.string().nullable().optional(),
  /**
   * 確定版を作り始めた回数（PRODUCT_DEV-698 9/14）。失敗したら上限まで作り直し、
   * 上限に達したら数字だけのメールを送る。
   */
  aiAttempts: z.number().optional(),
  /**
   * 確定版の生成を積んだ時刻（PRODUCT_DEV-698 9/14）。**積んだがまだ始まっていない生成を、
   * 作り直しの枠でもう一度積まないため**に持つ（夜 24:00 に全テナントぶんが集中すると、
   * 15分では始まらないことがある）。
   */
  aiQueuedAt: z.string().nullable().optional(),

  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBTeamSummary = z.infer<typeof DDBTeamSummarySchema>;

/**
 * `GET /activity-summary?scope=team` に相乗りして返す（PRODUCT_DEV-698）。
 *
 * **専用ルートを作らない。** 管理者ビューは開いた時点でこの API を1回叩くので、
 * まとめのためにもう1往復させる理由が無い。
 */
export interface TeamSummaryView {
  body: string | null;
  aiStatus: SummaryAiStatus;
  generatedAt: string | null;
  memberCount: number | null;
  /** PRODUCT_DEV-721 U6: うち兼務している人の数（生成時点。機能フラグ ON の部署の枠だけ） */
  concurrentMemberCount?: number | null;
  /** 音声があるか。実 URL は別途 audio-url で署名して取る（PR6）。 */
  hasAudio: boolean;
  audioDurationSec: number | null;
  /**
   * メンバー別タブ「メンバーの動き」・会社別タブ「取引先の動き」（PRODUCT_DEV-698 9/11）。
   * optional なのは古い API（項目なし）と、9/11 より前に生成されたアイテム（null）の両方があるため。
   */
  membersBody?: string | null;
  companiesBody?: string | null;
  /**
   * 確定版で保存した「目を通したいところ」（PRODUCT_DEV-698 9/15）。**null/省略は「確定版の判定が無い」**
   * （途中の期間・9/15 より前のアイテム・生成失敗・古い API）で、画面はルールの分だけを出す。
   */
  attention?: AttentionPerson[] | null;
  /** PRODUCT_DEV-1142 ③: 見出しの下に並べる1行（`DDBTeamSummary.bodyLines`）。null/省略は文章（`body`）で出す */
  bodyLines?: { urgent: string[]; progress: string[] } | null;
  /** PRODUCT_DEV-1142 ③: 作ったときのまとめ方。省略はフォロー重視 */
  focus?: TeamSummaryFocus;
  /** PRODUCT_DEV-1322: 週報・月報の全体像（3段落）。null/省略は無し（日報・1322 より前） */
  overview?: TeamSummaryOverview | null;
  /** PRODUCT_DEV-1322: マネージャーへのヒント。null/省略は無し */
  hints?: TeamSummaryHint[] | null;
}

/** PATCH /activity-summary/{period}/{periodKey}。省略したキーは変更しない。 */
export const PatchActivitySummarySchema = z
  .object({
    body: z.string().max(20000).nullable().optional(),
    note: z.string().max(4000).nullable().optional(),
    flag: SummaryFlagSchema.optional(),
    /**
     * true で「本人提出」として提出する（再提出も同じ）。
     * false は送らない ——「提出を取り消す」操作は要件に無い。
     */
    submit: z.literal(true).optional(),
  })
  .strict();
export type PatchActivitySummary = z.infer<typeof PatchActivitySummarySchema>;

/** POST /activity-summary/comments（上司のコメント投稿）。 */
export const PostSummaryCommentSchema = z
  .object({
    /** コメント先のサマリ対象者。 */
    userId: z.string().min(1),
    period: SummaryPeriodSchema,
    periodKey: z.string().min(1),
    body: z.string().min(1).max(2000),
  })
  .strict();
export type PostSummaryComment = z.infer<typeof PostSummaryCommentSchema>;

export type ActivitySummaryDetailResponse = {
  success: boolean;
  /** 未作成なら null（0件と未作成を取り違えないため）。 */
  summary: DDBActivitySummary | null;
  /** 現時点で集計し直した値。保存済みスナップショットとは別に返す。 */
  metrics: ActivityMetrics;
  range: PeriodRange;
  /**
   * 提出期限。締め日そのものではなく、締め日 + テナント設定の猶予日数。
   * PRODUCT_DEV-874 からは**日報にも必ず付く**（既定は当日 24:00）。この時刻に自動提出される。
   */
  submitDue?: { dueOn: string; dueTime: string; label: string } | null;
  /**
   * いま集め直した「本文のもとになりうる記録」の件数（PRODUCT_DEV-874）。
   *
   * 保存済みの `summary.sourceTotal`（本文を作った時点の件数）と比べると、
   * **本文がまだ知らない記録が何件あるか**が分かる。optional なのは古い API への保険。
   */
  liveSourceTotal?: number;
  /**
   * PRODUCT_DEV-973: 兼務している人の、**ほかの所属部署**での同じ期間の件数。**本人以外が（その日報を読む権限をもって）、
   * 対象者がいま所属している部署の日報を開いたときだけ**返す（今は manager だけが他人の日報を読める）。異動前の部署の日報には付けない。
   *
   * 部署名と件数だけで、商談の相手・題名・本文は返さない（その部署の上司でない人に別の部署の顧客情報を渡さない）。
   * 0件の部署は入れない。期間は**この日報の期間**（部署ごとに締め日が違っても、開いている日報に合わせて数える）。
   * 本人が自分の日報を開いたとき・フラグ OFF のテナント・兼務でない人には付かない（古い API も付けない）。
   */
  otherDeptActivity?: OtherDeptActivity[];
};

/**
 * PRODUCT_DEV-973: ほかの所属部署での件数（`ActivitySummaryDetailResponse.otherDeptActivity` の1行）。
 * その部署の上司でない人にも返すので、**項目を足すときは security の観点でレビューする**（相手・題名・本文は入れない）。
 */
export type OtherDeptActivity = {
  deptId: string;
  deptName: string;
  /** 商談記録の件数（下書きを除く。日報の「記録」と同じ数え方） */
  recordCount: number;
  /** 顧客登録の件数（日報の「新規」と同じ数え方） */
  newCustomerCount: number;
};

/** アーカイブ一覧の1行（過去の日報・週報・月報）。 */
export type ActivitySummaryArchiveItem = {
  period: SummaryPeriodKind;
  periodKey: string;
  periodFrom: string;
  periodTo: string;
  submitStatus: SummarySubmitStatus;
  flag: SummaryFlag;
  recordCount: number;
  commentCount: number;
  /**
   * その期間の集計（保存済みのスナップショット）。
   *
   * TOP の締めカードが**その期間の数値**を出すために要る（PRODUCT_DEV-675 9/1）。
   * 以前は TOP が「今日の日報の集計」をそのまま月報カードへ渡しており、
   * 月初のように今日の記録が 0 件だと**4つとも 0**になって、詳細画面（23件）と
   * 食い違っていた。
   *
   * ⚠ **無い場合がある**（生成前・スナップショットを持たない古い提出物）。
   * 出せないときは 0 を並べず、数値の行そのものを出さないこと。
   */
  metrics?: ActivityMetrics;
  /** 提出期限（週報・月報のみ）。TOP の締めカードに出す。 */
  submitDue?: { dueOn: string; dueTime: string; label: string } | null;
  /**
   * 本人が確認して提出した時刻（PRODUCT_DEV-822）。**提出していなければ入らない。**
   *
   * TOP のタブは提出後も期限まで残り、提出済みの中身は「8/1 – 8/31 9/1 09:12 に提出」
   * の1行になる。ここを `updatedAt` で代用しない——コメントの追加や AI 生成でも動くので、
   * **提出していない時刻を「提出しました」と書く**ことになる。
   */
  confirmedAt?: string | null;
  /**
   * 一覧に出す短い抜粋。**所感を優先し、無ければ AI 本文の冒頭**。
   * 一覧で「その日に何があったか」を思い出せるのが目的なので、本人が書いた所感の方が強い。
   */
  excerpt?: string;
  updatedAt: string;
  /**
   * **DDB にレコードが無い営業日の行**（PRODUCT_DEV-820）。一覧が読み取り時に補う。
   *
   * 日報は「その日に商談記録が1件以上ある人」にしか作られないため、記録が無い営業日は
   * 一覧から丸ごと消えていた。「日報が消えた」と読まれるうえ、後から所感だけ書きたい日に
   * 入口が無い。**空行を足すのは営業日だけで、実際のレコードには営業日判定をかけない**
   * ——かけてしまうと、あとで休日設定を変えたときに書いたはずの日報が一覧から消える。
   */
  placeholder?: boolean;
  /**
   * 今日ぶん（まだ締まっていない）。**空行かどうかに関わらず今日の日報には必ず立つ**。
   *
   * ⚠ 一度は空行にしか立てておらず、**天気アイコンを付けて実レコードができている今日**は
   * 「未提出・活動なし」と出ていた（PRODUCT_DEV-820）。終業前なのに 0 件が確定したように
   * 読まれるので、実レコードでも今日は未確定として扱う。
   */
  pending?: boolean;
};

export type ActivitySummaryArchiveResponse = {
  success: boolean;
  items: ActivitySummaryArchiveItem[];
  /**
   * PRODUCT_DEV-721 U6: 機能フラグ ON のテナントで、**いま所属していない部署**（異動前の部署）の日報がある部署。
   * 本人はその部署の日報を読める（読むだけ）。画面は `?deptId=` を付けて一覧を開き直す
   */
  pastDepartments?: Array<{ deptId: string; name: string }>;
};

/** SQS メッセージ（activity-summary-generator 用）。 */
export interface ActivitySummaryGenerateMessage {
  tenantId: string;
  /**
   * 個人の下書きの対象者。**scope="team" のときは入らない**ので optional
   * （必須のままにすると、部のまとめを積むのに意味の無い空文字を入れることになる）。
   */
  userId?: string;
  period: SummaryPeriodKind;
  periodKey: string;
  enqueuedAt?: string;
  /**
   * PRODUCT_DEV-698: 個人の下書きか、部署のまとめか。
   * **省略時は `"user"`**（既にキューに積まれているメッセージが従来どおり動く）。
   * `"team"` のときは `userId` を使わず `deptId` の枠を集計する。
   */
  scope?: "user" | "team";
  /**
   * scope="team" のときの部署。`null` は「未所属」枠。
   * PRODUCT_DEV-721 U6: scope="user" でも、機能フラグ ON のテナントは**どの部署の日報か**を入れる。
   * **`splitByDept: true` のときだけ**この部署の日報（部署のキー・部署の材料）として処理する。
   * `splitByDept` が無ければ部署を見ず、部署を持たない今の日報（OFF のテナントと同じ）として処理する。
   */
  deptId?: string | null;
  /**
   * scope="team" のときの対象メンバー（氏名つき）。呼び出し側が解決して渡す。
   * **氏名まで渡すのは、まとめが「佐藤さんは…」と人を名指しするため。**
   * ワーカー側で引き直すと、集計した瞬間と生成した瞬間で在籍が食い違いうる。
   */
  members?: Array<{
    userId: string;
    name: string;
    /** PRODUCT_DEV-721 U6: その人の既定の部署（先頭の所属）。`splitByDept` のとき部署の無い材料の行き先を決める */
    defaultDeptId?: string | null;
  }>;
  /** PRODUCT_DEV-721 U6: scope="team" のとき、メンバーのうち兼務している人の数（生成した時点の値を保存する）。 */
  concurrentMemberCount?: number;
  /**
   * PRODUCT_DEV-721 U6: 日報を部署ごとに分けるテナント（機能フラグ ON）のメッセージか。
   * true なら `deptId` の部署の材料だけで作り、日報はその部署のキーで読み書きする。省略は今の日報
   * （ワーカーは部署テーブルを読めないので、積む側が決めて渡す）。
   */
  splitByDept?: boolean;
  /** PRODUCT_DEV-721 U6: scope="user" で `splitByDept` のとき、本人の既定の部署（先頭の所属） */
  userDefaultDeptId?: string | null;
  /**
   * scope="team" のときの期間。**個人と違い「保存済みアイテムの periodFrom」を
   * 当てにできない**（初回はアイテムがまだ無い）ので、積む側が入れる。
   */
  periodFrom?: string;
  periodTo?: string;
  /**
   * scope="team" のときの提出期限（PRODUCT_DEV-698 9/14。ISO 8601）。**これより後に本人が
   * 提出・修正した日報は、部のまとめの材料にしない**（確定版は期限までの提出で作る）。
   * 省略時は本人が提出したものをすべて材料にする（既に積まれているメッセージの互換）。
   */
  submitDeadlineAt?: string;
  /**
   * PRODUCT_DEV-1142 ③: scope="team" のときの部のまとめのまとめ方（部署の設定。積む側が解決して渡す）。
   * 省略はフォロー重視（OFF のテナント・未設定の部署・既に積まれているメッセージ）。
   */
  teamSummaryFocus?: TeamSummaryFocus;
}
